import gql from 'graphql-tag';
import * as ApolloReactHooks from '@sketch/graphql-apollo';

const ArtboardThumbnailsFragmentDoc = gql `
  fragment ArtboardThumbnails on File {
    thumbnails {
      type
      url
    }
  }
`;
const ArtboardDetailInfoCwvFragmentDoc = gql `
  fragment ArtboardDetailInfoCWV on Artboard {
    identifier
    permanentArtboardShortId
    files(type: FULL) {
      identifier
      url
      scale
      thumbnails {
        type
        url
      }
    }
  }
`;
const ComponentInfoFragmentDoc = gql `
  fragment ComponentInfo on Component {
    identifier
    uuid
    name
    description
    path
    depth
    ... on Symbol {
      artboard {
        ...ArtboardDetailInfoCWV
      }
    }
    ... on ColorVariable {
      rgbaValue {
        blue
        green
        red
        alpha
      }
    }
    ... on TextStyle {
      needsContrastingBackground
      horizontalAlignment
      font {
        size
        name
        weight
      }
      files {
        identifier
        url
        scale
        thumbnails {
          type
          url
        }
      }
    }
    ... on LayerStyle {
      needsContrastingBackground
      files {
        identifier
        url
        scale
        thumbnails {
          type
          url
        }
      }
    }
  }
  ${ArtboardDetailInfoCwvFragmentDoc}
`;
const AnnotationArtboardInfoFragmentDoc = gql `
  fragment AnnotationArtboardInfo on Artboard {
    permanentArtboardShortId
    documentVersionShortId
    subscriptionStatus
    unreadCount
    annotationCount
  }
`;
const ArtboardDetailInfoFragmentDoc = gql `
  fragment ArtboardDetailInfo on Artboard {
    uuid
    identifier
    shortId
    documentVersionShortId
    permanentArtboardShortId
    revisionIdentifier
    width
    height
    name
    isPage
    isSymbolMaster
    documentOrder
    isFlowHome
    subscriptionStatus
    hasMissingFonts
    page {
      uuid
      identifier
      name
      artboardCount
    }
    files(type: FULL) {
      identifier
      url
      scale
      ...ArtboardThumbnails
    }
    component {
      ...ComponentInfo
    }
    ...AnnotationArtboardInfo
  }
  ${ArtboardThumbnailsFragmentDoc}
  ${ComponentInfoFragmentDoc}
  ${AnnotationArtboardInfoFragmentDoc}
`;
const RevisionInfoFragmentDoc = gql `
  fragment RevisionInfo on Revision {
    identifier
    createdAt
    updatedAt
    creator {
      identifier
      name
    }
    version {
      identifier
      shortId
      description
    }
    userIsCreator
    createdAfterVersioning
    artboard @include(if: $includeArtboardDetails) {
      ...ArtboardDetailInfo
    }
    kind
  }
  ${ArtboardDetailInfoFragmentDoc}
`;
const RevisionDeletedFragmentDoc = gql `
  fragment RevisionDeleted on RevisionDeleted {
    identifier
    userIsCreator
    artboardIdentifier
    createdAtNN: createdAt
    creatorNN: creator {
      identifier
      name
    }
  }
`;
const RevisionUnavailableFragmentDoc = gql `
  fragment RevisionUnavailable on RevisionUnavailable {
    identifier
    createdAtNN: createdAt
    creatorNN: creator {
      identifier
      name
    }
    userIsCreator
  }
`;
const AnnotationPageInfoFragmentDoc = gql `
  fragment AnnotationPageInfo on Page {
    identifier
    pageUUID: uuid
    hasUnreadComments @include(if: $includeHasUnreadComments)
  }
`;
const AnnotationSubjectCountersFragmentDoc = gql `
  fragment AnnotationSubjectCounters on Annotation {
    identifier
    currentSubject {
      ... on Artboard {
        documentVersionShortId
        permanentArtboardShortId
        uuid
        ...AnnotationArtboardInfo
        page {
          identifier
          uuid
          ...AnnotationPageInfo
        }
      }
      ... on Page {
        identifier
        pageUUID: uuid
        ...AnnotationPageInfo
      }
    }
  }
  ${AnnotationArtboardInfoFragmentDoc}
  ${AnnotationPageInfoFragmentDoc}
`;
const DeletedAnnotationSubjectCountersFragmentDoc = gql `
  fragment DeletedAnnotationSubjectCounters on AnnotationDeletedEvent {
    identifier
    currentSubject {
      ... on Artboard {
        documentVersionShortId
        permanentArtboardShortId
        uuid
        ...AnnotationArtboardInfo
        page {
          identifier
          uuid
          ...AnnotationPageInfo
        }
      }
      ... on Page {
        identifier
        pageUUID: uuid
        ...AnnotationPageInfo
      }
    }
  }
  ${AnnotationArtboardInfoFragmentDoc}
  ${AnnotationPageInfoFragmentDoc}
`;
const AnnotationSubjectFragmentDoc = gql `
  fragment AnnotationSubject on Annotation {
    identifier
    currentSubject {
      ... on Artboard {
        documentVersionShortId
        permanentArtboardShortId
        uuid
        page {
          identifier
          uuid
        }
      }
      ... on Page {
        identifier
        pageUUID: uuid
      }
      ... on Component {
        identifier
        uuid
      }
    }
  }
`;
const AnnotationSubjectSubscriptionStatusFragmentDoc = gql `
  fragment AnnotationSubjectSubscriptionStatus on Annotation {
    identifier
    subscriptionStatus
    share {
      identifier
      subscriptionStatus
    }
    ...AnnotationSubject
    currentSubject {
      ... on Artboard {
        documentVersionShortId
        permanentArtboardShortId
        uuid
        subscriptionStatus
      }
    }
  }
  ${AnnotationSubjectFragmentDoc}
`;
const CommentUserFragmentDoc = gql `
  fragment CommentUser on PublicUser {
    avatar {
      small
    }
    identifier
    name
  }
`;
const BaseCommentFragmentDoc = gql `
  fragment BaseComment on Comment {
    identifier
    userIsCreator
    userCanDelete
  }
`;
const CommentFragmentDoc = gql `
  fragment Comment on Comment {
    identifier
    body
    createdAt
    isRead
    isEdited
    user {
      ...CommentUser
    }
    ...BaseComment
  }
  ${CommentUserFragmentDoc}
  ${BaseCommentFragmentDoc}
`;
const AnnotationCoordinatesFragmentDoc = gql `
  fragment AnnotationCoordinates on Annotation {
    identifier
    coordinates {
      identifier @client
      x
      y
    }
  }
`;
const AnnotationResolutionInfoFragmentDoc = gql `
  fragment AnnotationResolutionInfo on Annotation {
    identifier
    resolution {
      resolvedAt
      user {
        ...CommentUser
      }
    }
  }
  ${CommentUserFragmentDoc}
`;
const AnnotationDotFragmentDoc = gql `
  fragment AnnotationDot on Annotation {
    identifier
    hasNewComments
    isOptimistic @client
    ...AnnotationSubject
    ...AnnotationCoordinates
    ...AnnotationResolutionInfo
  }
  ${AnnotationSubjectFragmentDoc}
  ${AnnotationCoordinatesFragmentDoc}
  ${AnnotationResolutionInfoFragmentDoc}
`;
const AnnotationLastCommentFragmentDoc = gql `
  fragment AnnotationLastComment on Annotation {
    identifier
    latestCommentCreatedAt
  }
`;
const AnnotationCommentCountFragmentDoc = gql `
  fragment AnnotationCommentCount on Annotation {
    comments {
      meta {
        totalCount
      }
    }
  }
`;
const BaseAnnotationFragmentDoc = gql `
  fragment BaseAnnotation on Annotation {
    identifier
    isNew
    subscriptionStatus
    firstComment {
      ...Comment
    }
    ...AnnotationDot
    ...AnnotationLastComment
    ...AnnotationCommentCount
  }
  ${CommentFragmentDoc}
  ${AnnotationDotFragmentDoc}
  ${AnnotationLastCommentFragmentDoc}
  ${AnnotationCommentCountFragmentDoc}
`;
const CredentialsFragmentDoc = gql `
  fragment Credentials on OAuthCredentials {
    authToken
    refreshToken
    expirationDateTime
  }
`;
const ShareListItemBaseFragmentDoc = gql `
  fragment ShareListItemBase on Share {
    identifier
    name
    type
    userCanUpdateSettings
    updatedAt
    deletedAt
    privacyIcon
    publicAccessLevel
  }
`;
const ShareThumbnailsFragmentDoc = gql `
  fragment ShareThumbnails on File {
    identifier
    thumbnails {
      identifier
      url
      url2x
      type
    }
  }
`;
const SharesListVersionItemFragmentDoc = gql `
  fragment SharesListVersionItem on Version {
    identifier
    shortId
    createdAt
    userIsCreator
    creator {
      identifier
      name
      avatar {
        small
      }
    }
    document {
      identifier
      renderStatus
      previewFile {
        identifier
        url
        ...ShareThumbnails
      }
    }
  }
  ${ShareThumbnailsFragmentDoc}
`;
const ProjectFragmentDoc = gql `
  fragment Project on Project {
    name
    identifier
    type
    workspaceAccessLevel
    deletedAt
  }
`;
const ShareListItemFragmentDoc = gql `
  fragment ShareListItem on Share {
    ...ShareListItemBase
    version(preferredState: PROCESSED) {
      ...SharesListVersionItem
    }
    project {
      ...Project
    }
  }
  ${ShareListItemBaseFragmentDoc}
  ${SharesListVersionItemFragmentDoc}
  ${ProjectFragmentDoc}
`;
const ShareMetaFragmentDoc = gql `
  fragment ShareMeta on PaginationMeta {
    after
    before
    limit
    totalCount
  }
`;
const CollectionSharesFragmentDoc = gql `
  fragment CollectionShares on Collection {
    shares(after: $after) {
      entries {
        ...ShareListItem
      }
      meta {
        ...ShareMeta
      }
    }
  }
  ${ShareListItemFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
const UnpaginatedCollectionShareFragmentDoc = gql `
  fragment UnpaginatedCollectionShare on UnpaginatedCollectionShare {
    identifier
    name
    updatedAt
    file {
      ...ShareThumbnails
    }
  }
  ${ShareThumbnailsFragmentDoc}
`;
const CollectionFragmentDoc = gql `
  fragment Collection on Collection {
    identifier
    name
    description
  }
`;
const CollectionSharePreviewFragmentDoc = gql `
  fragment CollectionSharePreview on CollectionSharePreview {
    shareIdentifier
    name
    file {
      ...ShareThumbnails
    }
  }
  ${ShareThumbnailsFragmentDoc}
`;
const CollectionPreviewsFragmentDoc = gql `
  fragment CollectionPreviews on Collection {
    ...Collection
    previews {
      ...CollectionSharePreview
    }
    shareCount
  }
  ${CollectionFragmentDoc}
  ${CollectionSharePreviewFragmentDoc}
`;
const CollectionForSelectFragmentDoc = gql `
  fragment CollectionForSelect on Collection {
    identifier
    name
  }
`;
const UserErrorFragmentDoc = gql `
  fragment UserError on UserError {
    message
    code
  }
`;
const SizeFragmentDoc = gql `
  fragment Size on Size {
    xs
    sm
    md
    lg
    xl
  }
`;
const LinkFragmentDoc = gql `
  fragment Link on Link {
    text
    url
    icon
    variant
  }
`;
const PublicationQueryPreferencesFragmentDoc = gql `
  fragment PublicationQueryPreferences on PublicationQueryPreferences {
    search
    limit
    sort
    period
    include
    exclude
    allowPagination
    documentType
  }
`;
const CommunityPublicationQueryFragmentDoc = gql `
  fragment CommunityPublicationQuery on CommunityPublicationQuery {
    id
    title
    subTitle
    link {
      ...Link
    }
    preferences {
      ...PublicationQueryPreferences
    }
  }
  ${LinkFragmentDoc}
  ${PublicationQueryPreferencesFragmentDoc}
`;
const SingleImageFragmentDoc = gql `
  fragment SingleImage on SingleImage {
    alt
    src
  }
`;
const ResponsiveImageFragmentDoc = gql `
  fragment ResponsiveImage on ResponsiveImage {
    alt
    source {
      xs
      sm
      md
      lg
      xl
    }
  }
`;
const VideoSourceFragmentDoc = gql `
  fragment VideoSource on VideoSource {
    backgroundColor
    poster
    src
  }
`;
const VideoFragmentDoc = gql `
  fragment Video on Video {
    standard {
      ...VideoSource
    }
    retina {
      ...VideoSource
    }
  }
  ${VideoSourceFragmentDoc}
`;
const CommunityHeroFragmentDoc = gql `
  fragment CommunityHero on CommunityHero {
    id
    image {
      ...SingleImage
      ...ResponsiveImage
    }
    video {
      ...Video
    }
    backgroundColor
    title
    content
    contentPosition
    icon
    links {
      ...Link
    }
    size {
      ...Size
    }
  }
  ${SingleImageFragmentDoc}
  ${ResponsiveImageFragmentDoc}
  ${VideoFragmentDoc}
  ${LinkFragmentDoc}
  ${SizeFragmentDoc}
`;
const CommunityItemFragmentDoc = gql `
  fragment CommunityItem on CommunityItem {
    ... on CommunityPublicationQuery {
      ...CommunityPublicationQuery
    }
    ... on CommunityHero {
      ...CommunityHero
    }
  }
  ${CommunityPublicationQueryFragmentDoc}
  ${CommunityHeroFragmentDoc}
`;
const CommunitySectionFragmentDoc = gql `
  fragment CommunitySection on CommunitySection {
    id
    size {
      ...Size
    }
    items {
      ...CommunityItem
    }
  }
  ${SizeFragmentDoc}
  ${CommunityItemFragmentDoc}
`;
const VisibilityFragmentDoc = gql `
  fragment Visibility on Visibility {
    hideOnBreakpoint
    hiddenForFF
  }
`;
const PermissionsFragmentDoc = gql `
  fragment Permissions on Permissions {
    roles
    audience
    categories
  }
`;
const EventDateFragmentDoc = gql `
  fragment EventDate on EventDate {
    day
    time
  }
`;
const SpeakerFragmentDoc = gql `
  fragment Speaker on Speaker {
    name
    role
    image
  }
`;
const DiscoverItemFragmentDoc = gql `
  fragment DiscoverItem on DiscoverItem {
    id
    type
    image {
      ...SingleImage
      ...ResponsiveImage
    }
    video {
      ...Video
    }
    title
    content
    url
    featured
    size {
      ...Size
    }
    permissions {
      ...Permissions
    }
    categories
    mobile {
      description
      icon
    }
    links {
      ...Link
    }
    contentPosition
    source
    length
    author
    icon
    note
    place
    backgroundColor
    date {
      ...EventDate
    }
    speakers {
      ...Speaker
    }
    dismissable
  }
  ${SingleImageFragmentDoc}
  ${ResponsiveImageFragmentDoc}
  ${VideoFragmentDoc}
  ${SizeFragmentDoc}
  ${PermissionsFragmentDoc}
  ${LinkFragmentDoc}
  ${EventDateFragmentDoc}
  ${SpeakerFragmentDoc}
`;
const DiscoverSectionFragmentDoc = gql `
  fragment DiscoverSection on DiscoverSection {
    id
    type
    title
    subTitle {
      text
      visibility {
        ...Visibility
      }
    }
    stretchedContent
    permissions {
      ...Permissions
    }
    limit
    size {
      ...Size
    }
    categories {
      id
      name
    }
    links {
      ...Link
    }
    items {
      ... on DiscoverItem {
        ...DiscoverItem
      }
    }
    visibility {
      ...Visibility
    }
  }
  ${VisibilityFragmentDoc}
  ${PermissionsFragmentDoc}
  ${SizeFragmentDoc}
  ${LinkFragmentDoc}
  ${DiscoverItemFragmentDoc}
`;
const PersonalTokenFragmentDoc = gql `
  fragment PersonalToken on PersonalToken {
    identifier
    accessScopes
    description
    expiresAt
    lastUsedAt
    createdAt
  }
`;
const ProjectWithPrivacyIconFragmentDoc = gql `
  fragment ProjectWithPrivacyIcon on Project {
    ...Project
    privacyIcon
  }
  ${ProjectFragmentDoc}
`;
const ProjectInSidebarAndHeaderFragmentDoc = gql `
  fragment ProjectInSidebarAndHeader on Project {
    userCanLeave
    ...ProjectWithPrivacyIcon
  }
  ${ProjectWithPrivacyIconFragmentDoc}
`;
const WorkspaceMembershipFragmentDoc = gql `
  fragment WorkspaceMembership on WorkspaceMembership {
    identifier
    acceptedAt
    isEditor
    isOwner
    activityStatus {
      lastSeenInDays
      status
    }
    user {
      identifier
      email
      avatar {
        small
        large
      }
      name
    }
    invite {
      identifier
      email
    }
    role
    personalTokenPermissions
  }
`;
const ProjectMembershipFragmentDoc = gql `
  fragment ProjectMembership on ProjectMembership {
    identifier
    inviteStatus
    privateAccessLevel
    guestCommentsEnabled
    guestInspectEnabled
    workspaceMembership {
      ...WorkspaceMembership
    }
  }
  ${WorkspaceMembershipFragmentDoc}
`;
const UserAccessProjectMembershipFragmentDoc = gql `
  fragment UserAccessProjectMembership on ProjectMembership {
    ...ProjectMembership
    project {
      ...Project
      privacyIcon
    }
  }
  ${ProjectMembershipFragmentDoc}
  ${ProjectFragmentDoc}
`;
const TrashedProjectFragmentDoc = gql `
  fragment TrashedProject on TrashedProject {
    identifier
    shortId
    deletedAt
    name
    shares(limit: 4) {
      entries {
        identifier
        name
        version {
          shortId
          document {
            identifier
            previewFile {
              url
              ...ShareThumbnails
            }
          }
        }
      }
      meta {
        totalCount
      }
    }
  }
  ${ShareThumbnailsFragmentDoc}
`;
const PrototypeFileFragmentDoc = gql `
  fragment PrototypeFile on Document {
    prototypeFile {
      downloadUrl
      status
      imageToken
      taskId
    }
  }
`;
const WorkspaceProfileFragmentDoc = gql `
  fragment WorkspaceProfile on WorkspaceProfile {
    identifier
    description
    websiteUrl
    donateUrl
    isPublic
    shortUrlName
    workspace {
      identifier
      name
      userRole
      avatar {
        large
        small
      }
    }
  }
`;
const PresentationFileFragmentDoc = gql `
  fragment PresentationFile on Page {
    identifier
    uuid
    presentationFile {
      downloadUrl
      status
      imageToken
      manifestUrl
    }
  }
`;
const PublicationPageFragmentDoc = gql `
  fragment PublicationPage on Page {
    identifier
    uuid
    name
    ...PresentationFile
  }
  ${PresentationFileFragmentDoc}
`;
const PublicWorkspaceProfileFragmentDoc = gql `
  fragment PublicWorkspaceProfile on PublicWorkspaceProfile {
    identifier
    description
    donateUrl
    isPublic
    shortUrlName
    websiteUrl
  }
`;
const PublicationReactionsFragmentDoc = gql `
  fragment PublicationReactions on Publication {
    identifier
    totalHearts
    userReaction
  }
`;
const PublicationFragmentDoc = gql `
  fragment Publication on Publication {
    identifier
    updatedAt
    workspaceProfile {
      identifier
      ...WorkspaceProfile
    }
    share {
      identifier
      name
      description
      type
      publicAccessLevel
      userCanInspect
      version(preferredState: PROCESSED_AND_PUBLISHED) {
        identifier
        shortId
        document {
          identifier
          downloadAvailable
          url
          renderStatus
          pages(limit: 1) {
            entries {
              ...PublicationPage
            }
          }
        }
      }
      workspace {
        identifier
        name
        userRole
        avatar {
          large
          small
        }
        profile {
          identifier
          ...PublicWorkspaceProfile
        }
      }
    }
    ...PublicationReactions
  }
  ${WorkspaceProfileFragmentDoc}
  ${PublicationPageFragmentDoc}
  ${PublicWorkspaceProfileFragmentDoc}
  ${PublicationReactionsFragmentDoc}
`;
const SharePublicationFragmentDoc = gql `
  fragment SharePublication on Share {
    identifier
    name
    version {
      shortId
      identifier
      document {
        identifier
        previewFile {
          ...ShareThumbnails
        }
      }
    }
  }
  ${ShareThumbnailsFragmentDoc}
`;
const PublicationThumbnailFragmentDoc = gql `
  fragment PublicationThumbnail on Publication {
    identifier
    share {
      identifier
      ...SharePublication
    }
  }
  ${SharePublicationFragmentDoc}
`;
const PublicationItemFragmentDoc = gql `
  fragment PublicationItem on Publication {
    identifier
    workspaceProfile {
      identifier
      shortUrlName
      workspace {
        identifier
        name
      }
    }
    ...PublicationReactions
    ...PublicationThumbnail
  }
  ${PublicationReactionsFragmentDoc}
  ${PublicationThumbnailFragmentDoc}
`;
const PublicationListingFragmentDoc = gql `
  fragment PublicationListing on PublicationListing {
    title
    preferences {
      ...PublicationQueryPreferences
    }
    modifiers
  }
  ${PublicationQueryPreferencesFragmentDoc}
`;
const PageDetailsFragmentDoc = gql `
  fragment PageDetails on Page {
    identifier
    uuid
    name
  }
`;
const ShareSettingsFragmentDoc = gql `
  fragment ShareSettings on Share {
    description
    type
    commentsEnabled
    userCanInspect
    updatedAt
  }
`;
const ShareLibraryListItemFragmentDoc = gql `
  fragment ShareLibraryListItem on Share {
    ...ShareListItem
    designSystems {
      name
      preview
    }
  }
  ${ShareListItemFragmentDoc}
`;
const ShareSubscriptionStatusFragmentDoc = gql `
  fragment ShareSubscriptionStatus on Share {
    subscriptionStatus
  }
`;
const ShareUserAccessLevelFragmentDoc = gql `
  fragment ShareUserAccessLevel on Share {
    userAccessLevel
  }
`;
const SharePublicPermissionsFragmentDoc = gql `
  fragment SharePublicPermissions on Share {
    identifier
    name
    publicAccessLevel
    publicCommentsEnabled
    publicInspectEnabled
    userCanUpdateCommentsEnabled
    privacyIcon
    userCanPublish
    publication {
      identifier
    }
  }
`;
const ShareDropdownOptionsBaseFragmentDoc = gql `
  fragment ShareDropdownOptionsBase on Share {
    userCanEdit
    userCanInspect
    userCanLeaveShare
    userAccessLevel
    commentsEnabled
    publicAccessLevel
    workspace {
      identifier
    }
  }
`;
const DocumentDownloadFragmentDoc = gql `
  fragment DocumentDownload on Document {
    url
    downloadAvailable
    downloadUnavailableError
    userCanOpenInApp
  }
`;
const ShareDropdownOptionsPreloadFragmentDoc = gql `
  fragment ShareDropdownOptionsPreload on Share {
    identifier
    ...ShareDropdownOptionsBase
    version(preferredState: PROCESSED) {
      shortId
      identifier
      document {
        identifier
        sketchVersion
        ...DocumentDownload
      }
    }
  }
  ${ShareDropdownOptionsBaseFragmentDoc}
  ${DocumentDownloadFragmentDoc}
`;
const SharePermissionsFragmentDoc = gql `
  fragment SharePermissions on Share {
    ...ShareDropdownOptionsPreload
    parentAccessLevel
    updatedAt
    userCanInspect
    userCanUpdateParentPermissions
    userCanUpdateSettings
    versionTypeAvailableToUser
  }
  ${ShareDropdownOptionsPreloadFragmentDoc}
`;
const ShareBaseFragmentDoc = gql `
  fragment ShareBase on Share {
    ...ShareListItemBase
    ...ShareSubscriptionStatus
    ...ShareUserAccessLevel
    ...SharePublicPermissions
    ...ShareDropdownOptionsBase
    ...SharePermissions
    type
    userCanUpdateCommentsEnabled
    userCanLeaveShare
    createdAt
    updatedAt
    deletedAt
    privacyIcon
    publicUrl
    allowedParentAccessLevels
    parentAccessLevel
    userCanUpdateParentPermissions
    isShared
    isDraft
    userIsMember
    isSharedWithGuests
    memberships {
      meta {
        totalCount
      }
    }
    description
    commentsEnabled
    userIsWatching
    userHasUnacceptedInvitationToView
    unreadCount
    versionTypeAvailableToUser
  }
  ${ShareListItemBaseFragmentDoc}
  ${ShareSubscriptionStatusFragmentDoc}
  ${ShareUserAccessLevelFragmentDoc}
  ${SharePublicPermissionsFragmentDoc}
  ${ShareDropdownOptionsBaseFragmentDoc}
  ${SharePermissionsFragmentDoc}
`;
const PublicWorkspaceSsoIdentityFragmentDoc = gql `
  fragment PublicWorkspaceSSOIdentity on PublicWorkspace {
    identifier
    name
    userRole
    customer {
      identifier
      ssoEnabled
      ssoStartUrl
    }
  }
`;
const PublicWorkspaceFragmentDoc = gql `
  fragment PublicWorkspace on PublicWorkspace {
    ...PublicWorkspaceSSOIdentity
    identifier
    name
    userIsMember
    userRole
    userIsOwner
    type
    avatar {
      small
      large
    }
  }
  ${PublicWorkspaceSsoIdentityFragmentDoc}
`;
const VersionListItemFragmentDoc = gql `
  fragment VersionListItem on Version {
    identifier
    shortId
    createdAt
    userIsCreator
    createdAfterVersioning
    updatedAt
    description
    kind
    creator {
      identifier
      name
      avatar {
        small
      }
    }
    document {
      identifier
      ...DocumentDownload
      renderStatus
      colorspace
      size
      sketchVersion
      previewFile {
        identifier
        width
        height
        url
        ...ShareThumbnails
      }
    }
  }
  ${DocumentDownloadFragmentDoc}
  ${ShareThumbnailsFragmentDoc}
`;
const DownloadableAssetFragmentDoc = gql `
  fragment DownloadableAsset on DownloadableAsset {
    __typename
    identifier
    status
    fileName
    fileSizeInBytes
    path
    layerUuids
  }
`;
const DocumentDownloadableAssetsFragmentDoc = gql `
  fragment DocumentDownloadableAssets on Document {
    __typename
    identifier
    assetStatus
    downloadableAssets {
      ...DownloadableAsset
    }
  }
  ${DownloadableAssetFragmentDoc}
`;
const VersionDocumentFragmentDoc = gql `
  fragment VersionDocument on Document {
    identifier
    ...DocumentDownload
    renderStatus
    colorspace
    size
    sketchVersion
    downloadUnavailableError
    name
    isPresent
    pageCount
    artboardCount
    documentVersion
    coeditCompatibilityVersion
    canEditDescriptions
    ...DocumentDownloadableAssets
    pages(limit: 1) {
      entries {
        uuid
      }
    }
  }
  ${DocumentDownloadFragmentDoc}
  ${DocumentDownloadableAssetsFragmentDoc}
`;
const VersionFragmentDoc = gql `
  fragment Version on Version {
    ...VersionListItem
    document {
      ...VersionDocument
      previewFile {
        identifier
        width
        height
        url
        ...ShareThumbnails
      }
    }
  }
  ${VersionListItemFragmentDoc}
  ${VersionDocumentFragmentDoc}
  ${ShareThumbnailsFragmentDoc}
`;
const ShareInfoFragmentDoc = gql `
  fragment ShareInfo on Share {
    ...ShareBase
    workspace {
      ...PublicWorkspace
      customer {
        ssoEnabled
      }
    }
    version(preferredState: PROCESSED) {
      ...Version
      document {
        previewFile {
          ...ShareThumbnails
        }
      }
    }
    project {
      ...Project
    }
  }
  ${ShareBaseFragmentDoc}
  ${PublicWorkspaceFragmentDoc}
  ${VersionFragmentDoc}
  ${ShareThumbnailsFragmentDoc}
  ${ProjectFragmentDoc}
`;
const ShareInfoWithCollectionFragmentDoc = gql `
  fragment ShareInfoWithCollection on Share {
    ...ShareInfo
    collection {
      identifier
      name
    }
  }
  ${ShareInfoFragmentDoc}
`;
const ShareViewerHasUnacceptedMembershipInviteFragmentDoc = gql `
  fragment ShareViewerHasUnacceptedMembershipInvite on Share {
    userHasUnacceptedInvitationToView
  }
`;
const SubscriptionShareFragmentDoc = gql `
  fragment SubscriptionShare on Share {
    identifier
    name
    deletedAt
    workspace {
      identifier
    }
    project {
      identifier
      shortId
    }
    userCanUpdateSettings
  }
`;
const ShareDropdownOptionsFragmentDoc = gql `
  fragment ShareDropdownOptions on Share {
    identifier
    ...ShareDropdownOptionsBase
    version(shortId: $versionShortId) {
      shortId
      identifier
      document {
        identifier
        sketchVersion
        ...DocumentDownload
      }
    }
  }
  ${ShareDropdownOptionsBaseFragmentDoc}
  ${DocumentDownloadFragmentDoc}
`;
const ShareWorkspaceFragmentDoc = gql `
  fragment ShareWorkspace on Share {
    identifier
    workspace {
      identifier
      userRole
    }
  }
`;
const ShareAboutFragmentDoc = gql `
  fragment ShareAbout on Share {
    identifier
    description
    name
    commentsEnabled
    type
    userIsMember
    userCanInspect
    userCanUpdateSettings
    updatedAt
    subscriptionStatus
    workspace {
      identifier
      userIsMember
    }
    version(shortId: $versionShortId) {
      identifier
      shortId
      userIsCreator
      creator {
        identifier
        name
        avatar {
          small
        }
      }
      document {
        identifier
        pages(limit: 1) {
          entries {
            uuid
          }
          meta {
            totalCount
          }
        }
        prototypes: artboards(limit: 3, filter: PROTOTYPE) {
          entries {
            ...ArtboardDetailInfo
          }
          meta {
            totalCount
          }
        }
        componentCount {
          colorVar
          layerStyle
          symbol
          textStyle
        }
      }
    }
  }
  ${ArtboardDetailInfoFragmentDoc}
`;
const ShareMembershipInfoFragmentDoc = gql `
  fragment ShareMembershipInfo on ShareMembership {
    identifier
    email
    acceptedAt
    inviteStatus
    privateAccessLevel
    guestCommentsEnabled
    guestInspectEnabled
    userCanUpdateCommentsEnabled
    workspaceMembership {
      role
      user {
        identifier
        name
        email
        avatar {
          small
        }
      }
    }
  }
`;
const UserAccessShareMembershipFragmentDoc = gql `
  fragment UserAccessShareMembership on ShareMembership {
    ...ShareMembershipInfo
    share {
      ...ShareInfo
    }
  }
  ${ShareMembershipInfoFragmentDoc}
  ${ShareInfoFragmentDoc}
`;
const TrashedShareFragmentDoc = gql `
  fragment TrashedShare on TrashedShare {
    identifier
    deletedAt
    updatedAt
    name
    userAccessLevel
    type
    privacyIcon
    userAccessLevel
    version {
      identifier
      userIsCreator
      shortId
      createdAt
      updatedAt
      document {
        identifier
        downloadAvailable
        url
        renderStatus
        previewFile {
          identifier
          width
          height
          url
          ...ShareThumbnails
        }
      }
      creator {
        identifier
        name
        avatar {
          small
        }
      }
    }
    workspace {
      identifier
      userRole
    }
    project {
      ...Project
    }
  }
  ${ShareThumbnailsFragmentDoc}
  ${ProjectFragmentDoc}
`;
const SignUpImageFragmentDoc = gql `
  fragment SignUpImage on SignUpImage {
    src
    alt
  }
`;
const SignUpContentFragmentDoc = gql `
  fragment SignUpContent on SignUpContent {
    title
    tint
    introduction
    features {
      icon {
        ...SignUpImage
      }
      description
    }
    relatedContent {
      title
      description
      thumbnail {
        ...SignUpImage
      }
      url
    }
    callToAction
    heroImage {
      ...SignUpImage
    }
  }
  ${SignUpImageFragmentDoc}
`;
const IdentityProviderConfigFragmentDoc = gql `
  fragment IdentityProviderConfig on IdentityProviderConfig {
    binding
    certificate
    entityId
    ssoUrl
  }
`;
const AttributeMappingEntryFragmentDoc = gql `
  fragment AttributeMappingEntry on AttributeMappingEntry {
    fullName {
      firstName
      surname
      updateOnLogin
    }
    email {
      mapping
      updateOnLogin
    }
  }
`;
const WorkspaceSsoConfigFragmentDoc = gql `
  fragment WorkspaceSsoConfig on WorkspaceSsoConfig {
    acsUrl
    serviceProviderEntityId
    config {
      ...IdentityProviderConfig
    }
    attributeMapping {
      ...AttributeMappingEntry
    }
  }
  ${IdentityProviderConfigFragmentDoc}
  ${AttributeMappingEntryFragmentDoc}
`;
const UserNotificationFragmentDoc = gql `
  fragment UserNotification on UserNotification {
    identifier
    date
    isRead
    title
    subtitle
    path
    user {
      identifier
      name
      avatar {
        small
      }
    }
  }
`;
const NotificationCommentFragmentDoc = gql `
  fragment NotificationComment on Comment {
    identifier
    body
    annotation {
      identifier
      currentSubject {
        ... on Artboard {
          permanentArtboardShortId
          documentVersionShortId
          nameArtboard: name
        }
        ... on Page {
          identifier
          uuid
          name
        }
      }
    }
  }
`;
const CommentNotificationFragmentDoc = gql `
  fragment CommentNotification on CommentNotification {
    ...UserNotification
    comment {
      identifier
      ...NotificationComment
    }
    share {
      identifier
      name
      workspace {
        identifier
        name
      }
    }
    artboard {
      identifier
      shortId
      name
      documentVersionShortId
      permanentArtboardShortId
      files(type: FULL) {
        identifier
        url
        size
        thumbnails {
          type
          url
        }
      }
      ...AnnotationArtboardInfo
    }
  }
  ${UserNotificationFragmentDoc}
  ${NotificationCommentFragmentDoc}
  ${AnnotationArtboardInfoFragmentDoc}
`;
const MentionNotificationFragmentDoc = gql `
  fragment MentionNotification on MentionNotification {
    ...UserNotification
    comment {
      identifier
      ...NotificationComment
    }
    share {
      identifier
      name
      workspace {
        identifier
        name
      }
    }
    artboard {
      identifier
      shortId
      name
      documentVersionShortId
      permanentArtboardShortId
      files(type: FULL) {
        identifier
        url
        size
        thumbnails {
          type
          url
        }
      }
    }
  }
  ${UserNotificationFragmentDoc}
  ${NotificationCommentFragmentDoc}
`;
const AddShareMemberNotificationFragmentDoc = gql `
  fragment AddShareMemberNotification on AddShareMemberNotification {
    ...UserNotification
    token
    share {
      identifier
      name
      userHasUnacceptedInvitationToView
      workspace {
        identifier
        name
      }
      version(preferredState: PROCESSED) {
        shortId
        document {
          identifier
          previewFile {
            identifier
            url
            size
            thumbnails {
              type
              url
            }
          }
        }
      }
    }
  }
  ${UserNotificationFragmentDoc}
`;
const DownloadableAssetRenderedNotificationFragmentDoc = gql `
  fragment DownloadableAssetRenderedNotification on DownloadableAssetRenderedNotification {
    ...UserNotification
    share {
      identifier
      name
      workspace {
        identifier
        name
      }
      version(preferredState: PROCESSED) {
        shortId
        document {
          previewFile {
            identifier
            url
            size
            thumbnails {
              type
              url
            }
          }
        }
      }
    }
    notifiedVersion {
      shortId
      document {
        name
        ...DocumentDownloadableAssets
      }
    }
  }
  ${UserNotificationFragmentDoc}
  ${DocumentDownloadableAssetsFragmentDoc}
`;
const NotificationInfoFragmentDoc = gql `
  fragment NotificationInfo on UserNotification {
    ...UserNotification
    ... on CommentNotification {
      ...CommentNotification
    }
    ... on MentionNotification {
      ...MentionNotification
    }
    ... on AddShareMemberNotification {
      ...AddShareMemberNotification
    }
    ... on DownloadableAssetRenderedNotification {
      ...DownloadableAssetRenderedNotification
    }
  }
  ${UserNotificationFragmentDoc}
  ${CommentNotificationFragmentDoc}
  ${MentionNotificationFragmentDoc}
  ${AddShareMemberNotificationFragmentDoc}
  ${DownloadableAssetRenderedNotificationFragmentDoc}
`;
const ShareMemberInfoFragmentDoc = gql `
  fragment ShareMemberInfo on PublicUser {
    identifier
    name
    avatar {
      small
    }
  }
`;
const NotificationStatusFragmentDoc = gql `
  fragment NotificationStatus on User {
    identifier
    hasUnreadNotifications
    hasUnseenNotifications
  }
`;
const UserEmailFragmentDoc = gql `
  fragment UserEmail on User {
    identifier
    email
  }
`;
const UserPreferencesFragmentDoc = gql `
  fragment UserPreferences on User {
    identifier
    preferences {
      showOnboardingFirstSteps
    }
  }
`;
const UserFragmentDoc = gql `
  fragment User on User {
    email
    identifier
    name
    personalIdentityEmail
    professionalRole
    isVerified
    isSuperadmin
    isPartner
    hasAvatar
    avatar {
      small
    }
    receiveEmailNotifications
    hasAcceptedAgreement
    ...UserPreferences
  }
  ${UserPreferencesFragmentDoc}
`;
const InitialUserFragmentDoc = gql `
  fragment InitialUser on User {
    identifier
    featureFlags
    hasPersonalIdentity
    mfaState
    ...User
  }
  ${UserFragmentDoc}
`;
const WorkspaceMinimalIdentityFragmentDoc = gql `
  fragment WorkspaceMinimalIdentity on Workspace {
    identifier
    name
    avatar {
      small
    }
  }
`;
const WorkspaceSsoIdentityFragmentDoc = gql `
  fragment WorkspaceSSOIdentity on Workspace {
    identifier
    name
    userRole
    customer {
      identifier
      ssoEnabled
      ssoStartUrl
      tosAgreed
    }
  }
`;
const WorkspaceMinimalFragmentDoc = gql `
  fragment WorkspaceMinimal on Workspace {
    ...WorkspaceMinimalIdentity
    ...WorkspaceSSOIdentity
    type
    avatar {
      large
    }
    status
    userCanEdit
    userRole
    userIsOwner
    status
    type
    features {
      restrictedPublicLinks
      workspaceProfileEnabled
    }
  }
  ${WorkspaceMinimalIdentityFragmentDoc}
  ${WorkspaceSsoIdentityFragmentDoc}
`;
const UserWorkspaceMembershipFragmentDoc = gql `
  fragment UserWorkspaceMembership on WorkspaceMembership {
    identifier
    role
    isEditor
    personalTokenPermissions
    workspace {
      ...WorkspaceMinimal
      customer {
        billing {
          status
          trialEnd
        }
      }
    }
    user {
      identifier
      email
    }
  }
  ${WorkspaceMinimalFragmentDoc}
`;
const UserActiveSessionFragmentDoc = gql `
  fragment UserActiveSession on Session {
    id
    lastUpdateAt
    type
    ipAddress
    countryCode
    deviceName
    userAgent
    isCurrentOne
  }
`;
const UserNotificationCountFragmentDoc = gql `
  fragment UserNotificationCount on User {
    identifier
    notifications(filter: { state: NEW }) {
      meta {
        totalCount
      }
    }
  }
`;
const ReadShareFragmentDoc = gql `
  fragment ReadShare on Share {
    identifier
  }
`;
const ArtboardRevisionFragmentDoc = gql `
  fragment ArtboardRevision on Revision {
    identifier
    kind
    creator {
      identifier
      name
    }
    userIsCreator
    createdAt
    version {
      identifier
      shortId
      description
    }
    artboard {
      permanentArtboardShortId
      documentVersionShortId
      uuid
    }
  }
`;
const ArtboardRevisionDeletedFragmentDoc = gql `
  fragment ArtboardRevisionDeleted on RevisionDeleted {
    identifier
    creator {
      name
    }
    userIsCreator
    createdAt
    artboardIdentifier
  }
`;
const ArtboardRevisionUnavailableFragmentDoc = gql `
  fragment ArtboardRevisionUnavailable on RevisionUnavailable {
    identifier
    creator {
      name
    }
    userIsCreator
    createdAt
  }
`;
const ShareLatestStarredVersionFragmentDoc = gql `
  fragment ShareLatestStarredVersion on Share {
    identifier
    lastStarredVersion: version(preferredState: PROCESSED_AND_PUBLISHED) {
      kind
      ...Version
    }
  }
  ${VersionFragmentDoc}
`;
const VersionDeletedFragmentDoc = gql `
  fragment VersionDeleted on VersionDeleted {
    identifier
    deletedAt
    createdAt
    order
    creator {
      identifier
      name
    }
    userIsCreator
  }
`;
const VersionUnavailableFragmentDoc = gql `
  fragment VersionUnavailable on VersionUnavailable {
    identifier
    createdAt
    creator {
      identifier
      name
    }
    userIsCreator
  }
`;
const VersionUnavailableWithOrderFragmentDoc = gql `
  fragment VersionUnavailableWithOrder on VersionUnavailable {
    ...VersionUnavailable
    order
  }
  ${VersionUnavailableFragmentDoc}
`;
const VersionListItemWithOrderFragmentDoc = gql `
  fragment VersionListItemWithOrder on Version {
    ...VersionListItem
    order
  }
  ${VersionListItemFragmentDoc}
`;
const ShareVersionHistoryFragmentDoc = gql `
  fragment ShareVersionHistory on Share {
    identifier
    availableVersions: versionHistory(include: [VERSION]) {
      meta {
        totalCount
      }
    }
    ...ShareLatestStarredVersion
    versionHistory(
      after: $after
      limit: $limit
      include: [VERSION, VERSION_UNAVAILABLE]
    ) {
      entries {
        ...VersionDeleted
        ...VersionUnavailableWithOrder
        ...VersionListItemWithOrder
      }
      meta {
        totalCount
        after
      }
    }
  }
  ${ShareLatestStarredVersionFragmentDoc}
  ${VersionDeletedFragmentDoc}
  ${VersionUnavailableWithOrderFragmentDoc}
  ${VersionListItemWithOrderFragmentDoc}
`;
const BillingMinimalFragmentDoc = gql `
  fragment BillingMinimal on Billing {
    status
    subscriptionEnd
  }
`;
const BillingWorkspaceNoticeFragmentDoc = gql `
  fragment BillingWorkspaceNotice on Billing {
    status
    subscriptionEnd
    trialEnd
    subscriptionSetUp
  }
`;
const BillingDetailsFragmentDoc = gql `
  fragment BillingDetails on BillingDetails {
    name
    email
    address {
      line1
      city
      state
      country
      postalCode
    }
    taxId {
      id
      status
    }
  }
`;
const CouponMinimalFragmentDoc = gql `
  fragment CouponMinimal on CouponInfo {
    promotionCode
    percentOff
    endDate
  }
`;
const BillingHistoryEntryFragmentDoc = gql `
  fragment BillingHistoryEntry on BillingHistoryEntry {
    id
    amount
    description
    createdAt
    ... on CustomerInvoice {
      pdf
      couponInfo {
        ...CouponMinimal
      }
    }
    ... on CreditTransaction {
      licenseRedemption {
        serial
        months
        seats
        credits
      }
    }
  }
  ${CouponMinimalFragmentDoc}
`;
const BillingPlanFragmentDoc = gql `
  fragment BillingPlan on BillingPlan {
    id
    name
    type
    invoicePrice
    monthlyPrice
    product
  }
`;
const BillingSeatsInfoFragmentDoc = gql `
  fragment BillingSeatsInfo on BillingSeatsInfo {
    availableSeats
    usedSeats
    currentSeatsTotal
    scheduledSeatsTotal
  }
`;
const CouponFragmentDoc = gql `
  fragment Coupon on CouponInfo {
    promotionCode
    percentOff
    amountOff
    errorMessage
    errorCode
  }
`;
const LicenseRedemptionFragmentDoc = gql `
  fragment LicenseRedemption on LicenseRedemption {
    id
    status
    createdAt
    ownerEmail
    serial
    credits
  }
`;
const LicenseRedemptionPreviewFragmentDoc = gql `
  fragment LicenseRedemptionPreview on LicenseRedemptionPreview {
    ownerEmail
    serial
  }
`;
const PaymentDetailsFragmentDoc = gql `
  fragment PaymentDetails on PaymentDetails {
    type
    ... on CreditCardPaymentDetails {
      id
      last4Digits
      brand
      expiration {
        month
        year
      }
    }
  }
`;
const PublicWorkspaceMinimalFragmentDoc = gql `
  fragment PublicWorkspaceMinimal on PublicWorkspace {
    identifier
    name
    avatar {
      small
    }
    customer {
      identifier
      ssoStartUrl
      ssoEnabled
    }
  }
`;
const WebhookDestinationFragmentDoc = gql `
  fragment WebhookDestination on WebhookDestination {
    url
  }
`;
const WorkspaceMinimalWithBillingStatusFragmentDoc = gql `
  fragment WorkspaceMinimalWithBillingStatus on Workspace {
    ...WorkspaceMinimal
    customer @include(if: $includeBilling) {
      identifier
      billing {
        status
      }
    }
  }
  ${WorkspaceMinimalFragmentDoc}
`;
const DuplicationWorkspaceFragmentDoc = gql `
  fragment DuplicationWorkspace on Workspace {
    ...WorkspaceMinimalIdentity
    ...WorkspaceSSOIdentity
    status
    userCanEdit
    uploadsRestrictedFor
  }
  ${WorkspaceMinimalIdentityFragmentDoc}
  ${WorkspaceSsoIdentityFragmentDoc}
`;
const InvitableUserFragmentDoc = gql `
  fragment InvitableUser on WorkspaceMembership {
    isEditor
    role
    identifier
    user {
      identifier
      avatar {
        small
      }
      email
      name
    }
  }
`;
const InvitableUserEmailFragmentDoc = gql `
  fragment InvitableUserEmail on WorkspaceMembership {
    identifier
    user {
      identifier
      email
    }
  }
`;
const RevisionCreatedDocument = gql `
  subscription revisionCreated(
    $permanentArtboardShortId: String!
    $includeArtboardDetails: Boolean = false
  ) {
    revisionCreated(permanentArtboardShortId: $permanentArtboardShortId) {
      ...RevisionInfo
    }
  }
  ${RevisionInfoFragmentDoc}
`;
/**
 * __useRevisionCreatedSubscription__
 *
 * To run a query within a React component, call `useRevisionCreatedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useRevisionCreatedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useRevisionCreatedSubscription({
 *   variables: {
 *      permanentArtboardShortId: // value for 'permanentArtboardShortId'
 *      includeArtboardDetails: // value for 'includeArtboardDetails'
 *   },
 * });
 */
function useRevisionCreatedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(RevisionCreatedDocument, baseOptions);
}
const RevisionDeletedDocument = gql `
  subscription revisionDeleted($permanentArtboardShortId: String!) {
    revisionDeleted(permanentArtboardShortId: $permanentArtboardShortId) {
      ...RevisionDeleted
    }
  }
  ${RevisionDeletedFragmentDoc}
`;
/**
 * __useRevisionDeletedSubscription__
 *
 * To run a query within a React component, call `useRevisionDeletedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useRevisionDeletedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useRevisionDeletedSubscription({
 *   variables: {
 *      permanentArtboardShortId: // value for 'permanentArtboardShortId'
 *   },
 * });
 */
function useRevisionDeletedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(RevisionDeletedDocument, baseOptions);
}
const RevisionMovedDocument = gql `
  subscription revisionMoved(
    $permanentArtboardShortId: String!
    $includeArtboardDetails: Boolean = false
  ) {
    revisionMoved(permanentArtboardShortId: $permanentArtboardShortId) {
      previousArtboardIdentifier
      revision {
        ...RevisionInfo
      }
    }
  }
  ${RevisionInfoFragmentDoc}
`;
/**
 * __useRevisionMovedSubscription__
 *
 * To run a query within a React component, call `useRevisionMovedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useRevisionMovedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useRevisionMovedSubscription({
 *   variables: {
 *      permanentArtboardShortId: // value for 'permanentArtboardShortId'
 *      includeArtboardDetails: // value for 'includeArtboardDetails'
 *   },
 * });
 */
function useRevisionMovedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(RevisionMovedDocument, baseOptions);
}
const CreateAnnotationDocument = gql `
  mutation createAnnotation(
    $shareIdentifier: UUID!
    $body: String!
    $subject: AnnotationSubjectInput!
    $coordinates: AnnotationCoordinatesInput
    $includeHasUnreadComments: Boolean = false
  ) {
    createAnnotation(
      body: $body
      shareIdentifier: $shareIdentifier
      subject: $subject
      coordinates: $coordinates
    ) {
      annotation {
        ...BaseAnnotation
        isOptimistic @client
        ...AnnotationCoordinates
        ...AnnotationSubjectSubscriptionStatus
        ...AnnotationSubjectCounters
      }
    }
  }
  ${BaseAnnotationFragmentDoc}
  ${AnnotationCoordinatesFragmentDoc}
  ${AnnotationSubjectSubscriptionStatusFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useCreateAnnotationMutation__
 *
 * To run a mutation, you first call `useCreateAnnotationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateAnnotationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createAnnotationMutation, { data, loading, error }] = useCreateAnnotationMutation({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      body: // value for 'body'
 *      subject: // value for 'subject'
 *      coordinates: // value for 'coordinates'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useCreateAnnotationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateAnnotationDocument, baseOptions);
}
const MoveAnnotationDocument = gql `
  mutation moveAnnotation(
    $annotationIdentifier: UUID!
    $coordinates: AnnotationCoordinatesInput!
    $subject: AnnotationSubjectInput!
    $includeHasUnreadComments: Boolean = false
  ) {
    moveAnnotation(
      annotationIdentifier: $annotationIdentifier
      coordinates: $coordinates
      subject: $subject
    ) {
      annotation {
        identifier
        ...AnnotationCoordinates
        ...AnnotationSubjectCounters
      }
    }
  }
  ${AnnotationCoordinatesFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useMoveAnnotationMutation__
 *
 * To run a mutation, you first call `useMoveAnnotationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMoveAnnotationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [moveAnnotationMutation, { data, loading, error }] = useMoveAnnotationMutation({
 *   variables: {
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *      coordinates: // value for 'coordinates'
 *      subject: // value for 'subject'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useMoveAnnotationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(MoveAnnotationDocument, baseOptions);
}
const ResolveAnnotationDocument = gql `
  mutation resolveAnnotation($annotationIdentifier: UUID!) {
    resolveAnnotation(annotationIdentifier: $annotationIdentifier) {
      annotation {
        identifier
        ...AnnotationResolutionInfo
        ...AnnotationSubject
      }
    }
  }
  ${AnnotationResolutionInfoFragmentDoc}
  ${AnnotationSubjectFragmentDoc}
`;
/**
 * __useResolveAnnotationMutation__
 *
 * To run a mutation, you first call `useResolveAnnotationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useResolveAnnotationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [resolveAnnotationMutation, { data, loading, error }] = useResolveAnnotationMutation({
 *   variables: {
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *   },
 * });
 */
function useResolveAnnotationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ResolveAnnotationDocument, baseOptions);
}
const UnResolveAnnotationDocument = gql `
  mutation unResolveAnnotation($annotationIdentifier: UUID!) {
    unresolveAnnotation(annotationIdentifier: $annotationIdentifier) {
      annotation {
        identifier
        ...AnnotationResolutionInfo
        ...AnnotationSubject
      }
    }
  }
  ${AnnotationResolutionInfoFragmentDoc}
  ${AnnotationSubjectFragmentDoc}
`;
/**
 * __useUnResolveAnnotationMutation__
 *
 * To run a mutation, you first call `useUnResolveAnnotationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUnResolveAnnotationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [unResolveAnnotationMutation, { data, loading, error }] = useUnResolveAnnotationMutation({
 *   variables: {
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *   },
 * });
 */
function useUnResolveAnnotationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UnResolveAnnotationDocument, baseOptions);
}
const DeleteAnnotationDocument = gql `
  mutation deleteAnnotation(
    $identifier: UUID!
    $includeHasUnreadComments: Boolean = false
  ) {
    deleteAnnotation(annotationIdentifier: $identifier) {
      annotation {
        identifier
        ...AnnotationSubjectCounters
      }
    }
  }
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useDeleteAnnotationMutation__
 *
 * To run a mutation, you first call `useDeleteAnnotationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteAnnotationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteAnnotationMutation, { data, loading, error }] = useDeleteAnnotationMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useDeleteAnnotationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteAnnotationDocument, baseOptions);
}
const GetAnnotationsDocument = gql `
  query getAnnotations(
    $shareIdentifier: UUID!
    $versionIdentifier: UUID
    $subject: AnnotationSubjectInput
    $after: String
    $sort: AnnotationSortOrder
    $annotationStatus: AnnotationStatusFilter
  ) {
    annotations(
      shareIdentifier: $shareIdentifier
      versionIdentifier: $versionIdentifier
      subject: $subject
      after: $after
      sortOrder: $sort
      status: $annotationStatus
    ) {
      entries {
        ...BaseAnnotation
      }
      meta {
        after
        totalCount
      }
    }
  }
  ${BaseAnnotationFragmentDoc}
`;
/**
 * __useGetAnnotationsQuery__
 *
 * To run a query within a React component, call `useGetAnnotationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAnnotationsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAnnotationsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionIdentifier: // value for 'versionIdentifier'
 *      subject: // value for 'subject'
 *      after: // value for 'after'
 *      sort: // value for 'sort'
 *      annotationStatus: // value for 'annotationStatus'
 *   },
 * });
 */
function useGetAnnotationsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAnnotationsDocument, baseOptions);
}
function useGetAnnotationsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAnnotationsDocument, baseOptions);
}
const GetAnnotationCommentsDocument = gql `
  query getAnnotationComments($annotationIdentifier: UUID!, $after: String) {
    annotation(identifier: $annotationIdentifier) {
      identifier
      isNew
      share {
        identifier
      }
      ...AnnotationSubject
      subscriptionStatus
      ...AnnotationResolutionInfo
      firstComment {
        ...BaseComment
      }
      comments(after: $after) {
        entries {
          ...Comment
        }
        meta {
          after
          totalCount
        }
      }
    }
  }
  ${AnnotationSubjectFragmentDoc}
  ${AnnotationResolutionInfoFragmentDoc}
  ${BaseCommentFragmentDoc}
  ${CommentFragmentDoc}
`;
/**
 * __useGetAnnotationCommentsQuery__
 *
 * To run a query within a React component, call `useGetAnnotationCommentsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAnnotationCommentsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAnnotationCommentsQuery({
 *   variables: {
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *      after: // value for 'after'
 *   },
 * });
 */
function useGetAnnotationCommentsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAnnotationCommentsDocument, baseOptions);
}
function useGetAnnotationCommentsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAnnotationCommentsDocument, baseOptions);
}
const GetMentionableUsersDocument = gql `
  query getMentionableUsers($shareIdentifier: UUID!) {
    share(id: $shareIdentifier) {
      identifier
      mentionableUsers {
        avatar {
          small
        }
        identifier
        name
      }
    }
  }
`;
/**
 * __useGetMentionableUsersQuery__
 *
 * To run a query within a React component, call `useGetMentionableUsersQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetMentionableUsersQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetMentionableUsersQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetMentionableUsersQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetMentionableUsersDocument, baseOptions);
}
function useGetMentionableUsersLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetMentionableUsersDocument, baseOptions);
}
const GetAnnotationDotsDocument = gql `
  query getAnnotationDots(
    $shareIdentifier: UUID!
    $versionIdentifier: UUID
    $subject: AnnotationSubjectInput!
    $after: String
    $annotationStatus: AnnotationStatusFilter
    $sort: AnnotationSortOrder
  ) {
    annotations(
      shareIdentifier: $shareIdentifier
      versionIdentifier: $versionIdentifier
      subject: $subject
      limit: 100
      after: $after
      status: $annotationStatus
      sortOrder: $sort
    ) {
      entries {
        ...AnnotationDot
      }
      meta {
        after
        totalCount
      }
    }
  }
  ${AnnotationDotFragmentDoc}
`;
/**
 * __useGetAnnotationDotsQuery__
 *
 * To run a query within a React component, call `useGetAnnotationDotsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAnnotationDotsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAnnotationDotsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionIdentifier: // value for 'versionIdentifier'
 *      subject: // value for 'subject'
 *      after: // value for 'after'
 *      annotationStatus: // value for 'annotationStatus'
 *      sort: // value for 'sort'
 *   },
 * });
 */
function useGetAnnotationDotsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAnnotationDotsDocument, baseOptions);
}
function useGetAnnotationDotsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAnnotationDotsDocument, baseOptions);
}
const GetAnnotationSettingsDocument = gql `
  query getAnnotationSettings {
    hideAnnotationsDots @client
  }
`;
/**
 * __useGetAnnotationSettingsQuery__
 *
 * To run a query within a React component, call `useGetAnnotationSettingsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAnnotationSettingsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAnnotationSettingsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetAnnotationSettingsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAnnotationSettingsDocument, baseOptions);
}
function useGetAnnotationSettingsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAnnotationSettingsDocument, baseOptions);
}
const GetAnnotationDotDocument = gql `
  query getAnnotationDot($identifier: UUID!) {
    annotation(identifier: $identifier) {
      ...AnnotationDot
    }
  }
  ${AnnotationDotFragmentDoc}
`;
/**
 * __useGetAnnotationDotQuery__
 *
 * To run a query within a React component, call `useGetAnnotationDotQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAnnotationDotQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAnnotationDotQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetAnnotationDotQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAnnotationDotDocument, baseOptions);
}
function useGetAnnotationDotLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAnnotationDotDocument, baseOptions);
}
const GetAnnotationLatestVisibleVersionDocument = gql `
  query getAnnotationLatestVisibleVersion($identifier: UUID!) {
    annotation(identifier: $identifier) {
      latestVisibleVersion {
        isLatest
        identifier
        shortId
      }
    }
  }
`;
/**
 * __useGetAnnotationLatestVisibleVersionQuery__
 *
 * To run a query within a React component, call `useGetAnnotationLatestVisibleVersionQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAnnotationLatestVisibleVersionQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAnnotationLatestVisibleVersionQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetAnnotationLatestVisibleVersionQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAnnotationLatestVisibleVersionDocument, baseOptions);
}
function useGetAnnotationLatestVisibleVersionLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAnnotationLatestVisibleVersionDocument, baseOptions);
}
const GetAnnotationSummaryDocument = gql `
  query getAnnotationSummary($identifier: UUID!) {
    annotation(identifier: $identifier) {
      identifier
      summary {
        body
        resolution
      }
    }
  }
`;
/**
 * __useGetAnnotationSummaryQuery__
 *
 * To run a query within a React component, call `useGetAnnotationSummaryQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAnnotationSummaryQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAnnotationSummaryQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetAnnotationSummaryQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAnnotationSummaryDocument, baseOptions);
}
function useGetAnnotationSummaryLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAnnotationSummaryDocument, baseOptions);
}
const AnnotationCreatedDocument = gql `
  subscription annotationCreated(
    $permanentArtboardId: String
    $permanentPageId: String
    $shareIdentifier: UUID!
    $includeHasUnreadComments: Boolean = false
  ) {
    annotationCreated(
      permanentArtboardId: $permanentArtboardId
      permanentPageId: $permanentPageId
      shareIdentifier: $shareIdentifier
    ) {
      ...BaseAnnotation
      ...AnnotationSubjectCounters
      ...AnnotationSubjectSubscriptionStatus
    }
  }
  ${BaseAnnotationFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
  ${AnnotationSubjectSubscriptionStatusFragmentDoc}
`;
/**
 * __useAnnotationCreatedSubscription__
 *
 * To run a query within a React component, call `useAnnotationCreatedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationCreatedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationCreatedSubscription({
 *   variables: {
 *      permanentArtboardId: // value for 'permanentArtboardId'
 *      permanentPageId: // value for 'permanentPageId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useAnnotationCreatedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationCreatedDocument, baseOptions);
}
const AnnotationDeletedDocument = gql `
  subscription annotationDeleted(
    $annotationIdentifier: UUID
    $permanentArtboardId: String
    $permanentPageId: String
    $shareIdentifier: UUID
    $subject: AnnotationSubjectInput
    $includeHasUnreadComments: Boolean = false
  ) {
    annotationDeleted(
      annotationIdentifier: $annotationIdentifier
      permanentArtboardId: $permanentArtboardId
      permanentPageId: $permanentPageId
      shareIdentifier: $shareIdentifier
      subject: $subject
    ) {
      identifier
      ...DeletedAnnotationSubjectCounters
    }
  }
  ${DeletedAnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useAnnotationDeletedSubscription__
 *
 * To run a query within a React component, call `useAnnotationDeletedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationDeletedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationDeletedSubscription({
 *   variables: {
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *      permanentArtboardId: // value for 'permanentArtboardId'
 *      permanentPageId: // value for 'permanentPageId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *      subject: // value for 'subject'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useAnnotationDeletedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationDeletedDocument, baseOptions);
}
const AnnotationMovedDocument = gql `
  subscription annotationMoved(
    $permanentArtboardId: String
    $permanentPageId: String
    $shareIdentifier: UUID!
    $subject: AnnotationSubjectInput
    $includeHasUnreadComments: Boolean = false
  ) {
    annotationMoved(
      permanentArtboardId: $permanentArtboardId
      permanentPageId: $permanentPageId
      shareIdentifier: $shareIdentifier
      subject: $subject
    ) {
      ...BaseAnnotation
      ...AnnotationSubjectSubscriptionStatus
      ...AnnotationSubjectCounters
    }
  }
  ${BaseAnnotationFragmentDoc}
  ${AnnotationSubjectSubscriptionStatusFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useAnnotationMovedSubscription__
 *
 * To run a query within a React component, call `useAnnotationMovedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationMovedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationMovedSubscription({
 *   variables: {
 *      permanentArtboardId: // value for 'permanentArtboardId'
 *      permanentPageId: // value for 'permanentPageId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *      subject: // value for 'subject'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useAnnotationMovedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationMovedDocument, baseOptions);
}
const AnnotationResolvedDocument = gql `
  subscription annotationResolved(
    $shareIdentifier: UUID!
    $annotationIdentifier: UUID
  ) {
    annotationResolved(
      shareIdentifier: $shareIdentifier
      annotationIdentifier: $annotationIdentifier
    ) {
      identifier
      ...BaseAnnotation
    }
  }
  ${BaseAnnotationFragmentDoc}
`;
/**
 * __useAnnotationResolvedSubscription__
 *
 * To run a query within a React component, call `useAnnotationResolvedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationResolvedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationResolvedSubscription({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *   },
 * });
 */
function useAnnotationResolvedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationResolvedDocument, baseOptions);
}
const AnnotationUnresolvedDocument = gql `
  subscription annotationUnresolved(
    $shareIdentifier: UUID!
    $annotationIdentifier: UUID
  ) {
    annotationUnresolved(
      shareIdentifier: $shareIdentifier
      annotationIdentifier: $annotationIdentifier
    ) {
      identifier
      ...BaseAnnotation
    }
  }
  ${BaseAnnotationFragmentDoc}
`;
/**
 * __useAnnotationUnresolvedSubscription__
 *
 * To run a query within a React component, call `useAnnotationUnresolvedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationUnresolvedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationUnresolvedSubscription({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *   },
 * });
 */
function useAnnotationUnresolvedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationUnresolvedDocument, baseOptions);
}
const EditCommentDocument = gql `
  mutation editComment($identifier: UUID!, $body: String!) {
    editComment(commentIdentifier: $identifier, body: $body) {
      comment {
        identifier
        body
        isEdited
      }
    }
  }
`;
/**
 * __useEditCommentMutation__
 *
 * To run a mutation, you first call `useEditCommentMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEditCommentMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [editCommentMutation, { data, loading, error }] = useEditCommentMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      body: // value for 'body'
 *   },
 * });
 */
function useEditCommentMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EditCommentDocument, baseOptions);
}
const CreateAnnotationCommentDocument = gql `
  mutation createAnnotationComment(
    $identifier: UUID!
    $body: String!
    $includeHasUnreadComments: Boolean = false
  ) {
    createAnnotationReply(annotationIdentifier: $identifier, body: $body) {
      comment {
        ...Comment
        annotation {
          ...AnnotationSubjectSubscriptionStatus
          ...AnnotationSubjectCounters
          ...AnnotationLastComment
        }
      }
    }
  }
  ${CommentFragmentDoc}
  ${AnnotationSubjectSubscriptionStatusFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
  ${AnnotationLastCommentFragmentDoc}
`;
/**
 * __useCreateAnnotationCommentMutation__
 *
 * To run a mutation, you first call `useCreateAnnotationCommentMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateAnnotationCommentMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createAnnotationCommentMutation, { data, loading, error }] = useCreateAnnotationCommentMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      body: // value for 'body'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useCreateAnnotationCommentMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateAnnotationCommentDocument, baseOptions);
}
const DeleteAnnotationReplyDocument = gql `
  mutation deleteAnnotationReply($identifier: UUID!) {
    deleteAnnotationReply(commentIdentifier: $identifier) {
      comment {
        identifier
        annotation {
          identifier
          ...AnnotationCommentCount
        }
      }
    }
  }
  ${AnnotationCommentCountFragmentDoc}
`;
/**
 * __useDeleteAnnotationReplyMutation__
 *
 * To run a mutation, you first call `useDeleteAnnotationReplyMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteAnnotationReplyMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteAnnotationReplyMutation, { data, loading, error }] = useDeleteAnnotationReplyMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useDeleteAnnotationReplyMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteAnnotationReplyDocument, baseOptions);
}
const MarkCommentsAsReadDocument = gql `
  mutation markCommentsAsRead(
    $commentIdentifiers: [UUID!]!
    $includeHasUnreadComments: Boolean = false
  ) {
    markCommentsAsRead(commentIdentifiers: $commentIdentifiers) {
      comments {
        identifier
        isRead
        annotation {
          identifier
          hasNewComments
          isNew
          ...AnnotationSubjectCounters
        }
      }
    }
  }
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useMarkCommentsAsReadMutation__
 *
 * To run a mutation, you first call `useMarkCommentsAsReadMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMarkCommentsAsReadMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [markCommentsAsReadMutation, { data, loading, error }] = useMarkCommentsAsReadMutation({
 *   variables: {
 *      commentIdentifiers: // value for 'commentIdentifiers'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useMarkCommentsAsReadMutation(baseOptions) {
    return ApolloReactHooks.useMutation(MarkCommentsAsReadDocument, baseOptions);
}
const GetBaseCommentDocument = gql `
  query getBaseComment($identifier: UUID!) {
    comment(identifier: $identifier) @client {
      identifier
      ...BaseComment
    }
  }
  ${BaseCommentFragmentDoc}
`;
/**
 * __useGetBaseCommentQuery__
 *
 * To run a query within a React component, call `useGetBaseCommentQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetBaseCommentQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetBaseCommentQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetBaseCommentQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetBaseCommentDocument, baseOptions);
}
function useGetBaseCommentLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetBaseCommentDocument, baseOptions);
}
const AnnotationReplyCreatedDocument = gql `
  subscription annotationReplyCreated(
    $shareIdentifier: UUID!
    $includeHasUnreadComments: Boolean = false
  ) {
    annotationReplyCreated(shareIdentifier: $shareIdentifier) {
      identifier
      ...Comment
      annotation {
        ...BaseAnnotation
        ...AnnotationSubjectCounters
      }
    }
  }
  ${CommentFragmentDoc}
  ${BaseAnnotationFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useAnnotationReplyCreatedSubscription__
 *
 * To run a query within a React component, call `useAnnotationReplyCreatedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationReplyCreatedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationReplyCreatedSubscription({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useAnnotationReplyCreatedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationReplyCreatedDocument, baseOptions);
}
const AnnotationReplyDeletedDocument = gql `
  subscription annotationReplyDeleted(
    $shareIdentifier: UUID!
    $includeHasUnreadComments: Boolean = false
  ) {
    annotationReplyDeleted(shareIdentifier: $shareIdentifier) {
      identifier
      annotation {
        ...BaseAnnotation
        ...AnnotationSubjectCounters
      }
    }
  }
  ${BaseAnnotationFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useAnnotationReplyDeletedSubscription__
 *
 * To run a query within a React component, call `useAnnotationReplyDeletedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationReplyDeletedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationReplyDeletedSubscription({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useAnnotationReplyDeletedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationReplyDeletedDocument, baseOptions);
}
const AnnotationReplyEditedDocument = gql `
  subscription annotationReplyEdited($shareIdentifier: UUID!) {
    commentEdited(shareIdentifier: $shareIdentifier) {
      identifier
      body
      isEdited
    }
  }
`;
/**
 * __useAnnotationReplyEditedSubscription__
 *
 * To run a query within a React component, call `useAnnotationReplyEditedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationReplyEditedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationReplyEditedSubscription({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useAnnotationReplyEditedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationReplyEditedDocument, baseOptions);
}
const AnnotationReplyReadDocument = gql `
  subscription annotationReplyRead(
    $shareIdentifier: UUID!
    $includeHasUnreadComments: Boolean = false
  ) {
    commentsMarkedAsRead(shareIdentifier: $shareIdentifier) {
      identifier
      body
      isRead
      annotation {
        ...BaseAnnotation
        ...AnnotationSubjectCounters
      }
    }
  }
  ${BaseAnnotationFragmentDoc}
  ${AnnotationSubjectCountersFragmentDoc}
`;
/**
 * __useAnnotationReplyReadSubscription__
 *
 * To run a query within a React component, call `useAnnotationReplyReadSubscription` and pass it any options that fit your needs.
 * When your component renders, `useAnnotationReplyReadSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAnnotationReplyReadSubscription({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useAnnotationReplyReadSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(AnnotationReplyReadDocument, baseOptions);
}
const SetNotificationSubscriptionDocument = gql `
  mutation setNotificationSubscription(
    $shareIdentifier: UUID
    $permanentArtboardId: String
    $annotationIdentifier: UUID
    $state: NotificationSubscriptionState!
  ) {
    setNotificationSubscription(
      shareIdentifier: $shareIdentifier
      annotationIdentifier: $annotationIdentifier
      permanentArtboardId: $permanentArtboardId
      state: $state
    ) {
      share {
        identifier
        subscriptionStatus
      }
      annotation {
        identifier
        subscriptionStatus
      }
      artboard {
        permanentArtboardShortId
        documentVersionShortId
        subscriptionStatus
      }
    }
  }
`;
/**
 * __useSetNotificationSubscriptionMutation__
 *
 * To run a mutation, you first call `useSetNotificationSubscriptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSetNotificationSubscriptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [setNotificationSubscriptionMutation, { data, loading, error }] = useSetNotificationSubscriptionMutation({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      permanentArtboardId: // value for 'permanentArtboardId'
 *      annotationIdentifier: // value for 'annotationIdentifier'
 *      state: // value for 'state'
 *   },
 * });
 */
function useSetNotificationSubscriptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SetNotificationSubscriptionDocument, baseOptions);
}
const SignInDocument = gql `
  mutation signIn($email: String!, $password: String!) {
    signIn(email: $email, password: $password) @client {
      ... on MFACredentials {
        mfaToken
      }
      ... on SignInCredentials {
        credentials {
          ...Credentials
        }
      }
    }
  }
  ${CredentialsFragmentDoc}
`;
/**
 * __useSignInMutation__
 *
 * To run a mutation, you first call `useSignInMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSignInMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [signInMutation, { data, loading, error }] = useSignInMutation({
 *   variables: {
 *      email: // value for 'email'
 *      password: // value for 'password'
 *   },
 * });
 */
function useSignInMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SignInDocument, baseOptions);
}
const SignUpDocument = gql `
  mutation signUp($input: SignUpInput!) {
    signUp(input: $input) {
      errors {
        ...UserError
      }
      successful
      credentials {
        ...Credentials
      }
      user {
        identifier
        ...InitialUser
      }
    }
  }
  ${UserErrorFragmentDoc}
  ${CredentialsFragmentDoc}
  ${InitialUserFragmentDoc}
`;
/**
 * __useSignUpMutation__
 *
 * To run a mutation, you first call `useSignUpMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSignUpMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [signUpMutation, { data, loading, error }] = useSignUpMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useSignUpMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SignUpDocument, baseOptions);
}
const SignInWithSsoDocument = gql `
  mutation signInWithSso($exchangeToken: String!) {
    signInWithSso(exchangeToken: $exchangeToken) @client {
      credentials {
        ...Credentials
      }
      workspaceId
    }
  }
  ${CredentialsFragmentDoc}
`;
/**
 * __useSignInWithSsoMutation__
 *
 * To run a mutation, you first call `useSignInWithSsoMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSignInWithSsoMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [signInWithSsoMutation, { data, loading, error }] = useSignInWithSsoMutation({
 *   variables: {
 *      exchangeToken: // value for 'exchangeToken'
 *   },
 * });
 */
function useSignInWithSsoMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SignInWithSsoDocument, baseOptions);
}
const RevokeOAuthDocument = gql `
  mutation revokeOAuth($token: String!) {
    revokeOAuth(token: $token) @client {
      successful
    }
  }
`;
/**
 * __useRevokeOAuthMutation__
 *
 * To run a mutation, you first call `useRevokeOAuthMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRevokeOAuthMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [revokeOAuthMutation, { data, loading, error }] = useRevokeOAuthMutation({
 *   variables: {
 *      token: // value for 'token'
 *   },
 * });
 */
function useRevokeOAuthMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RevokeOAuthDocument, baseOptions);
}
const ValidateMfaTotpDocument = gql `
  mutation validateMfaTotp($token: String!, $totp: String!) {
    validateMfaTotp(token: $token, totp: $totp) @client {
      credentials {
        ...Credentials
      }
    }
  }
  ${CredentialsFragmentDoc}
`;
/**
 * __useValidateMfaTotpMutation__
 *
 * To run a mutation, you first call `useValidateMfaTotpMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useValidateMfaTotpMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [validateMfaTotpMutation, { data, loading, error }] = useValidateMfaTotpMutation({
 *   variables: {
 *      token: // value for 'token'
 *      totp: // value for 'totp'
 *   },
 * });
 */
function useValidateMfaTotpMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ValidateMfaTotpDocument, baseOptions);
}
const ValidateMfaRecoveryCodeDocument = gql `
  mutation validateMfaRecoveryCode($token: String!, $recoveryCode: String!) {
    validateMfaRecoveryCode(token: $token, recoveryCode: $recoveryCode)
      @client {
      credentials {
        ...Credentials
      }
    }
  }
  ${CredentialsFragmentDoc}
`;
/**
 * __useValidateMfaRecoveryCodeMutation__
 *
 * To run a mutation, you first call `useValidateMfaRecoveryCodeMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useValidateMfaRecoveryCodeMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [validateMfaRecoveryCodeMutation, { data, loading, error }] = useValidateMfaRecoveryCodeMutation({
 *   variables: {
 *      token: // value for 'token'
 *      recoveryCode: // value for 'recoveryCode'
 *   },
 * });
 */
function useValidateMfaRecoveryCodeMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ValidateMfaRecoveryCodeDocument, baseOptions);
}
const RevokeActiveSessionsDocument = gql `
  mutation revokeActiveSessions($exceptCurrentOne: Boolean!) {
    revokeActiveSessions(exceptCurrentOne: $exceptCurrentOne) {
      errors {
        ...UserError
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useRevokeActiveSessionsMutation__
 *
 * To run a mutation, you first call `useRevokeActiveSessionsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRevokeActiveSessionsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [revokeActiveSessionsMutation, { data, loading, error }] = useRevokeActiveSessionsMutation({
 *   variables: {
 *      exceptCurrentOne: // value for 'exceptCurrentOne'
 *   },
 * });
 */
function useRevokeActiveSessionsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RevokeActiveSessionsDocument, baseOptions);
}
const RevokeSessionDocument = gql `
  mutation revokeSession($id: UUID!) {
    revokeSession(id: $id) {
      errors {
        ...UserError
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useRevokeSessionMutation__
 *
 * To run a mutation, you first call `useRevokeSessionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRevokeSessionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [revokeSessionMutation, { data, loading, error }] = useRevokeSessionMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
function useRevokeSessionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RevokeSessionDocument, baseOptions);
}
const GetUserCredentialsDocument = gql `
  query getUserCredentials {
    userCredentials @client {
      ...Credentials
    }
  }
  ${CredentialsFragmentDoc}
`;
/**
 * __useGetUserCredentialsQuery__
 *
 * To run a query within a React component, call `useGetUserCredentialsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetUserCredentialsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetUserCredentialsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetUserCredentialsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetUserCredentialsDocument, baseOptions);
}
function useGetUserCredentialsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetUserCredentialsDocument, baseOptions);
}
const SetupMfaDocument = gql `
  mutation setupMfa {
    setupMfa {
      otpAuthUri
    }
  }
`;
/**
 * __useSetupMfaMutation__
 *
 * To run a mutation, you first call `useSetupMfaMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSetupMfaMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [setupMfaMutation, { data, loading, error }] = useSetupMfaMutation({
 *   variables: {
 *   },
 * });
 */
function useSetupMfaMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SetupMfaDocument, baseOptions);
}
const ConfirmMfaSetupDocument = gql `
  mutation confirmMfaSetup($totp: String!) {
    confirmMfaSetup(totp: $totp) {
      recoveryCodes {
        entries
      }
    }
  }
`;
/**
 * __useConfirmMfaSetupMutation__
 *
 * To run a mutation, you first call `useConfirmMfaSetupMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useConfirmMfaSetupMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [confirmMfaSetupMutation, { data, loading, error }] = useConfirmMfaSetupMutation({
 *   variables: {
 *      totp: // value for 'totp'
 *   },
 * });
 */
function useConfirmMfaSetupMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ConfirmMfaSetupDocument, baseOptions);
}
const DisableMfaDocument = gql `
  mutation disableMfa($password: String!) {
    disableMfa(password: $password) {
      user {
        identifier
        mfaState
      }
    }
  }
`;
/**
 * __useDisableMfaMutation__
 *
 * To run a mutation, you first call `useDisableMfaMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDisableMfaMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [disableMfaMutation, { data, loading, error }] = useDisableMfaMutation({
 *   variables: {
 *      password: // value for 'password'
 *   },
 * });
 */
function useDisableMfaMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DisableMfaDocument, baseOptions);
}
const GenerateMfaRecoveryCodesDocument = gql `
  mutation generateMfaRecoveryCodes($password: String!) {
    generateMfaRecoveryCodes(password: $password) {
      recoveryCodes {
        entries
      }
    }
  }
`;
/**
 * __useGenerateMfaRecoveryCodesMutation__
 *
 * To run a mutation, you first call `useGenerateMfaRecoveryCodesMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useGenerateMfaRecoveryCodesMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [generateMfaRecoveryCodesMutation, { data, loading, error }] = useGenerateMfaRecoveryCodesMutation({
 *   variables: {
 *      password: // value for 'password'
 *   },
 * });
 */
function useGenerateMfaRecoveryCodesMutation(baseOptions) {
    return ApolloReactHooks.useMutation(GenerateMfaRecoveryCodesDocument, baseOptions);
}
const SignInAndDisableMfaDocument = gql `
  mutation signInAndDisableMfa(
    $email: String!
    $password: String!
    $disableMfaToken: UUID!
  ) {
    signInAndDisableMfa(
      email: $email
      password: $password
      disableMfaToken: $disableMfaToken
    ) @client {
      credentials {
        ...Credentials
      }
    }
  }
  ${CredentialsFragmentDoc}
`;
/**
 * __useSignInAndDisableMfaMutation__
 *
 * To run a mutation, you first call `useSignInAndDisableMfaMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSignInAndDisableMfaMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [signInAndDisableMfaMutation, { data, loading, error }] = useSignInAndDisableMfaMutation({
 *   variables: {
 *      email: // value for 'email'
 *      password: // value for 'password'
 *      disableMfaToken: // value for 'disableMfaToken'
 *   },
 * });
 */
function useSignInAndDisableMfaMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SignInAndDisableMfaDocument, baseOptions);
}
const GetBrowserSettingsDocument = gql `
  query getBrowserSettings {
    sidebarRightOpen @client
  }
`;
/**
 * __useGetBrowserSettingsQuery__
 *
 * To run a query within a React component, call `useGetBrowserSettingsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetBrowserSettingsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetBrowserSettingsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetBrowserSettingsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetBrowserSettingsDocument, baseOptions);
}
function useGetBrowserSettingsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetBrowserSettingsDocument, baseOptions);
}
const AddSharesToCollectionDocument = gql `
  mutation addSharesToCollection(
    $identifier: UUID!
    $shareIdentifiers: [UUID!]!
  ) {
    addSharesToCollection(
      identifier: $identifier
      shareIdentifiers: $shareIdentifiers
    ) {
      collection {
        ...CollectionPreviews
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
`;
/**
 * __useAddSharesToCollectionMutation__
 *
 * To run a mutation, you first call `useAddSharesToCollectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAddSharesToCollectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [addSharesToCollectionMutation, { data, loading, error }] = useAddSharesToCollectionMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      shareIdentifiers: // value for 'shareIdentifiers'
 *   },
 * });
 */
function useAddSharesToCollectionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AddSharesToCollectionDocument, baseOptions);
}
const CreateCollectionDocument = gql `
  mutation createCollection(
    $projectIdentifier: UUID!
    $name: String!
    $description: String
    $shareIdentifiers: [UUID!]!
    $addAllProjectShares: Boolean
  ) {
    createCollection(
      projectIdentifier: $projectIdentifier
      name: $name
      description: $description
      shareIdentifiers: $shareIdentifiers
      addAllProjectShares: $addAllProjectShares
    ) {
      collection {
        ...CollectionPreviews
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
`;
/**
 * __useCreateCollectionMutation__
 *
 * To run a mutation, you first call `useCreateCollectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateCollectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createCollectionMutation, { data, loading, error }] = useCreateCollectionMutation({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *      name: // value for 'name'
 *      description: // value for 'description'
 *      shareIdentifiers: // value for 'shareIdentifiers'
 *      addAllProjectShares: // value for 'addAllProjectShares'
 *   },
 * });
 */
function useCreateCollectionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateCollectionDocument, baseOptions);
}
const RemoveSharesFromCollectionDocument = gql `
  mutation removeSharesFromCollection(
    $identifier: UUID!
    $shareIdentifiers: [UUID!]!
  ) {
    removeSharesFromCollection(
      identifier: $identifier
      shareIdentifiers: $shareIdentifiers
    ) {
      collection {
        ...CollectionPreviews
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
`;
/**
 * __useRemoveSharesFromCollectionMutation__
 *
 * To run a mutation, you first call `useRemoveSharesFromCollectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRemoveSharesFromCollectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [removeSharesFromCollectionMutation, { data, loading, error }] = useRemoveSharesFromCollectionMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      shareIdentifiers: // value for 'shareIdentifiers'
 *   },
 * });
 */
function useRemoveSharesFromCollectionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RemoveSharesFromCollectionDocument, baseOptions);
}
const RenameCollectionDocument = gql `
  mutation renameCollection($identifier: UUID!, $name: String!) {
    renameCollection(identifier: $identifier, name: $name) {
      collection {
        ...CollectionPreviews
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
`;
/**
 * __useRenameCollectionMutation__
 *
 * To run a mutation, you first call `useRenameCollectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRenameCollectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [renameCollectionMutation, { data, loading, error }] = useRenameCollectionMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      name: // value for 'name'
 *   },
 * });
 */
function useRenameCollectionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RenameCollectionDocument, baseOptions);
}
const EditCollectionDescriptionDocument = gql `
  mutation editCollectionDescription($identifier: UUID!, $description: String) {
    editCollectionDescription(
      identifier: $identifier
      description: $description
    ) {
      collection {
        ...CollectionPreviews
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
`;
/**
 * __useEditCollectionDescriptionMutation__
 *
 * To run a mutation, you first call `useEditCollectionDescriptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEditCollectionDescriptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [editCollectionDescriptionMutation, { data, loading, error }] = useEditCollectionDescriptionMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      description: // value for 'description'
 *   },
 * });
 */
function useEditCollectionDescriptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EditCollectionDescriptionDocument, baseOptions);
}
const DeleteCollectionDocument = gql `
  mutation deleteCollection($identifier: UUID!) {
    deleteCollection(identifier: $identifier) {
      collection {
        identifier
      }
    }
  }
`;
/**
 * __useDeleteCollectionMutation__
 *
 * To run a mutation, you first call `useDeleteCollectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteCollectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteCollectionMutation, { data, loading, error }] = useDeleteCollectionMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useDeleteCollectionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteCollectionDocument, baseOptions);
}
const EditCollectionDocument = gql `
  mutation editCollection(
    $identifier: UUID!
    $name: String!
    $description: String
    $shareIdentifiers: [UUID!]!
    $addAllProjectShares: Boolean
  ) {
    editCollection(
      identifier: $identifier
      name: $name
      description: $description
      shareIdentifiers: $shareIdentifiers
      addAllProjectShares: $addAllProjectShares
    ) {
      collection {
        ...CollectionPreviews
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
`;
/**
 * __useEditCollectionMutation__
 *
 * To run a mutation, you first call `useEditCollectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEditCollectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [editCollectionMutation, { data, loading, error }] = useEditCollectionMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      name: // value for 'name'
 *      description: // value for 'description'
 *      shareIdentifiers: // value for 'shareIdentifiers'
 *      addAllProjectShares: // value for 'addAllProjectShares'
 *   },
 * });
 */
function useEditCollectionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EditCollectionDocument, baseOptions);
}
const MoveAllCollectionSharesDocument = gql `
  mutation moveAllCollectionShares(
    $originIdentifier: UUID!
    $targetIdentifier: UUID!
    $removeCollection: Boolean!
  ) {
    moveAllCollectionShares(
      originIdentifier: $originIdentifier
      targetIdentifier: $targetIdentifier
      removeCollection: $removeCollection
    ) {
      targetIdentifier
      projectIdentifier
    }
  }
`;
/**
 * __useMoveAllCollectionSharesMutation__
 *
 * To run a mutation, you first call `useMoveAllCollectionSharesMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMoveAllCollectionSharesMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [moveAllCollectionSharesMutation, { data, loading, error }] = useMoveAllCollectionSharesMutation({
 *   variables: {
 *      originIdentifier: // value for 'originIdentifier'
 *      targetIdentifier: // value for 'targetIdentifier'
 *      removeCollection: // value for 'removeCollection'
 *   },
 * });
 */
function useMoveAllCollectionSharesMutation(baseOptions) {
    return ApolloReactHooks.useMutation(MoveAllCollectionSharesDocument, baseOptions);
}
const GetCollectionSharesDocument = gql `
  query getCollectionShares(
    $projectIdentifier: UUID!
    $collectionIdentifier: UUID!
    $after: String
    $search: SharesSearchParams
    $sortOrder: ShareSortOrder
  ) {
    project(identifier: $projectIdentifier) {
      ...ProjectInSidebarAndHeader
      collection(identifier: $collectionIdentifier) {
        ...CollectionPreviews
        shares(after: $after, search: $search, sortOrder: $sortOrder) {
          entries {
            ...ShareListItem
          }
          meta {
            ...ShareMeta
          }
        }
      }
    }
  }
  ${ProjectInSidebarAndHeaderFragmentDoc}
  ${CollectionPreviewsFragmentDoc}
  ${ShareListItemFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetCollectionSharesQuery__
 *
 * To run a query within a React component, call `useGetCollectionSharesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCollectionSharesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCollectionSharesQuery({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *      collectionIdentifier: // value for 'collectionIdentifier'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *      sortOrder: // value for 'sortOrder'
 *   },
 * });
 */
function useGetCollectionSharesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetCollectionSharesDocument, baseOptions);
}
function useGetCollectionSharesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetCollectionSharesDocument, baseOptions);
}
const GetCollectionSharesUnpaginatedDocument = gql `
  query getCollectionSharesUnpaginated(
    $projectIdentifier: UUID!
    $collectionIdentifier: UUID!
  ) {
    project(identifier: $projectIdentifier) {
      identifier
      collection(identifier: $collectionIdentifier) {
        identifier
        sharesUnpaginated {
          ...UnpaginatedCollectionShare
        }
      }
    }
  }
  ${UnpaginatedCollectionShareFragmentDoc}
`;
/**
 * __useGetCollectionSharesUnpaginatedQuery__
 *
 * To run a query within a React component, call `useGetCollectionSharesUnpaginatedQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCollectionSharesUnpaginatedQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCollectionSharesUnpaginatedQuery({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *      collectionIdentifier: // value for 'collectionIdentifier'
 *   },
 * });
 */
function useGetCollectionSharesUnpaginatedQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetCollectionSharesUnpaginatedDocument, baseOptions);
}
function useGetCollectionSharesUnpaginatedLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetCollectionSharesUnpaginatedDocument, baseOptions);
}
const GetProjectCollectionsDocument = gql `
  query getProjectCollections($projectIdentifier: UUID!, $after: String) {
    project(identifier: $projectIdentifier) {
      identifier
      collections(after: $after) {
        entries {
          ...CollectionPreviews
        }
        meta {
          ...ShareMeta
        }
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetProjectCollectionsQuery__
 *
 * To run a query within a React component, call `useGetProjectCollectionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectCollectionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectCollectionsQuery({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *      after: // value for 'after'
 *   },
 * });
 */
function useGetProjectCollectionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectCollectionsDocument, baseOptions);
}
function useGetProjectCollectionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectCollectionsDocument, baseOptions);
}
const GetShareCollectionDocument = gql `
  query getShareCollection($shareIdentifier: UUID!) {
    share(id: $shareIdentifier) {
      identifier
      collection {
        ...CollectionForSelect
      }
    }
  }
  ${CollectionForSelectFragmentDoc}
`;
/**
 * __useGetShareCollectionQuery__
 *
 * To run a query within a React component, call `useGetShareCollectionQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareCollectionQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareCollectionQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetShareCollectionQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareCollectionDocument, baseOptions);
}
function useGetShareCollectionLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareCollectionDocument, baseOptions);
}
const GetProjectCollectionsForSelectDocument = gql `
  query getProjectCollectionsForSelect($projectIdentifier: UUID!) {
    project(identifier: $projectIdentifier) {
      identifier
      collectionsUnpaginated {
        identifier
        name
      }
    }
  }
`;
/**
 * __useGetProjectCollectionsForSelectQuery__
 *
 * To run a query within a React component, call `useGetProjectCollectionsForSelectQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectCollectionsForSelectQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectCollectionsForSelectQuery({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useGetProjectCollectionsForSelectQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectCollectionsForSelectDocument, baseOptions);
}
function useGetProjectCollectionsForSelectLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectCollectionsForSelectDocument, baseOptions);
}
const ShareCollectionChangedDocument = gql `
  subscription shareCollectionChanged($projectIdentifier: UUID!) {
    shareCollectionChanged(projectIdentifier: $projectIdentifier) {
      ... on ShareAddedToCollectionEvent {
        share {
          ...ShareListItem
        }
        collection {
          ...CollectionForSelect
        }
        projectIdentifier
      }
      ... on ShareRemovedFromCollectionEvent {
        share {
          ...ShareListItem
        }
        collection {
          ...CollectionForSelect
        }
        destinationProjectIdentifier
      }
    }
  }
  ${ShareListItemFragmentDoc}
  ${CollectionForSelectFragmentDoc}
`;
/**
 * __useShareCollectionChangedSubscription__
 *
 * To run a query within a React component, call `useShareCollectionChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useShareCollectionChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useShareCollectionChangedSubscription({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useShareCollectionChangedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(ShareCollectionChangedDocument, baseOptions);
}
const CollectionDeletedDocument = gql `
  subscription collectionDeleted($projectIdentifier: UUID!) {
    collectionDeleted(projectIdentifier: $projectIdentifier) {
      identifier
    }
  }
`;
/**
 * __useCollectionDeletedSubscription__
 *
 * To run a query within a React component, call `useCollectionDeletedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useCollectionDeletedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCollectionDeletedSubscription({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useCollectionDeletedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(CollectionDeletedDocument, baseOptions);
}
const CollectionChangedDocument = gql `
  subscription collectionChanged($projectIdentifier: UUID!) {
    collectionChanged(projectIdentifier: $projectIdentifier) {
      collection {
        identifier
        name
        description
      }
    }
  }
`;
/**
 * __useCollectionChangedSubscription__
 *
 * To run a query within a React component, call `useCollectionChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useCollectionChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCollectionChangedSubscription({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useCollectionChangedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(CollectionChangedDocument, baseOptions);
}
const CollectionCreatedDocument = gql `
  subscription collectionCreated($projectIdentifier: UUID!) {
    collectionCreated(projectIdentifier: $projectIdentifier) {
      collection {
        ...CollectionPreviews
      }
    }
  }
  ${CollectionPreviewsFragmentDoc}
`;
/**
 * __useCollectionCreatedSubscription__
 *
 * To run a query within a React component, call `useCollectionCreatedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useCollectionCreatedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCollectionCreatedSubscription({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useCollectionCreatedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(CollectionCreatedDocument, baseOptions);
}
const GetCommunityPageItemsDocument = gql `
  query getCommunityPageItems {
    community @client {
      sections {
        ...CommunitySection
      }
    }
  }
  ${CommunitySectionFragmentDoc}
`;
/**
 * __useGetCommunityPageItemsQuery__
 *
 * To run a query within a React component, call `useGetCommunityPageItemsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCommunityPageItemsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCommunityPageItemsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetCommunityPageItemsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetCommunityPageItemsDocument, baseOptions);
}
function useGetCommunityPageItemsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetCommunityPageItemsDocument, baseOptions);
}
const CreateDesignSystemDocument = gql `
  mutation createDesignSystem(
    $workspaceIdentifier: UUID!
    $input: CreateDesignSystemInput!
  ) {
    createDesignSystem(
      workspaceIdentifier: $workspaceIdentifier
      input: $input
    ) {
      identifier
      description
      preview
      header
      shares {
        meta {
          totalCount
        }
        entries {
          name
          identifier
        }
      }
    }
  }
`;
/**
 * __useCreateDesignSystemMutation__
 *
 * To run a mutation, you first call `useCreateDesignSystemMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateDesignSystemMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createDesignSystemMutation, { data, loading, error }] = useCreateDesignSystemMutation({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      input: // value for 'input'
 *   },
 * });
 */
function useCreateDesignSystemMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateDesignSystemDocument, baseOptions);
}
const DeleteDesignSystemDocument = gql `
  mutation deleteDesignSystem($identifier: UUID!) {
    deleteDesignSystem(identifier: $identifier) {
      name
    }
  }
`;
/**
 * __useDeleteDesignSystemMutation__
 *
 * To run a mutation, you first call `useDeleteDesignSystemMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteDesignSystemMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteDesignSystemMutation, { data, loading, error }] = useDeleteDesignSystemMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useDeleteDesignSystemMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteDesignSystemDocument, baseOptions);
}
const UpdateDesignSystemDocument = gql `
  mutation updateDesignSystem(
    $identifier: UUID!
    $input: UpdateDesignSystemInput!
  ) {
    updateDesignSystem(identifier: $identifier, input: $input) {
      identifier
      header
    }
  }
`;
/**
 * __useUpdateDesignSystemMutation__
 *
 * To run a mutation, you first call `useUpdateDesignSystemMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateDesignSystemMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateDesignSystemMutation, { data, loading, error }] = useUpdateDesignSystemMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateDesignSystemMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateDesignSystemDocument, baseOptions);
}
const UpdateDesignSystemContentDocument = gql `
  mutation updateDesignSystemContent(
    $identifier: UUID!
    $input: UpdateDesignSystemInput!
  ) {
    updateDesignSystem(identifier: $identifier, input: $input) {
      identifier
      content
    }
  }
`;
/**
 * __useUpdateDesignSystemContentMutation__
 *
 * To run a mutation, you first call `useUpdateDesignSystemContentMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateDesignSystemContentMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateDesignSystemContentMutation, { data, loading, error }] = useUpdateDesignSystemContentMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateDesignSystemContentMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateDesignSystemContentDocument, baseOptions);
}
const CreateDesignSystemDocsDocument = gql `
  mutation createDesignSystemDocs($designSystemId: UUID!) {
    createDesignSystemDocs(identifier: $designSystemId) {
      identifier
      description
    }
  }
`;
/**
 * __useCreateDesignSystemDocsMutation__
 *
 * To run a mutation, you first call `useCreateDesignSystemDocsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateDesignSystemDocsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createDesignSystemDocsMutation, { data, loading, error }] = useCreateDesignSystemDocsMutation({
 *   variables: {
 *      designSystemId: // value for 'designSystemId'
 *   },
 * });
 */
function useCreateDesignSystemDocsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateDesignSystemDocsDocument, baseOptions);
}
const GetWorkspaceDesignSystemsDocument = gql `
  query getWorkspaceDesignSystems(
    $identifier: UUID!
    $after: String
    $limit: Int = 15
  ) {
    workspace(identifier: $identifier) {
      identifier
      userRole
      designSystems(limit: $limit, after: $after) {
        entries {
          identifier
          preview
          header
          name
          description
          shareCount
          previewShares {
            ...ShareListItem
          }
        }
        meta {
          ...ShareMeta
        }
      }
    }
  }
  ${ShareListItemFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetWorkspaceDesignSystemsQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceDesignSystemsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceDesignSystemsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceDesignSystemsQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetWorkspaceDesignSystemsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceDesignSystemsDocument, baseOptions);
}
function useGetWorkspaceDesignSystemsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceDesignSystemsDocument, baseOptions);
}
const GetDesignSystemDocument = gql `
  query getDesignSystem(
    $identifier: UUID!
    $after: String
    $limit: Int = 10
    $search: String
  ) {
    designSystem(identifier: $identifier) {
      identifier
      name
      description
      content
      createdAt
      updatedAt
      preview
      header
      sections {
        identifier
        name
      }
      shares(after: $after, limit: $limit, search: { name: $search }) {
        entries {
          ...ShareListItem
        }
        meta {
          ...ShareMeta
        }
      }
    }
  }
  ${ShareListItemFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetDesignSystemQuery__
 *
 * To run a query within a React component, call `useGetDesignSystemQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDesignSystemQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDesignSystemQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetDesignSystemQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDesignSystemDocument, baseOptions);
}
function useGetDesignSystemLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDesignSystemDocument, baseOptions);
}
const GetEligibleSharesDocument = gql `
  query getEligibleShares(
    $identifier: UUID!
    $after: String
    $limit: Int = 10
    $search: String
  ) {
    designSystemEligibleShares(
      workspaceIdentifier: $identifier
      limit: $limit
      after: $after
      search: { name: $search }
    ) {
      entries {
        ...SharePublication
      }
      meta {
        ...ShareMeta
      }
    }
  }
  ${SharePublicationFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetEligibleSharesQuery__
 *
 * To run a query within a React component, call `useGetEligibleSharesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetEligibleSharesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetEligibleSharesQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetEligibleSharesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetEligibleSharesDocument, baseOptions);
}
function useGetEligibleSharesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetEligibleSharesDocument, baseOptions);
}
const CanUseDesignSystemManagerDocument = gql `
  query canUseDesignSystemManager($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      features {
        designSystemsEnabled
      }
    }
  }
`;
/**
 * __useCanUseDesignSystemManagerQuery__
 *
 * To run a query within a React component, call `useCanUseDesignSystemManagerQuery` and pass it any options that fit your needs.
 * When your component renders, `useCanUseDesignSystemManagerQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCanUseDesignSystemManagerQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useCanUseDesignSystemManagerQuery(baseOptions) {
    return ApolloReactHooks.useQuery(CanUseDesignSystemManagerDocument, baseOptions);
}
function useCanUseDesignSystemManagerLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(CanUseDesignSystemManagerDocument, baseOptions);
}
const GetDesignSystemSectionsDocument = gql `
  query getDesignSystemSections($identifier: UUID!) {
    designSystem(identifier: $identifier) {
      identifier
      sections {
        identifier
        name
        pages {
          identifier
          name
        }
      }
    }
  }
`;
/**
 * __useGetDesignSystemSectionsQuery__
 *
 * To run a query within a React component, call `useGetDesignSystemSectionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDesignSystemSectionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDesignSystemSectionsQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetDesignSystemSectionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDesignSystemSectionsDocument, baseOptions);
}
function useGetDesignSystemSectionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDesignSystemSectionsDocument, baseOptions);
}
const GetDiscoverDocument = gql `
  query getDiscover {
    discover @client {
      sections @client {
        ...DiscoverSection
      }
    }
  }
  ${DiscoverSectionFragmentDoc}
`;
/**
 * __useGetDiscoverQuery__
 *
 * To run a query within a React component, call `useGetDiscoverQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDiscoverQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDiscoverQuery({
 *   variables: {
 *   },
 * });
 */
function useGetDiscoverQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDiscoverDocument, baseOptions);
}
function useGetDiscoverLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDiscoverDocument, baseOptions);
}
const GetDiscoverHashDocument = gql `
  query getDiscoverHash {
    discover @client {
      contentHash @client
    }
  }
`;
/**
 * __useGetDiscoverHashQuery__
 *
 * To run a query within a React component, call `useGetDiscoverHashQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDiscoverHashQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDiscoverHashQuery({
 *   variables: {
 *   },
 * });
 */
function useGetDiscoverHashQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDiscoverHashDocument, baseOptions);
}
function useGetDiscoverHashLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDiscoverHashDocument, baseOptions);
}
const GetDiscoverResourcesDocument = gql `
  query getDiscoverResources($type: DiscoveryResourceType!) {
    discoverResources(type: $type) @client {
      ...DiscoverSection
    }
  }
  ${DiscoverSectionFragmentDoc}
`;
/**
 * __useGetDiscoverResourcesQuery__
 *
 * To run a query within a React component, call `useGetDiscoverResourcesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDiscoverResourcesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDiscoverResourcesQuery({
 *   variables: {
 *      type: // value for 'type'
 *   },
 * });
 */
function useGetDiscoverResourcesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDiscoverResourcesDocument, baseOptions);
}
function useGetDiscoverResourcesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDiscoverResourcesDocument, baseOptions);
}
const PersonalTokenCreateDocument = gql `
  mutation personalTokenCreate(
    $accessScopes: [PublicApiAccessScope!]!
    $description: String!
    $expiresAt: DateTime
  ) {
    personalTokenCreate(
      accessScopes: $accessScopes
      description: $description
      expiresAt: $expiresAt
    ) {
      secret
      personalToken {
        ...PersonalToken
      }
    }
  }
  ${PersonalTokenFragmentDoc}
`;
/**
 * __usePersonalTokenCreateMutation__
 *
 * To run a mutation, you first call `usePersonalTokenCreateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `usePersonalTokenCreateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [personalTokenCreateMutation, { data, loading, error }] = usePersonalTokenCreateMutation({
 *   variables: {
 *      accessScopes: // value for 'accessScopes'
 *      description: // value for 'description'
 *      expiresAt: // value for 'expiresAt'
 *   },
 * });
 */
function usePersonalTokenCreateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(PersonalTokenCreateDocument, baseOptions);
}
const PersonalTokenDeleteDocument = gql `
  mutation personalTokenDelete($identifier: UUID!) {
    personalTokenDelete(identifier: $identifier) {
      identifier
    }
  }
`;
/**
 * __usePersonalTokenDeleteMutation__
 *
 * To run a mutation, you first call `usePersonalTokenDeleteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `usePersonalTokenDeleteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [personalTokenDeleteMutation, { data, loading, error }] = usePersonalTokenDeleteMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function usePersonalTokenDeleteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(PersonalTokenDeleteDocument, baseOptions);
}
const GetPersonalTokensDocument = gql `
  query getPersonalTokens {
    me {
      identifier
      personalTokens {
        ...PersonalToken
      }
    }
  }
  ${PersonalTokenFragmentDoc}
`;
/**
 * __useGetPersonalTokensQuery__
 *
 * To run a query within a React component, call `useGetPersonalTokensQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPersonalTokensQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPersonalTokensQuery({
 *   variables: {
 *   },
 * });
 */
function useGetPersonalTokensQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPersonalTokensDocument, baseOptions);
}
function useGetPersonalTokensLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPersonalTokensDocument, baseOptions);
}
const RenameProjectDocument = gql `
  mutation renameProject($projectId: ShortId!, $name: String!) {
    renameProject(name: $name, projectId: $projectId) {
      successful
      errors {
        ...UserError
      }
      project {
        ...Project
      }
    }
  }
  ${UserErrorFragmentDoc}
  ${ProjectFragmentDoc}
`;
/**
 * __useRenameProjectMutation__
 *
 * To run a mutation, you first call `useRenameProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRenameProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [renameProjectMutation, { data, loading, error }] = useRenameProjectMutation({
 *   variables: {
 *      projectId: // value for 'projectId'
 *      name: // value for 'name'
 *   },
 * });
 */
function useRenameProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RenameProjectDocument, baseOptions);
}
const DeleteProjectDocument = gql `
  mutation deleteProject($projectId: ShortId!) {
    deleteProject(projectId: $projectId) {
      successful
      errors {
        ...UserError
      }
      project {
        identifier
        deletedAt
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useDeleteProjectMutation__
 *
 * To run a mutation, you first call `useDeleteProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteProjectMutation, { data, loading, error }] = useDeleteProjectMutation({
 *   variables: {
 *      projectId: // value for 'projectId'
 *   },
 * });
 */
function useDeleteProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteProjectDocument, baseOptions);
}
const RemoveShareFromProjectDocument = gql `
  mutation removeShareFromProject($shareId: ShortId!, $projectId: ShortId!) {
    removeShareFromProject(shareId: $shareId, projectId: $projectId) {
      errors {
        ...UserError
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useRemoveShareFromProjectMutation__
 *
 * To run a mutation, you first call `useRemoveShareFromProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRemoveShareFromProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [removeShareFromProjectMutation, { data, loading, error }] = useRemoveShareFromProjectMutation({
 *   variables: {
 *      shareId: // value for 'shareId'
 *      projectId: // value for 'projectId'
 *   },
 * });
 */
function useRemoveShareFromProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RemoveShareFromProjectDocument, baseOptions);
}
const MoveShareToProjectDocument = gql `
  mutation moveShareToProject($projectId: UUID!, $shareId: UUID!) {
    moveShareToProject(
      projectIdentifier: $projectId
      shareIdentifier: $shareId
    ) {
      errors {
        ...UserError
      }
      successful
      project {
        ...Project
      }
      share {
        ...ShareListItem
        isDraft
      }
    }
  }
  ${UserErrorFragmentDoc}
  ${ProjectFragmentDoc}
  ${ShareListItemFragmentDoc}
`;
/**
 * __useMoveShareToProjectMutation__
 *
 * To run a mutation, you first call `useMoveShareToProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMoveShareToProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [moveShareToProjectMutation, { data, loading, error }] = useMoveShareToProjectMutation({
 *   variables: {
 *      projectId: // value for 'projectId'
 *      shareId: // value for 'shareId'
 *   },
 * });
 */
function useMoveShareToProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(MoveShareToProjectDocument, baseOptions);
}
const RestoreProjectDocument = gql `
  mutation restoreProject($projectIdentifier: UUID!) {
    restoreProject(identifier: $projectIdentifier) {
      project {
        identifier
        deletedAt
      }
    }
  }
`;
/**
 * __useRestoreProjectMutation__
 *
 * To run a mutation, you first call `useRestoreProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRestoreProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [restoreProjectMutation, { data, loading, error }] = useRestoreProjectMutation({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useRestoreProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RestoreProjectDocument, baseOptions);
}
const DeleteProjectPermanentlyDocument = gql `
  mutation deleteProjectPermanently($projectIdentifier: UUID!) {
    deleteProjectPermanently(identifier: $projectIdentifier) {
      projectIdentifier
    }
  }
`;
/**
 * __useDeleteProjectPermanentlyMutation__
 *
 * To run a mutation, you first call `useDeleteProjectPermanentlyMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteProjectPermanentlyMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteProjectPermanentlyMutation, { data, loading, error }] = useDeleteProjectPermanentlyMutation({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useDeleteProjectPermanentlyMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteProjectPermanentlyDocument, baseOptions);
}
const RequestAccessToProjectDocument = gql `
  mutation requestAccessToProject(
    $projectShortId: ShortId
    $projectIdentifier: UUID
  ) {
    requestAccessToProject(
      projectShortId: $projectShortId
      projectIdentifier: $projectIdentifier
    ) {
      __typename
    }
  }
`;
/**
 * __useRequestAccessToProjectMutation__
 *
 * To run a mutation, you first call `useRequestAccessToProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRequestAccessToProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [requestAccessToProjectMutation, { data, loading, error }] = useRequestAccessToProjectMutation({
 *   variables: {
 *      projectShortId: // value for 'projectShortId'
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useRequestAccessToProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RequestAccessToProjectDocument, baseOptions);
}
const UpdateProjectPermissionDocument = gql `
  mutation updateProjectPermission(
    $projectIdentifier: UUID!
    $workspaceAccessLevel: ProjectWorkspaceAccessLevel!
  ) {
    updateProjectPermission(
      projectIdentifier: $projectIdentifier
      workspaceAccessLevel: $workspaceAccessLevel
    ) {
      project {
        ...Project
      }
    }
  }
  ${ProjectFragmentDoc}
`;
/**
 * __useUpdateProjectPermissionMutation__
 *
 * To run a mutation, you first call `useUpdateProjectPermissionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateProjectPermissionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateProjectPermissionMutation, { data, loading, error }] = useUpdateProjectPermissionMutation({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *      workspaceAccessLevel: // value for 'workspaceAccessLevel'
 *   },
 * });
 */
function useUpdateProjectPermissionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateProjectPermissionDocument, baseOptions);
}
const CreateProjectDocument = gql `
  mutation createProject(
    $workspaceId: UUID!
    $name: String!
    $workspaceAccessLevel: ProjectWorkspaceAccessLevel
    $workspaceMembershipIdentifiers: [UUID!]
    $guestsToInvite: [CreateWorkspaceProjectGuestToInvite!]
  ) {
    createWorkspaceProject(
      workspaceIdentifier: $workspaceId
      name: $name
      workspaceAccessLevel: $workspaceAccessLevel
      workspaceMembershipIdentifiers: $workspaceMembershipIdentifiers
      guestsToInvite: $guestsToInvite
    ) {
      project {
        ...Project
      }
    }
  }
  ${ProjectFragmentDoc}
`;
/**
 * __useCreateProjectMutation__
 *
 * To run a mutation, you first call `useCreateProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createProjectMutation, { data, loading, error }] = useCreateProjectMutation({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *      name: // value for 'name'
 *      workspaceAccessLevel: // value for 'workspaceAccessLevel'
 *      workspaceMembershipIdentifiers: // value for 'workspaceMembershipIdentifiers'
 *      guestsToInvite: // value for 'guestsToInvite'
 *   },
 * });
 */
function useCreateProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateProjectDocument, baseOptions);
}
const RemoveProjectMemberDocument = gql `
  mutation removeProjectMember(
    $membershipIdentifier: UUID!
    $confirmed: Boolean! = false
    $removeShareMemberships: Boolean
  ) {
    removeProjectMember(
      membershipIdentifier: $membershipIdentifier
      confirmed: $confirmed
      removeShareMemberships: $removeShareMemberships
    ) {
      project {
        identifier
        privacyIcon
      }
    }
  }
`;
/**
 * __useRemoveProjectMemberMutation__
 *
 * To run a mutation, you first call `useRemoveProjectMemberMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRemoveProjectMemberMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [removeProjectMemberMutation, { data, loading, error }] = useRemoveProjectMemberMutation({
 *   variables: {
 *      membershipIdentifier: // value for 'membershipIdentifier'
 *      confirmed: // value for 'confirmed'
 *      removeShareMemberships: // value for 'removeShareMemberships'
 *   },
 * });
 */
function useRemoveProjectMemberMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RemoveProjectMemberDocument, baseOptions);
}
const LeaveProjectDocument = gql `
  mutation leaveProject($projectIdentifier: UUID!) {
    leaveProject(projectIdentifier: $projectIdentifier) {
      project {
        identifier
      }
    }
  }
`;
/**
 * __useLeaveProjectMutation__
 *
 * To run a mutation, you first call `useLeaveProjectMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useLeaveProjectMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [leaveProjectMutation, { data, loading, error }] = useLeaveProjectMutation({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useLeaveProjectMutation(baseOptions) {
    return ApolloReactHooks.useMutation(LeaveProjectDocument, baseOptions);
}
const GetProjectsDocument = gql `
  query getProjects($workspaceId: UUID!, $after: String) {
    workspace(identifier: $workspaceId) {
      identifier
      projects(after: $after, limit: 200, type: STANDARD) {
        entries {
          ...ProjectInSidebarAndHeader
        }
        meta {
          after
          before
          limit
          totalCount
        }
      }
      draftsProject: projects(limit: 1, type: PERSONAL_DRAFTS) {
        entries {
          ...Project
        }
      }
    }
  }
  ${ProjectInSidebarAndHeaderFragmentDoc}
  ${ProjectFragmentDoc}
`;
/**
 * __useGetProjectsQuery__
 *
 * To run a query within a React component, call `useGetProjectsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectsQuery({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *      after: // value for 'after'
 *   },
 * });
 */
function useGetProjectsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectsDocument, baseOptions);
}
function useGetProjectsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectsDocument, baseOptions);
}
const GetShowDocumentsInProjectsDocument = gql `
  query getShowDocumentsInProjects {
    showDocumentsInProjects @client
  }
`;
/**
 * __useGetShowDocumentsInProjectsQuery__
 *
 * To run a query within a React component, call `useGetShowDocumentsInProjectsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShowDocumentsInProjectsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShowDocumentsInProjectsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetShowDocumentsInProjectsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShowDocumentsInProjectsDocument, baseOptions);
}
function useGetShowDocumentsInProjectsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShowDocumentsInProjectsDocument, baseOptions);
}
const ProjectChangedDocument = gql `
  subscription projectChanged {
    projectChanged {
      __typename
      workspaceIdentifier
      ... on ProjectCreatedEvent {
        project {
          ...Project
        }
      }
      ... on ProjectRestoredEvent {
        project {
          ...Project
        }
      }
      ... on ProjectChangedEvent {
        project {
          ...Project
        }
      }
      ... on ProjectDeletedEvent {
        project {
          ...Project
        }
      }
      ... on ProjectPermanentlyDeletedEvent {
        projectIdentifier
      }
      ... on ProjectWorkspaceAccessLevelChangedEvent {
        workspaceIdentifier
        project {
          identifier
          workspaceAccessLevel
          userMembership {
            identifier
          }
          privacyIcon
        }
      }
    }
  }
  ${ProjectFragmentDoc}
`;
/**
 * __useProjectChangedSubscription__
 *
 * To run a query within a React component, call `useProjectChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useProjectChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProjectChangedSubscription({
 *   variables: {
 *   },
 * });
 */
function useProjectChangedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(ProjectChangedDocument, baseOptions);
}
const AddProjectMemberDocument = gql `
  mutation addProjectMember(
    $projectIdentifier: UUID!
    $workspaceMembershipIdentifier: UUID
    $email: String
    $guestCommentsEnabled: Boolean = true
    $guestInspectEnabled: Boolean = true
  ) {
    addProjectMember(
      projectIdentifier: $projectIdentifier
      workspaceMembershipIdentifier: $workspaceMembershipIdentifier
      email: $email
      guestCommentsEnabled: $guestCommentsEnabled
      guestInspectEnabled: $guestInspectEnabled
    ) {
      project {
        identifier
        privacyIcon
      }
      projectMembership {
        ...ProjectMembership
      }
    }
  }
  ${ProjectMembershipFragmentDoc}
`;
/**
 * __useAddProjectMemberMutation__
 *
 * To run a mutation, you first call `useAddProjectMemberMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAddProjectMemberMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [addProjectMemberMutation, { data, loading, error }] = useAddProjectMemberMutation({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *      workspaceMembershipIdentifier: // value for 'workspaceMembershipIdentifier'
 *      email: // value for 'email'
 *      guestCommentsEnabled: // value for 'guestCommentsEnabled'
 *      guestInspectEnabled: // value for 'guestInspectEnabled'
 *   },
 * });
 */
function useAddProjectMemberMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AddProjectMemberDocument, baseOptions);
}
const AcceptProjectAccessRequestDocument = gql `
  mutation acceptProjectAccessRequest($membershipIdentifier: UUID!) {
    acceptProjectAccessRequest(membershipIdentifier: $membershipIdentifier) {
      project {
        identifier
      }
      projectMembership {
        ...ProjectMembership
        workspaceMembership {
          identifier
          workspace {
            identifier
          }
        }
      }
    }
  }
  ${ProjectMembershipFragmentDoc}
`;
/**
 * __useAcceptProjectAccessRequestMutation__
 *
 * To run a mutation, you first call `useAcceptProjectAccessRequestMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptProjectAccessRequestMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptProjectAccessRequestMutation, { data, loading, error }] = useAcceptProjectAccessRequestMutation({
 *   variables: {
 *      membershipIdentifier: // value for 'membershipIdentifier'
 *   },
 * });
 */
function useAcceptProjectAccessRequestMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AcceptProjectAccessRequestDocument, baseOptions);
}
const UpdateProjectMembershipDocument = gql `
  mutation updateProjectMembership(
    $membershipIdentifier: UUID!
    $guestCommentsEnabled: Boolean
    $guestInspectEnabled: Boolean
  ) {
    updateProjectMembership(
      identifier: $membershipIdentifier
      guestCommentsEnabled: $guestCommentsEnabled
      guestInspectEnabled: $guestInspectEnabled
    ) {
      projectMembership {
        ...ProjectMembership
      }
    }
  }
  ${ProjectMembershipFragmentDoc}
`;
/**
 * __useUpdateProjectMembershipMutation__
 *
 * To run a mutation, you first call `useUpdateProjectMembershipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateProjectMembershipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateProjectMembershipMutation, { data, loading, error }] = useUpdateProjectMembershipMutation({
 *   variables: {
 *      membershipIdentifier: // value for 'membershipIdentifier'
 *      guestCommentsEnabled: // value for 'guestCommentsEnabled'
 *      guestInspectEnabled: // value for 'guestInspectEnabled'
 *   },
 * });
 */
function useUpdateProjectMembershipMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateProjectMembershipDocument, baseOptions);
}
const AcceptProjectMembershipInvitationDocument = gql `
  mutation acceptProjectMembershipInvitation(
    $input: AcceptProjectMembershipInvitationParams!
  ) {
    acceptProjectMembershipInvitation(input: $input) {
      projectMembership {
        identifier
        inviteStatus
        project {
          identifier
        }
      }
    }
  }
`;
/**
 * __useAcceptProjectMembershipInvitationMutation__
 *
 * To run a mutation, you first call `useAcceptProjectMembershipInvitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptProjectMembershipInvitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptProjectMembershipInvitationMutation, { data, loading, error }] = useAcceptProjectMembershipInvitationMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useAcceptProjectMembershipInvitationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AcceptProjectMembershipInvitationDocument, baseOptions);
}
const GetProjectMembershipsDocument = gql `
  query getProjectMemberships($projectIdentifier: UUID!, $after: String) {
    project(identifier: $projectIdentifier) {
      identifier
      memberships(after: $after) {
        entries {
          ...ProjectMembership
        }
        meta {
          after
          limit
          totalCount
        }
      }
    }
  }
  ${ProjectMembershipFragmentDoc}
`;
/**
 * __useGetProjectMembershipsQuery__
 *
 * To run a query within a React component, call `useGetProjectMembershipsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectMembershipsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectMembershipsQuery({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *      after: // value for 'after'
 *   },
 * });
 */
function useGetProjectMembershipsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectMembershipsDocument, baseOptions);
}
function useGetProjectMembershipsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectMembershipsDocument, baseOptions);
}
const GetProjectMembershipsCountDocument = gql `
  query getProjectMembershipsCount($projectIdentifier: UUID!) {
    project(identifier: $projectIdentifier) {
      identifier
      membershipCount {
        admin
        member
        guest
      }
    }
  }
`;
/**
 * __useGetProjectMembershipsCountQuery__
 *
 * To run a query within a React component, call `useGetProjectMembershipsCountQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectMembershipsCountQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectMembershipsCountQuery({
 *   variables: {
 *      projectIdentifier: // value for 'projectIdentifier'
 *   },
 * });
 */
function useGetProjectMembershipsCountQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectMembershipsCountDocument, baseOptions);
}
function useGetProjectMembershipsCountLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectMembershipsCountDocument, baseOptions);
}
const GetProjectMembershipByTokenDocument = gql `
  query getProjectMembershipByToken($token: String!) {
    projectMembership(token: $token) {
      identifier
      email
      inviteStatus
      project {
        identifier
        name
        workspace {
          identifier
        }
      }
      inviter {
        name
        avatar {
          large
        }
      }
    }
  }
`;
/**
 * __useGetProjectMembershipByTokenQuery__
 *
 * To run a query within a React component, call `useGetProjectMembershipByTokenQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectMembershipByTokenQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectMembershipByTokenQuery({
 *   variables: {
 *      token: // value for 'token'
 *   },
 * });
 */
function useGetProjectMembershipByTokenQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectMembershipByTokenDocument, baseOptions);
}
function useGetProjectMembershipByTokenLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectMembershipByTokenDocument, baseOptions);
}
const ProjectAccessChangedDocument = gql `
  subscription projectAccessChanged {
    projectAccessChanged {
      workspaceIdentifier
      ... on ProjectAccessRevokedEvent {
        project {
          __typename
          identifier
          name
        }
      }
    }
  }
`;
/**
 * __useProjectAccessChangedSubscription__
 *
 * To run a query within a React component, call `useProjectAccessChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useProjectAccessChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProjectAccessChangedSubscription({
 *   variables: {
 *   },
 * });
 */
function useProjectAccessChangedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(ProjectAccessChangedDocument, baseOptions);
}
const ProjectMembershipChangedDocument = gql `
  subscription projectMembershipChanged {
    projectChanged {
      __typename
      workspaceIdentifier
      ... on ProjectMemberAddedEvent {
        membership {
          ...ProjectMembership
        }
        project {
          identifier
          privacyIcon
        }
      }
      ... on ProjectMemberChangedEvent {
        membership {
          ...ProjectMembership
        }
        project {
          identifier
        }
      }
      ... on ProjectMemberRemovedEvent {
        membershipIdentifier
        project {
          identifier
          privacyIcon
        }
      }
    }
  }
  ${ProjectMembershipFragmentDoc}
`;
/**
 * __useProjectMembershipChangedSubscription__
 *
 * To run a query within a React component, call `useProjectMembershipChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useProjectMembershipChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProjectMembershipChangedSubscription({
 *   variables: {
 *   },
 * });
 */
function useProjectMembershipChangedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(ProjectMembershipChangedDocument, baseOptions);
}
const GetPrototypeFileForDocumentDocument = gql `
  query getPrototypeFileForDocument(
    $identifier: UUID!
    $versionShortId: ShortId
  ) {
    share(id: $identifier) {
      identifier
      version(shortId: $versionShortId) {
        identifier
        shortId
        document {
          identifier
          ...PrototypeFile
        }
      }
    }
  }
  ${PrototypeFileFragmentDoc}
`;
/**
 * __useGetPrototypeFileForDocumentQuery__
 *
 * To run a query within a React component, call `useGetPrototypeFileForDocumentQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPrototypeFileForDocumentQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPrototypeFileForDocumentQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      versionShortId: // value for 'versionShortId'
 *   },
 * });
 */
function useGetPrototypeFileForDocumentQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPrototypeFileForDocumentDocument, baseOptions);
}
function useGetPrototypeFileForDocumentLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPrototypeFileForDocumentDocument, baseOptions);
}
const PrototypeFileDocument = gql `
  subscription prototypeFile($documentId: String!) {
    prototypeFile(documentId: $documentId) {
      url
    }
  }
`;
/**
 * __usePrototypeFileSubscription__
 *
 * To run a query within a React component, call `usePrototypeFileSubscription` and pass it any options that fit your needs.
 * When your component renders, `usePrototypeFileSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePrototypeFileSubscription({
 *   variables: {
 *      documentId: // value for 'documentId'
 *   },
 * });
 */
function usePrototypeFileSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(PrototypeFileDocument, baseOptions);
}
const AddReactionToPublicationDocument = gql `
  mutation addReactionToPublication(
    $publicationIdentifier: UUID!
    $reaction: PublicationReactionType!
  ) {
    addReactionToPublication(
      publicationIdentifier: $publicationIdentifier
      reaction: $reaction
    ) {
      publication {
        ...PublicationReactions
      }
    }
  }
  ${PublicationReactionsFragmentDoc}
`;
/**
 * __useAddReactionToPublicationMutation__
 *
 * To run a mutation, you first call `useAddReactionToPublicationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAddReactionToPublicationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [addReactionToPublicationMutation, { data, loading, error }] = useAddReactionToPublicationMutation({
 *   variables: {
 *      publicationIdentifier: // value for 'publicationIdentifier'
 *      reaction: // value for 'reaction'
 *   },
 * });
 */
function useAddReactionToPublicationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AddReactionToPublicationDocument, baseOptions);
}
const GetPublicationDocument = gql `
  query getPublication($identifier: UUID!) {
    publication(identifier: $identifier) {
      ...Publication
    }
  }
  ${PublicationFragmentDoc}
`;
/**
 * __useGetPublicationQuery__
 *
 * To run a query within a React component, call `useGetPublicationQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPublicationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPublicationQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetPublicationQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPublicationDocument, baseOptions);
}
function useGetPublicationLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPublicationDocument, baseOptions);
}
const GetSharePagesDocument = gql `
  query getSharePages($identifier: UUID!, $after: String, $limit: Int) {
    share(id: $identifier) {
      identifier
      version(preferredState: PROCESSED_AND_PUBLISHED) {
        identifier
        shortId
        document {
          identifier
          pages(after: $after, limit: $limit) {
            entries {
              ...PublicationPage
            }
            meta {
              after
              totalCount
            }
          }
        }
      }
    }
  }
  ${PublicationPageFragmentDoc}
`;
/**
 * __useGetSharePagesQuery__
 *
 * To run a query within a React component, call `useGetSharePagesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSharePagesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSharePagesQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetSharePagesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSharePagesDocument, baseOptions);
}
function useGetSharePagesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSharePagesDocument, baseOptions);
}
const GetPublicationsDocument = gql `
  query getPublications(
    $after: String
    $exclude: [UUID!]
    $include: [UUID!]
    $limit: Int
    $sort: PublicationSortOrder
    $search: PublicationsSearchParams
  ) {
    publications(
      after: $after
      limit: $limit
      sortOrder: $sort
      include: $include
      exclude: $exclude
      search: $search
    ) {
      entries {
        ...PublicationItem
      }
      meta {
        after
        totalCount
      }
    }
  }
  ${PublicationItemFragmentDoc}
`;
/**
 * __useGetPublicationsQuery__
 *
 * To run a query within a React component, call `useGetPublicationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPublicationsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPublicationsQuery({
 *   variables: {
 *      after: // value for 'after'
 *      exclude: // value for 'exclude'
 *      include: // value for 'include'
 *      limit: // value for 'limit'
 *      sort: // value for 'sort'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetPublicationsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPublicationsDocument, baseOptions);
}
function useGetPublicationsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPublicationsDocument, baseOptions);
}
const PublicationReactionsUpdatedDocument = gql `
  subscription publicationReactionsUpdated($publicationIdentifier: UUID!) {
    publicationReactionsUpdated(publicationIdentifier: $publicationIdentifier) {
      publication {
        ...PublicationReactions
      }
    }
  }
  ${PublicationReactionsFragmentDoc}
`;
/**
 * __usePublicationReactionsUpdatedSubscription__
 *
 * To run a query within a React component, call `usePublicationReactionsUpdatedSubscription` and pass it any options that fit your needs.
 * When your component renders, `usePublicationReactionsUpdatedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePublicationReactionsUpdatedSubscription({
 *   variables: {
 *      publicationIdentifier: // value for 'publicationIdentifier'
 *   },
 * });
 */
function usePublicationReactionsUpdatedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(PublicationReactionsUpdatedDocument, baseOptions);
}
const GetPublicationListingDocument = gql `
  query getPublicationListing($identifier: String!) {
    publicationListing(identifier: $identifier) @client {
      ...PublicationListing
    }
  }
  ${PublicationListingFragmentDoc}
`;
/**
 * __useGetPublicationListingQuery__
 *
 * To run a query within a React component, call `useGetPublicationListingQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPublicationListingQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPublicationListingQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetPublicationListingQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPublicationListingDocument, baseOptions);
}
function useGetPublicationListingLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPublicationListingDocument, baseOptions);
}
const GetDetailViewArtboardDocument = gql `
  query getDetailViewArtboard(
    $permanentArtboardShortId: ShortId!
    $documentVersionShortId: ShortId
    $shareIdentifier: UUID!
  ) {
    artboard(
      permanentArtboardShortId: $permanentArtboardShortId
      documentVersionShortId: $documentVersionShortId
      shareIdentifier: $shareIdentifier
    ) {
      ...ArtboardDetailInfo
    }
  }
  ${ArtboardDetailInfoFragmentDoc}
`;
/**
 * __useGetDetailViewArtboardQuery__
 *
 * To run a query within a React component, call `useGetDetailViewArtboardQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDetailViewArtboardQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDetailViewArtboardQuery({
 *   variables: {
 *      permanentArtboardShortId: // value for 'permanentArtboardShortId'
 *      documentVersionShortId: // value for 'documentVersionShortId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetDetailViewArtboardQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDetailViewArtboardDocument, baseOptions);
}
function useGetDetailViewArtboardLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDetailViewArtboardDocument, baseOptions);
}
const GetPermanentArtboardShortIdDocument = gql `
  query getPermanentArtboardShortId(
    $shortId: ShortId!
    $shareIdentifier: UUID!
  ) {
    artboard(
      shortId: $shortId
      fetchLatestVersion: true
      shareIdentifier: $shareIdentifier
    ) {
      identifier
      shortId
      permanentArtboardShortId
      documentVersionShortId
    }
  }
`;
/**
 * __useGetPermanentArtboardShortIdQuery__
 *
 * To run a query within a React component, call `useGetPermanentArtboardShortIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPermanentArtboardShortIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPermanentArtboardShortIdQuery({
 *   variables: {
 *      shortId: // value for 'shortId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetPermanentArtboardShortIdQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPermanentArtboardShortIdDocument, baseOptions);
}
function useGetPermanentArtboardShortIdLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPermanentArtboardShortIdDocument, baseOptions);
}
const GetArtboardBySlugDocument = gql `
  query getArtboardBySlug(
    $shareIdentifier: UUID!
    $pageSlug: String!
    $artboardSlug: String!
  ) {
    share(id: $shareIdentifier) {
      identifier
      version(preferredState: PROCESSED) {
        identifier
        shortId
        document {
          identifier
          artboardCount
          page(slug: $pageSlug) {
            artboard(slug: $artboardSlug) {
              ...ArtboardDetailInfo
            }
          }
        }
      }
    }
  }
  ${ArtboardDetailInfoFragmentDoc}
`;
/**
 * __useGetArtboardBySlugQuery__
 *
 * To run a query within a React component, call `useGetArtboardBySlugQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetArtboardBySlugQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetArtboardBySlugQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      pageSlug: // value for 'pageSlug'
 *      artboardSlug: // value for 'artboardSlug'
 *   },
 * });
 */
function useGetArtboardBySlugQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetArtboardBySlugDocument, baseOptions);
}
function useGetArtboardBySlugLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetArtboardBySlugDocument, baseOptions);
}
const GetSurroundingArtboardsDocument = gql `
  query getSurroundingArtboards(
    $permanentArtboardShortId: ShortId!
    $documentVersionShortId: ShortId!
    $shareIdentifier: UUID!
  ) {
    artboard(
      permanentArtboardShortId: $permanentArtboardShortId
      documentVersionShortId: $documentVersionShortId
      shareIdentifier: $shareIdentifier
    ) {
      shortId
      documentOrder
      permanentArtboardShortId
      documentVersionShortId
      nextArtboard {
        ...ArtboardDetailInfo
      }
      previousArtboard {
        ...ArtboardDetailInfo
      }
    }
  }
  ${ArtboardDetailInfoFragmentDoc}
`;
/**
 * __useGetSurroundingArtboardsQuery__
 *
 * To run a query within a React component, call `useGetSurroundingArtboardsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSurroundingArtboardsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSurroundingArtboardsQuery({
 *   variables: {
 *      permanentArtboardShortId: // value for 'permanentArtboardShortId'
 *      documentVersionShortId: // value for 'documentVersionShortId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetSurroundingArtboardsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSurroundingArtboardsDocument, baseOptions);
}
function useGetSurroundingArtboardsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSurroundingArtboardsDocument, baseOptions);
}
const GetArtboardsForDocumentDocument = gql `
  query getArtboardsForDocument(
    $identifier: UUID!
    $versionShortId: ShortId
    $after: String
    $filter: ArtboardFilterType = ALL
    $search: String
  ) {
    share(id: $identifier) {
      identifier
      commentsEnabled
      version(shortId: $versionShortId) {
        identifier
        shortId
        document {
          identifier
          artboards(after: $after, filter: $filter, search: $search) {
            entries {
              ...ArtboardDetailInfo
            }
            meta {
              after
              totalCount
            }
          }
        }
      }
    }
  }
  ${ArtboardDetailInfoFragmentDoc}
`;
/**
 * __useGetArtboardsForDocumentQuery__
 *
 * To run a query within a React component, call `useGetArtboardsForDocumentQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetArtboardsForDocumentQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetArtboardsForDocumentQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      versionShortId: // value for 'versionShortId'
 *      after: // value for 'after'
 *      filter: // value for 'filter'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetArtboardsForDocumentQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetArtboardsForDocumentDocument, baseOptions);
}
function useGetArtboardsForDocumentLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetArtboardsForDocumentDocument, baseOptions);
}
const GetArtboardsForPageDocument = gql `
  query getArtboardsForPage(
    $identifier: UUID!
    $versionShortId: ShortId
    $pageUUID: String
    $after: String
    $search: String
  ) {
    share(id: $identifier) {
      identifier
      commentsEnabled
      version(shortId: $versionShortId) {
        identifier
        shortId
        document {
          identifier
          artboardCount
          page(uuid: $pageUUID) {
            identifier
            artboards(after: $after, search: $search) {
              entries {
                ...ArtboardDetailInfo
              }
              meta {
                after
              }
            }
          }
        }
      }
    }
  }
  ${ArtboardDetailInfoFragmentDoc}
`;
/**
 * __useGetArtboardsForPageQuery__
 *
 * To run a query within a React component, call `useGetArtboardsForPageQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetArtboardsForPageQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetArtboardsForPageQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      versionShortId: // value for 'versionShortId'
 *      pageUUID: // value for 'pageUUID'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetArtboardsForPageQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetArtboardsForPageDocument, baseOptions);
}
function useGetArtboardsForPageLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetArtboardsForPageDocument, baseOptions);
}
const GetArtboardInDocumentWithUuidDocument = gql `
  query getArtboardInDocumentWithUuid(
    $documentId: String!
    $artboardUuid: String!
  ) {
    artboardInDocumentWithUuid(documentId: $documentId, uuid: $artboardUuid) {
      permanentArtboardShortId
      isSymbolMaster
    }
  }
`;
/**
 * __useGetArtboardInDocumentWithUuidQuery__
 *
 * To run a query within a React component, call `useGetArtboardInDocumentWithUuidQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetArtboardInDocumentWithUuidQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetArtboardInDocumentWithUuidQuery({
 *   variables: {
 *      documentId: // value for 'documentId'
 *      artboardUuid: // value for 'artboardUuid'
 *   },
 * });
 */
function useGetArtboardInDocumentWithUuidQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetArtboardInDocumentWithUuidDocument, baseOptions);
}
function useGetArtboardInDocumentWithUuidLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetArtboardInDocumentWithUuidDocument, baseOptions);
}
const GetArtboardPrototypeInfoDocument = gql `
  query getArtboardPrototypeInfo($documentId: String!, $artboardUuid: String!) {
    artboardInDocumentWithUuid(documentId: $documentId, uuid: $artboardUuid) {
      isFlowHome
    }
  }
`;
/**
 * __useGetArtboardPrototypeInfoQuery__
 *
 * To run a query within a React component, call `useGetArtboardPrototypeInfoQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetArtboardPrototypeInfoQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetArtboardPrototypeInfoQuery({
 *   variables: {
 *      documentId: // value for 'documentId'
 *      artboardUuid: // value for 'artboardUuid'
 *   },
 * });
 */
function useGetArtboardPrototypeInfoQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetArtboardPrototypeInfoDocument, baseOptions);
}
function useGetArtboardPrototypeInfoLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetArtboardPrototypeInfoDocument, baseOptions);
}
const GetArtboardByPermanentArtboardShortIdDocument = gql `
  query getArtboardByPermanentArtboardShortId(
    $permanentArtboardShortId: ShortId!
    $shareIdentifier: UUID!
    $documentVersionShortId: ShortId
  ) {
    artboard(
      permanentArtboardShortId: $permanentArtboardShortId
      shareIdentifier: $shareIdentifier
      documentVersionShortId: $documentVersionShortId
    ) {
      uuid
      isFlowHome
    }
  }
`;
/**
 * __useGetArtboardByPermanentArtboardShortIdQuery__
 *
 * To run a query within a React component, call `useGetArtboardByPermanentArtboardShortIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetArtboardByPermanentArtboardShortIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetArtboardByPermanentArtboardShortIdQuery({
 *   variables: {
 *      permanentArtboardShortId: // value for 'permanentArtboardShortId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *      documentVersionShortId: // value for 'documentVersionShortId'
 *   },
 * });
 */
function useGetArtboardByPermanentArtboardShortIdQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetArtboardByPermanentArtboardShortIdDocument, baseOptions);
}
function useGetArtboardByPermanentArtboardShortIdLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetArtboardByPermanentArtboardShortIdDocument, baseOptions);
}
const GetComponentsDocument = gql `
  query getComponents(
    $shareIdentifier: ShortId
    $versionShortId: ShortId
    $after: String = null
    $before: String = null
    $type: ComponentFilterType = SYMBOL
    $search: String = null
    $path: String = null
    $limit: Int = 40
  ) {
    share(shortId: $shareIdentifier) {
      identifier
      version(shortId: $versionShortId) {
        identifier
        shortId
        hasComponentManifest
        document {
          identifier
          componentsState
          documentVersion
          coeditCompatibilityVersion
          canEditDescriptions
          components(
            type: $type
            after: $after
            before: $before
            search: $search
            path: $path
            limit: $limit
          ) {
            entries {
              ...ComponentInfo
            }
            meta {
              after
              before
              limit
              totalCount
            }
          }
        }
      }
      latestVersionOfAnyState: version {
        identifier
        document {
          identifier
          renderStatus
        }
      }
    }
  }
  ${ComponentInfoFragmentDoc}
`;
/**
 * __useGetComponentsQuery__
 *
 * To run a query within a React component, call `useGetComponentsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetComponentsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetComponentsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *      after: // value for 'after'
 *      before: // value for 'before'
 *      type: // value for 'type'
 *      search: // value for 'search'
 *      path: // value for 'path'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetComponentsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetComponentsDocument, baseOptions);
}
function useGetComponentsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetComponentsDocument, baseOptions);
}
const GetComponentsCountDocument = gql `
  query getComponentsCount(
    $shareIdentifier: ShortId
    $versionShortId: ShortId
  ) {
    share(shortId: $shareIdentifier) {
      identifier
      version(shortId: $versionShortId) {
        identifier
        shortId
        hasComponentManifest
        document {
          componentsState
          identifier
          componentCount {
            layerStyle
            textStyle
            colorVar
            symbol
          }
        }
      }
    }
  }
`;
/**
 * __useGetComponentsCountQuery__
 *
 * To run a query within a React component, call `useGetComponentsCountQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetComponentsCountQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetComponentsCountQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *   },
 * });
 */
function useGetComponentsCountQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetComponentsCountDocument, baseOptions);
}
function useGetComponentsCountLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetComponentsCountDocument, baseOptions);
}
const GetComponentGroupsDocument = gql `
  query getComponentGroups(
    $uuid: UUID!
    $type: ComponentFilterType!
    $versionShortId: ShortId
  ) {
    share(id: $uuid) {
      identifier
      version(shortId: $versionShortId) {
        identifier
        document {
          identifier
          componentGroups(type: $type) {
            data
          }
        }
      }
    }
  }
`;
/**
 * __useGetComponentGroupsQuery__
 *
 * To run a query within a React component, call `useGetComponentGroupsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetComponentGroupsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetComponentGroupsQuery({
 *   variables: {
 *      uuid: // value for 'uuid'
 *      type: // value for 'type'
 *      versionShortId: // value for 'versionShortId'
 *   },
 * });
 */
function useGetComponentGroupsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetComponentGroupsDocument, baseOptions);
}
function useGetComponentGroupsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetComponentGroupsDocument, baseOptions);
}
const GetComponentWithUuidDocument = gql `
  query getComponentWithUuid($documentId: UUID!, $uuid: String!) {
    componentInDocumentWithUuid(documentId: $documentId, uuid: $uuid) {
      ...ComponentInfo
    }
  }
  ${ComponentInfoFragmentDoc}
`;
/**
 * __useGetComponentWithUuidQuery__
 *
 * To run a query within a React component, call `useGetComponentWithUuidQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetComponentWithUuidQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetComponentWithUuidQuery({
 *   variables: {
 *      documentId: // value for 'documentId'
 *      uuid: // value for 'uuid'
 *   },
 * });
 */
function useGetComponentWithUuidQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetComponentWithUuidDocument, baseOptions);
}
function useGetComponentWithUuidLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetComponentWithUuidDocument, baseOptions);
}
const EditDescriptionsDocument = gql `
  mutation editDescriptions(
    $baseVersionIdentifier: UUID4!
    $compatibilityVersion: Int!
    $documentVersion: Int!
    $edits: [ComponentDescriptionEditInput!]!
    $shareIdentifier: UUID4!
  ) @capacitorapi {
    editComponents(
      baseVersionIdentifier: $baseVersionIdentifier
      compatibilityVersion: $compatibilityVersion
      documentVersion: $documentVersion
      edits: $edits
      shareIdentifier: $shareIdentifier
    ) {
      status
    }
  }
`;
/**
 * __useEditDescriptionsMutation__
 *
 * To run a mutation, you first call `useEditDescriptionsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEditDescriptionsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [editDescriptionsMutation, { data, loading, error }] = useEditDescriptionsMutation({
 *   variables: {
 *      baseVersionIdentifier: // value for 'baseVersionIdentifier'
 *      compatibilityVersion: // value for 'compatibilityVersion'
 *      documentVersion: // value for 'documentVersion'
 *      edits: // value for 'edits'
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useEditDescriptionsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EditDescriptionsDocument, baseOptions);
}
const GetPendingDescriptionDocument = gql `
  query getPendingDescription(
    $shareIdentifier: UUID4!
    $versionIdentifier: UUID4!
  ) @capacitorapi {
    collaborativeEditingSession(shareIdentifier: $shareIdentifier) {
      baseVersionIdentifier
      shareIdentifier
      compatibilityVersion
      componentEdits(versionIdentifier: $versionIdentifier) {
        componentUuid
        description
      }
    }
  }
`;
/**
 * __useGetPendingDescriptionQuery__
 *
 * To run a query within a React component, call `useGetPendingDescriptionQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPendingDescriptionQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPendingDescriptionQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionIdentifier: // value for 'versionIdentifier'
 *   },
 * });
 */
function useGetPendingDescriptionQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPendingDescriptionDocument, baseOptions);
}
function useGetPendingDescriptionLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPendingDescriptionDocument, baseOptions);
}
const GetPageDocument = gql `
  query getPage($share: UUID!, $version: ShortId, $pageUUID: String) {
    share(id: $share) {
      identifier
      commentsEnabled
      version(shortId: $version) {
        identifier
        shortId
        document {
          identifier
          artboardCount
          page(uuid: $pageUUID) {
            identifier
            uuid
            name
          }
        }
      }
    }
  }
`;
/**
 * __useGetPageQuery__
 *
 * To run a query within a React component, call `useGetPageQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPageQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPageQuery({
 *   variables: {
 *      share: // value for 'share'
 *      version: // value for 'version'
 *      pageUUID: // value for 'pageUUID'
 *   },
 * });
 */
function useGetPageQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPageDocument, baseOptions);
}
function useGetPageLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPageDocument, baseOptions);
}
const GetPagesDocument = gql `
  query getPages(
    $shareIdentifier: ShortId!
    $versionShortId: ShortId!
    $after: String = null
    $before: String = null
    $includeHasUnreadComments: Boolean = false
  ) {
    share(shortId: $shareIdentifier) {
      identifier
      version(shortId: $versionShortId) {
        identifier
        shortId
        document {
          identifier
          componentCount {
            symbol
            textStyle
            colorVar
            layerStyle
          }
          prototypes: artboards(filter: PROTOTYPE) {
            meta {
              totalCount
            }
          }
          pages(after: $after, before: $before) {
            entries {
              name
              identifier
              uuid
              ...AnnotationPageInfo
            }
            meta {
              after
              before
              limit
              totalCount
            }
          }
          prototypes: artboards(filter: PROTOTYPE) {
            meta {
              totalCount
            }
          }
        }
      }
    }
  }
  ${AnnotationPageInfoFragmentDoc}
`;
/**
 * __useGetPagesQuery__
 *
 * To run a query within a React component, call `useGetPagesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPagesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPagesQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *      after: // value for 'after'
 *      before: // value for 'before'
 *      includeHasUnreadComments: // value for 'includeHasUnreadComments'
 *   },
 * });
 */
function useGetPagesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPagesDocument, baseOptions);
}
function useGetPagesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPagesDocument, baseOptions);
}
const RenderDownloadableAssetsDocument = gql `
  mutation renderDownloadableAssets(
    $documentIdentifier: String!
    $input: RenderLayerDownloadableAssetsInput
  ) {
    renderDownloadableAssets(
      input: {
        documentIdentifier: $documentIdentifier
        renderLayerDownloadableAssetsInput: $input
      }
    ) {
      downloadableAsset {
        ...DownloadableAsset
      }
      errors {
        ...UserError
      }
    }
  }
  ${DownloadableAssetFragmentDoc}
  ${UserErrorFragmentDoc}
`;
/**
 * __useRenderDownloadableAssetsMutation__
 *
 * To run a mutation, you first call `useRenderDownloadableAssetsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRenderDownloadableAssetsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [renderDownloadableAssetsMutation, { data, loading, error }] = useRenderDownloadableAssetsMutation({
 *   variables: {
 *      documentIdentifier: // value for 'documentIdentifier'
 *      input: // value for 'input'
 *   },
 * });
 */
function useRenderDownloadableAssetsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RenderDownloadableAssetsDocument, baseOptions);
}
const GetDownloadableAssetsDocument = gql `
  query getDownloadableAssets(
    $shareIdentifier: ShortId!
    $versionShortId: ShortId
  ) {
    share(shortId: $shareIdentifier) {
      identifier
      version(shortId: $versionShortId) {
        shortId
        document {
          identifier
          ...DocumentDownloadableAssets
        }
      }
    }
  }
  ${DocumentDownloadableAssetsFragmentDoc}
`;
/**
 * __useGetDownloadableAssetsQuery__
 *
 * To run a query within a React component, call `useGetDownloadableAssetsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDownloadableAssetsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDownloadableAssetsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *   },
 * });
 */
function useGetDownloadableAssetsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDownloadableAssetsDocument, baseOptions);
}
function useGetDownloadableAssetsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDownloadableAssetsDocument, baseOptions);
}
const GetInspectorDataDocument = gql `
  query getInspectorData($artboardId: UUID, $pageId: UUID) {
    inspectorData(artboardId: $artboardId, pageId: $pageId) {
      inspector {
        status
        data
      }
    }
  }
`;
/**
 * __useGetInspectorDataQuery__
 *
 * To run a query within a React component, call `useGetInspectorDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetInspectorDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetInspectorDataQuery({
 *   variables: {
 *      artboardId: // value for 'artboardId'
 *      pageId: // value for 'pageId'
 *   },
 * });
 */
function useGetInspectorDataQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetInspectorDataDocument, baseOptions);
}
function useGetInspectorDataLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetInspectorDataDocument, baseOptions);
}
const GetInspectorComponentDataDocument = gql `
  query getInspectorComponentData($componentId: UUID) {
    inspectorData(componentId: $componentId) {
      inspector {
        status
        data
      }
    }
  }
`;
/**
 * __useGetInspectorComponentDataQuery__
 *
 * To run a query within a React component, call `useGetInspectorComponentDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetInspectorComponentDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetInspectorComponentDataQuery({
 *   variables: {
 *      componentId: // value for 'componentId'
 *   },
 * });
 */
function useGetInspectorComponentDataQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetInspectorComponentDataDocument, baseOptions);
}
function useGetInspectorComponentDataLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetInspectorComponentDataDocument, baseOptions);
}
const GetPagePresentationFileDocument = gql `
  query getPagePresentationFile(
    $shareIdentifier: UUID!
    $versionShortId: ShortId
    $pageUUID: String
  ) {
    share(id: $shareIdentifier) {
      identifier
      version(shortId: $versionShortId) {
        identifier
        shortId
        document {
          identifier
          page(uuid: $pageUUID) {
            identifier
            uuid
            ...PresentationFile
          }
        }
      }
    }
  }
  ${PresentationFileFragmentDoc}
`;
/**
 * __useGetPagePresentationFileQuery__
 *
 * To run a query within a React component, call `useGetPagePresentationFileQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPagePresentationFileQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPagePresentationFileQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *      pageUUID: // value for 'pageUUID'
 *   },
 * });
 */
function useGetPagePresentationFileQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPagePresentationFileDocument, baseOptions);
}
function useGetPagePresentationFileLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPagePresentationFileDocument, baseOptions);
}
const GetPresentationAssetsByArtboardDocument = gql `
  query getPresentationAssetsByArtboard(
    $documentId: String!
    $artboardUuid: String!
  ) {
    artboardInDocumentWithUuid(documentId: $documentId, uuid: $artboardUuid) {
      uuid
      page {
        identifier
        uuid
        ...PresentationFile
      }
    }
  }
  ${PresentationFileFragmentDoc}
`;
/**
 * __useGetPresentationAssetsByArtboardQuery__
 *
 * To run a query within a React component, call `useGetPresentationAssetsByArtboardQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPresentationAssetsByArtboardQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPresentationAssetsByArtboardQuery({
 *   variables: {
 *      documentId: // value for 'documentId'
 *      artboardUuid: // value for 'artboardUuid'
 *   },
 * });
 */
function useGetPresentationAssetsByArtboardQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPresentationAssetsByArtboardDocument, baseOptions);
}
function useGetPresentationAssetsByArtboardLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPresentationAssetsByArtboardDocument, baseOptions);
}
const PresentationFileDocument = gql `
  subscription presentationFile($pageId: String!) {
    presentationFile(pageId: $pageId) {
      url
    }
  }
`;
/**
 * __usePresentationFileSubscription__
 *
 * To run a query within a React component, call `usePresentationFileSubscription` and pass it any options that fit your needs.
 * When your component renders, `usePresentationFileSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePresentationFileSubscription({
 *   variables: {
 *      pageId: // value for 'pageId'
 *   },
 * });
 */
function usePresentationFileSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(PresentationFileDocument, baseOptions);
}
const ShareDeleteDocument = gql `
  mutation shareDelete($identifier: UUID!) {
    shareDelete(identifier: $identifier) {
      errors {
        ...UserError
      }
      successful
      share {
        identifier
        name
        deletedAt
        workspace {
          identifier
        }
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useShareDeleteMutation__
 *
 * To run a mutation, you first call `useShareDeleteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareDeleteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareDeleteMutation, { data, loading, error }] = useShareDeleteMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useShareDeleteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareDeleteDocument, baseOptions);
}
const RestoreShareDocument = gql `
  mutation restoreShare($identifier: UUID!) {
    restoreShare(identifier: $identifier) {
      share {
        ...ShareInfo
      }
    }
  }
  ${ShareInfoFragmentDoc}
`;
/**
 * __useRestoreShareMutation__
 *
 * To run a mutation, you first call `useRestoreShareMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRestoreShareMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [restoreShareMutation, { data, loading, error }] = useRestoreShareMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useRestoreShareMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RestoreShareDocument, baseOptions);
}
const ShareUpdateDocument = gql `
  mutation shareUpdate($identifier: UUID!, $share: ShareParams!) {
    shareUpdate(identifier: $identifier, share: $share) {
      errors {
        ...UserError
      }
      share {
        name
        identifier
        ...ShareSettings
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
  ${ShareSettingsFragmentDoc}
`;
/**
 * __useShareUpdateMutation__
 *
 * To run a mutation, you first call `useShareUpdateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareUpdateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareUpdateMutation, { data, loading, error }] = useShareUpdateMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      share: // value for 'share'
 *   },
 * });
 */
function useShareUpdateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareUpdateDocument, baseOptions);
}
const ShareLeaveDocument = gql `
  mutation shareLeave($identifier: UUID!) {
    shareLeave(identifier: $identifier) {
      errors {
        ...UserError
      }
      successful
      share {
        identifier
        workspace {
          identifier
          userIsMember
        }
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useShareLeaveMutation__
 *
 * To run a mutation, you first call `useShareLeaveMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareLeaveMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareLeaveMutation, { data, loading, error }] = useShareLeaveMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useShareLeaveMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareLeaveDocument, baseOptions);
}
const DeleteSharePermanentlyDocument = gql `
  mutation deleteSharePermanently(
    $workspaceId: UUID!
    $shareIdentifier: UUID!
  ) {
    deleteSharePermanently(
      workspaceIdentifier: $workspaceId
      shareIdentifier: $shareIdentifier
    ) {
      shareIdentifier
      workspace {
        identifier
      }
    }
  }
`;
/**
 * __useDeleteSharePermanentlyMutation__
 *
 * To run a mutation, you first call `useDeleteSharePermanentlyMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteSharePermanentlyMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteSharePermanentlyMutation, { data, loading, error }] = useDeleteSharePermanentlyMutation({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useDeleteSharePermanentlyMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteSharePermanentlyDocument, baseOptions);
}
const EmptyWorkspaceTrashDocument = gql `
  mutation emptyWorkspaceTrash($identifier: UUID!) {
    emptyWorkspaceTrash(identifier: $identifier) {
      workspace {
        identifier
      }
    }
  }
`;
/**
 * __useEmptyWorkspaceTrashMutation__
 *
 * To run a mutation, you first call `useEmptyWorkspaceTrashMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEmptyWorkspaceTrashMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [emptyWorkspaceTrashMutation, { data, loading, error }] = useEmptyWorkspaceTrashMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useEmptyWorkspaceTrashMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EmptyWorkspaceTrashDocument, baseOptions);
}
const UpdateSharePublicPermissionsDocument = gql `
  mutation updateSharePublicPermissions(
    $input: UpdateSharePublicPermissionsParams!
  ) {
    updateSharePublicPermissions(input: $input) {
      ...SharePublicPermissions
    }
  }
  ${SharePublicPermissionsFragmentDoc}
`;
/**
 * __useUpdateSharePublicPermissionsMutation__
 *
 * To run a mutation, you first call `useUpdateSharePublicPermissionsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateSharePublicPermissionsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateSharePublicPermissionsMutation, { data, loading, error }] = useUpdateSharePublicPermissionsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateSharePublicPermissionsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateSharePublicPermissionsDocument, baseOptions);
}
const UpdateShareParentPermissionsDocument = gql `
  mutation updateShareParentPermissions(
    $input: UpdateShareParentPermissionsParams!
  ) {
    updateShareParentPermissions(input: $input) {
      identifier
      userCanUpdateSettings
      parentAccessLevel
    }
  }
`;
/**
 * __useUpdateShareParentPermissionsMutation__
 *
 * To run a mutation, you first call `useUpdateShareParentPermissionsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateShareParentPermissionsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateShareParentPermissionsMutation, { data, loading, error }] = useUpdateShareParentPermissionsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateShareParentPermissionsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateShareParentPermissionsDocument, baseOptions);
}
const TransferShareDocument = gql `
  mutation transferShare(
    $shareIdentifier: UUID!
    $workspaceIdentifier: UUID!
    $projectIdentifier: UUID
    $membershipsOperation: MembershipsTransferOperation!
  ) {
    transferShare(
      shareIdentifier: $shareIdentifier
      workspaceIdentifier: $workspaceIdentifier
      projectIdentifier: $projectIdentifier
      membershipsOperation: $membershipsOperation
    ) {
      share {
        identifier
        project {
          ...Project
        }
        workspace {
          identifier
        }
      }
    }
  }
  ${ProjectFragmentDoc}
`;
/**
 * __useTransferShareMutation__
 *
 * To run a mutation, you first call `useTransferShareMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useTransferShareMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [transferShareMutation, { data, loading, error }] = useTransferShareMutation({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      projectIdentifier: // value for 'projectIdentifier'
 *      membershipsOperation: // value for 'membershipsOperation'
 *   },
 * });
 */
function useTransferShareMutation(baseOptions) {
    return ApolloReactHooks.useMutation(TransferShareDocument, baseOptions);
}
const UpdateSharePublicTokenExportDocument = gql `
  mutation updateSharePublicTokenExport(
    $shareIdentifier: UUID!
    $shareParams: ShareParams!
    $format: TokenExportFormat!
    $colorFormat: TokenColorFormat
  ) {
    shareUpdate(identifier: $shareIdentifier, share: $shareParams) {
      share {
        publicTokenExport
        publicTokenExportUrl(format: $format, colorFormat: $colorFormat)
      }
    }
  }
`;
/**
 * __useUpdateSharePublicTokenExportMutation__
 *
 * To run a mutation, you first call `useUpdateSharePublicTokenExportMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateSharePublicTokenExportMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateSharePublicTokenExportMutation, { data, loading, error }] = useUpdateSharePublicTokenExportMutation({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      shareParams: // value for 'shareParams'
 *      format: // value for 'format'
 *      colorFormat: // value for 'colorFormat'
 *   },
 * });
 */
function useUpdateSharePublicTokenExportMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateSharePublicTokenExportDocument, baseOptions);
}
const ShareRenameDocument = gql `
  mutation shareRename($identifier: UUID!, $name: String!) {
    shareUpdate(identifier: $identifier, share: { name: $name }) {
      successful
      share {
        identifier
        name
      }
      errors {
        message
      }
    }
  }
`;
/**
 * __useShareRenameMutation__
 *
 * To run a mutation, you first call `useShareRenameMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareRenameMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareRenameMutation, { data, loading, error }] = useShareRenameMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      name: // value for 'name'
 *   },
 * });
 */
function useShareRenameMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareRenameDocument, baseOptions);
}
const ShareDescriptionChangeDocument = gql `
  mutation shareDescriptionChange($identifier: UUID!, $description: String!) {
    shareUpdate(identifier: $identifier, share: { description: $description }) {
      successful
      share {
        identifier
        description
      }
      errors {
        message
      }
    }
  }
`;
/**
 * __useShareDescriptionChangeMutation__
 *
 * To run a mutation, you first call `useShareDescriptionChangeMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareDescriptionChangeMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareDescriptionChangeMutation, { data, loading, error }] = useShareDescriptionChangeMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      description: // value for 'description'
 *   },
 * });
 */
function useShareDescriptionChangeMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareDescriptionChangeDocument, baseOptions);
}
const ShareTypeChangeDocument = gql `
  mutation shareTypeChange($identifier: UUID!, $type: ShareType!) {
    shareUpdate(identifier: $identifier, share: { type: $type }) {
      successful
      share {
        identifier
        type
      }
      errors {
        message
      }
    }
  }
`;
/**
 * __useShareTypeChangeMutation__
 *
 * To run a mutation, you first call `useShareTypeChangeMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareTypeChangeMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareTypeChangeMutation, { data, loading, error }] = useShareTypeChangeMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      type: // value for 'type'
 *   },
 * });
 */
function useShareTypeChangeMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareTypeChangeDocument, baseOptions);
}
const DuplicateShareToDraftsDocument = gql `
  mutation duplicateShareToDrafts($input: DuplicateShareToDraftsParams!) {
    duplicateShareToDrafts(input: $input) {
      share {
        identifier
        workspace {
          identifier
          name
        }
      }
    }
  }
`;
/**
 * __useDuplicateShareToDraftsMutation__
 *
 * To run a mutation, you first call `useDuplicateShareToDraftsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDuplicateShareToDraftsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [duplicateShareToDraftsMutation, { data, loading, error }] = useDuplicateShareToDraftsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useDuplicateShareToDraftsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DuplicateShareToDraftsDocument, baseOptions);
}
const PublishShareDocument = gql `
  mutation publishShare(
    $shareIdentifier: UUID!
    $publicCommentsEnabled: Boolean!
    $publicInspectEnabled: Boolean!
  ) {
    publishShare(
      shareIdentifier: $shareIdentifier
      publicInspectEnabled: $publicInspectEnabled
      publicCommentsEnabled: $publicCommentsEnabled
    ) {
      publication {
        share {
          identifier
          ...SharePublicPermissions
        }
      }
    }
  }
  ${SharePublicPermissionsFragmentDoc}
`;
/**
 * __usePublishShareMutation__
 *
 * To run a mutation, you first call `usePublishShareMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `usePublishShareMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [publishShareMutation, { data, loading, error }] = usePublishShareMutation({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      publicCommentsEnabled: // value for 'publicCommentsEnabled'
 *      publicInspectEnabled: // value for 'publicInspectEnabled'
 *   },
 * });
 */
function usePublishShareMutation(baseOptions) {
    return ApolloReactHooks.useMutation(PublishShareDocument, baseOptions);
}
const UnpublishShareDocument = gql `
  mutation unpublishShare(
    $shareIdentifier: UUID!
    $publicAccessLevel: PublicAccessLevel!
    $publicCommentsEnabled: Boolean!
    $publicInspectEnabled: Boolean!
  ) {
    unpublishShare(
      shareIdentifier: $shareIdentifier
      publicInspectEnabled: $publicInspectEnabled
      publicCommentsEnabled: $publicCommentsEnabled
      publicAccessLevel: $publicAccessLevel
    ) {
      share {
        identifier
        ...SharePublicPermissions
      }
    }
  }
  ${SharePublicPermissionsFragmentDoc}
`;
/**
 * __useUnpublishShareMutation__
 *
 * To run a mutation, you first call `useUnpublishShareMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUnpublishShareMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [unpublishShareMutation, { data, loading, error }] = useUnpublishShareMutation({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      publicAccessLevel: // value for 'publicAccessLevel'
 *      publicCommentsEnabled: // value for 'publicCommentsEnabled'
 *      publicInspectEnabled: // value for 'publicInspectEnabled'
 *   },
 * });
 */
function useUnpublishShareMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UnpublishShareDocument, baseOptions);
}
const ReportShareDocument = gql `
  mutation reportShare($identifier: UUID!, $reason: ShareReportReason!) {
    reportShare(identifier: $identifier, reason: $reason) {
      reported
    }
  }
`;
/**
 * __useReportShareMutation__
 *
 * To run a mutation, you first call `useReportShareMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useReportShareMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [reportShareMutation, { data, loading, error }] = useReportShareMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      reason: // value for 'reason'
 *   },
 * });
 */
function useReportShareMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ReportShareDocument, baseOptions);
}
const GetShareDocument = gql `
  query getShare($shortId: ShortId!) {
    share(shortId: $shortId) {
      ...ShareInfoWithCollection
    }
  }
  ${ShareInfoWithCollectionFragmentDoc}
`;
/**
 * __useGetShareQuery__
 *
 * To run a query within a React component, call `useGetShareQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareQuery({
 *   variables: {
 *      shortId: // value for 'shortId'
 *   },
 * });
 */
function useGetShareQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareDocument, baseOptions);
}
function useGetShareLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareDocument, baseOptions);
}
const GetShareDropdownOptionsDocument = gql `
  query getShareDropdownOptions(
    $shareIdentifier: ShortId!
    $versionShortId: ShortId!
  ) {
    share(shortId: $shareIdentifier) {
      ...ShareDropdownOptions
    }
  }
  ${ShareDropdownOptionsFragmentDoc}
`;
/**
 * __useGetShareDropdownOptionsQuery__
 *
 * To run a query within a React component, call `useGetShareDropdownOptionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareDropdownOptionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareDropdownOptionsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *   },
 * });
 */
function useGetShareDropdownOptionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareDropdownOptionsDocument, baseOptions);
}
function useGetShareDropdownOptionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareDropdownOptionsDocument, baseOptions);
}
const GetShareWorkspaceDocument = gql `
  query getShareWorkspace($shareIdentifier: ShortId!) {
    share(shortId: $shareIdentifier) {
      ...ShareWorkspace
    }
  }
  ${ShareWorkspaceFragmentDoc}
`;
/**
 * __useGetShareWorkspaceQuery__
 *
 * To run a query within a React component, call `useGetShareWorkspaceQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareWorkspaceQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareWorkspaceQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetShareWorkspaceQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareWorkspaceDocument, baseOptions);
}
function useGetShareWorkspaceLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareWorkspaceDocument, baseOptions);
}
const GetSharesDocument = gql `
  query getShares(
    $identifier: UUID!
    $after: String
    $search: SharesSearchParams
    $sortOrder: ShareSortOrder
  ) {
    workspace(identifier: $identifier) {
      identifier
      shares(after: $after, search: $search, sortOrder: $sortOrder) {
        entries {
          ...ShareListItem
        }
        meta {
          ...ShareMeta
        }
      }
    }
  }
  ${ShareListItemFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetSharesQuery__
 *
 * To run a query within a React component, call `useGetSharesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSharesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSharesQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *      sortOrder: // value for 'sortOrder'
 *   },
 * });
 */
function useGetSharesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSharesDocument, baseOptions);
}
function useGetSharesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSharesDocument, baseOptions);
}
const GetSharesLibrariesDocument = gql `
  query getSharesLibraries(
    $identifier: UUID!
    $after: String
    $search: SharesSearchParams
  ) {
    workspace(identifier: $identifier) {
      identifier
      shares(after: $after, search: $search) {
        entries {
          ...ShareLibraryListItem
        }
        meta {
          ...ShareMeta
        }
      }
    }
  }
  ${ShareLibraryListItemFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetSharesLibrariesQuery__
 *
 * To run a query within a React component, call `useGetSharesLibrariesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSharesLibrariesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSharesLibrariesQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetSharesLibrariesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSharesLibrariesDocument, baseOptions);
}
function useGetSharesLibrariesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSharesLibrariesDocument, baseOptions);
}
const GetSharesDropdownOptionsDocument = gql `
  query getSharesDropdownOptions(
    $identifier: UUID!
    $after: String
    $search: SharesSearchParams
  ) {
    workspace(identifier: $identifier) {
      identifier
      shares(after: $after, search: $search) {
        entries {
          ...ShareDropdownOptionsPreload
        }
      }
    }
  }
  ${ShareDropdownOptionsPreloadFragmentDoc}
`;
/**
 * __useGetSharesDropdownOptionsQuery__
 *
 * To run a query within a React component, call `useGetSharesDropdownOptionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSharesDropdownOptionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSharesDropdownOptionsQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetSharesDropdownOptionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSharesDropdownOptionsDocument, baseOptions);
}
function useGetSharesDropdownOptionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSharesDropdownOptionsDocument, baseOptions);
}
const GetShareUserAccessLevelDocument = gql `
  query getShareUserAccessLevel($identifier: ShortId!) {
    share(shortId: $identifier) {
      identifier
      ...ShareUserAccessLevel
    }
  }
  ${ShareUserAccessLevelFragmentDoc}
`;
/**
 * __useGetShareUserAccessLevelQuery__
 *
 * To run a query within a React component, call `useGetShareUserAccessLevelQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareUserAccessLevelQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareUserAccessLevelQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetShareUserAccessLevelQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareUserAccessLevelDocument, baseOptions);
}
function useGetShareUserAccessLevelLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareUserAccessLevelDocument, baseOptions);
}
const GetProjectSharesDocument = gql `
  query getProjectShares(
    $shortId: ShortId!
    $after: String
    $search: SharesSearchParams
    $sortOrder: ShareSortOrder
  ) {
    project(shortId: $shortId) {
      ...ProjectInSidebarAndHeader
      workspaceAccessLevel
      shares(after: $after, searchParams: $search, sortOrder: $sortOrder) {
        entries {
          ...ShareListItem
          collection {
            identifier
            name
          }
        }
        meta {
          ...ShareMeta
        }
      }
    }
  }
  ${ProjectInSidebarAndHeaderFragmentDoc}
  ${ShareListItemFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetProjectSharesQuery__
 *
 * To run a query within a React component, call `useGetProjectSharesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectSharesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectSharesQuery({
 *   variables: {
 *      shortId: // value for 'shortId'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *      sortOrder: // value for 'sortOrder'
 *   },
 * });
 */
function useGetProjectSharesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectSharesDocument, baseOptions);
}
function useGetProjectSharesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectSharesDocument, baseOptions);
}
const GetProjectSharesDropdownOptionsDocument = gql `
  query getProjectSharesDropdownOptions(
    $shortId: ShortId!
    $after: String
    $search: SharesSearchParams
  ) {
    project(shortId: $shortId) {
      shortId
      identifier
      shares(after: $after, searchParams: $search) {
        entries {
          ...ShareDropdownOptionsPreload
        }
      }
    }
  }
  ${ShareDropdownOptionsPreloadFragmentDoc}
`;
/**
 * __useGetProjectSharesDropdownOptionsQuery__
 *
 * To run a query within a React component, call `useGetProjectSharesDropdownOptionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProjectSharesDropdownOptionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProjectSharesDropdownOptionsQuery({
 *   variables: {
 *      shortId: // value for 'shortId'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetProjectSharesDropdownOptionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProjectSharesDropdownOptionsDocument, baseOptions);
}
function useGetProjectSharesDropdownOptionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProjectSharesDropdownOptionsDocument, baseOptions);
}
const GetPersonalWorkspaceSharesCountDocument = gql `
  query getPersonalWorkspaceSharesCount {
    me {
      identifier
      personalWorkspace {
        identifier
        allShares: shares {
          meta {
            totalCount
          }
        }
        libraries: shares(search: { filters: [LIBRARY] }) {
          meta {
            totalCount
          }
        }
      }
    }
  }
`;
/**
 * __useGetPersonalWorkspaceSharesCountQuery__
 *
 * To run a query within a React component, call `useGetPersonalWorkspaceSharesCountQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPersonalWorkspaceSharesCountQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPersonalWorkspaceSharesCountQuery({
 *   variables: {
 *   },
 * });
 */
function useGetPersonalWorkspaceSharesCountQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPersonalWorkspaceSharesCountDocument, baseOptions);
}
function useGetPersonalWorkspaceSharesCountLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPersonalWorkspaceSharesCountDocument, baseOptions);
}
const GetLibrariesDocument = gql `
  query getLibraries($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      libraries: shares(search: { filters: [LIBRARY] }) {
        entries {
          identifier
          name
          version(preferredState: PROCESSED) {
            shortId
            document {
              previewFile {
                identifier
                url
              }
            }
          }
        }
        meta {
          ...ShareMeta
        }
      }
    }
  }
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetLibrariesQuery__
 *
 * To run a query within a React component, call `useGetLibrariesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetLibrariesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetLibrariesQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetLibrariesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetLibrariesDocument, baseOptions);
}
function useGetLibrariesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetLibrariesDocument, baseOptions);
}
const GetWorkspaceSharesCountDocument = gql `
  query getWorkspaceSharesCount($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      shares {
        meta {
          totalCount
        }
      }
    }
  }
`;
/**
 * __useGetWorkspaceSharesCountQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceSharesCountQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceSharesCountQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceSharesCountQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceSharesCountQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceSharesCountDocument, baseOptions);
}
function useGetWorkspaceSharesCountLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceSharesCountDocument, baseOptions);
}
const GetDocumentListSettingsDocument = gql `
  query getDocumentListSettings {
    documentsLayout @client
    showDocumentsInProjects @client
  }
`;
/**
 * __useGetDocumentListSettingsQuery__
 *
 * To run a query within a React component, call `useGetDocumentListSettingsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetDocumentListSettingsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetDocumentListSettingsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetDocumentListSettingsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetDocumentListSettingsDocument, baseOptions);
}
function useGetDocumentListSettingsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetDocumentListSettingsDocument, baseOptions);
}
const ShareForDocumentWithUuidDocument = gql `
  query shareForDocumentWithUuid($name: String!, $uuid: String!) {
    shareForDocumentWithUuid(uuid: $uuid, name: $name) {
      identifier
      name
      version {
        identifier
        shortId
        document {
          identifier
        }
      }
    }
  }
`;
/**
 * __useShareForDocumentWithUuidQuery__
 *
 * To run a query within a React component, call `useShareForDocumentWithUuidQuery` and pass it any options that fit your needs.
 * When your component renders, `useShareForDocumentWithUuidQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useShareForDocumentWithUuidQuery({
 *   variables: {
 *      name: // value for 'name'
 *      uuid: // value for 'uuid'
 *   },
 * });
 */
function useShareForDocumentWithUuidQuery(baseOptions) {
    return ApolloReactHooks.useQuery(ShareForDocumentWithUuidDocument, baseOptions);
}
function useShareForDocumentWithUuidLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(ShareForDocumentWithUuidDocument, baseOptions);
}
const GetShareSidebarDocument = gql `
  query getShareSidebar($identifier: UUID, $versionShortId: ShortId) {
    share(id: $identifier) {
      ...ShareAbout
    }
  }
  ${ShareAboutFragmentDoc}
`;
/**
 * __useGetShareSidebarQuery__
 *
 * To run a query within a React component, call `useGetShareSidebarQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareSidebarQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareSidebarQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      versionShortId: // value for 'versionShortId'
 *   },
 * });
 */
function useGetShareSidebarQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareSidebarDocument, baseOptions);
}
function useGetShareSidebarLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareSidebarDocument, baseOptions);
}
const GetShareColorTokenExportDocument = gql `
  query getShareColorTokenExport(
    $shareIdentifier: UUID
    $versionShortId: ShortId
    $format: TokenExportFormat!
    $colorFormat: TokenColorFormat
  ) {
    share(id: $shareIdentifier) {
      identifier
      publicTokenExport
      publicTokenExportUrl(format: $format, colorFormat: $colorFormat)
      version(shortId: $versionShortId) {
        identifier
        document {
          identifier
          componentsState
          tokenExport(format: $format, colorFormat: $colorFormat) {
            data
            downloadUrl
          }
        }
      }
    }
  }
`;
/**
 * __useGetShareColorTokenExportQuery__
 *
 * To run a query within a React component, call `useGetShareColorTokenExportQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareColorTokenExportQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareColorTokenExportQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *      format: // value for 'format'
 *      colorFormat: // value for 'colorFormat'
 *   },
 * });
 */
function useGetShareColorTokenExportQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareColorTokenExportDocument, baseOptions);
}
function useGetShareColorTokenExportLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareColorTokenExportDocument, baseOptions);
}
const GetSharePermissionsDocument = gql `
  query getSharePermissions($shareIdentifier: ShortId!) {
    share(shortId: $shareIdentifier) {
      ...SharePermissions
    }
  }
  ${SharePermissionsFragmentDoc}
`;
/**
 * __useGetSharePermissionsQuery__
 *
 * To run a query within a React component, call `useGetSharePermissionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSharePermissionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSharePermissionsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetSharePermissionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSharePermissionsDocument, baseOptions);
}
function useGetSharePermissionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSharePermissionsDocument, baseOptions);
}
const GetShareListItemDocument = gql `
  query getShareListItem($shareIdentifier: UUID!) {
    share(id: $shareIdentifier) {
      identifier
      ...ShareListItem
    }
  }
  ${ShareListItemFragmentDoc}
`;
/**
 * __useGetShareListItemQuery__
 *
 * To run a query within a React component, call `useGetShareListItemQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareListItemQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareListItemQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetShareListItemQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareListItemDocument, baseOptions);
}
function useGetShareListItemLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareListItemDocument, baseOptions);
}
const EmptiedWorkspaceTrashDocument = gql `
  subscription emptiedWorkspaceTrash {
    emptiedWorkspaceTrash {
      workspaceIdentifier
    }
  }
`;
/**
 * __useEmptiedWorkspaceTrashSubscription__
 *
 * To run a query within a React component, call `useEmptiedWorkspaceTrashSubscription` and pass it any options that fit your needs.
 * When your component renders, `useEmptiedWorkspaceTrashSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEmptiedWorkspaceTrashSubscription({
 *   variables: {
 *   },
 * });
 */
function useEmptiedWorkspaceTrashSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(EmptiedWorkspaceTrashDocument, baseOptions);
}
const ShareChangedDocument = gql `
  subscription shareChanged {
    shareChanged {
      shareIdentifier
      workspace {
        identifier
      }
      ... on ShareDeletedEvent {
        share {
          ...SubscriptionShare
        }
      }
      ... on ShareRestoredEvent {
        share {
          ...SubscriptionShare
        }
      }
    }
  }
  ${SubscriptionShareFragmentDoc}
`;
/**
 * __useShareChangedSubscription__
 *
 * To run a query within a React component, call `useShareChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useShareChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useShareChangedSubscription({
 *   variables: {
 *   },
 * });
 */
function useShareChangedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(ShareChangedDocument, baseOptions);
}
const ShareAcceptRequestDocument = gql `
  mutation shareAcceptRequest($id: UUID!) {
    shareAcceptRequest(id: $id) {
      errors {
        ...UserError
      }
      successful
      membership {
        identifier
        share {
          identifier
        }
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useShareAcceptRequestMutation__
 *
 * To run a mutation, you first call `useShareAcceptRequestMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareAcceptRequestMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareAcceptRequestMutation, { data, loading, error }] = useShareAcceptRequestMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
function useShareAcceptRequestMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareAcceptRequestDocument, baseOptions);
}
const AcceptShareMembershipInvitationDocument = gql `
  mutation acceptShareMembershipInvitation(
    $shareIdentifier: UUID
    $token: String
  ) {
    acceptShareMembershipInvitation(
      shareIdentifier: $shareIdentifier
      token: $token
    ) {
      identifier
      share {
        identifier
        userHasUnacceptedInvitationToView
      }
    }
  }
`;
/**
 * __useAcceptShareMembershipInvitationMutation__
 *
 * To run a mutation, you first call `useAcceptShareMembershipInvitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptShareMembershipInvitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptShareMembershipInvitationMutation, { data, loading, error }] = useAcceptShareMembershipInvitationMutation({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      token: // value for 'token'
 *   },
 * });
 */
function useAcceptShareMembershipInvitationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AcceptShareMembershipInvitationDocument, baseOptions);
}
const ShareDeleteMembershipDocument = gql `
  mutation shareDeleteMembership(
    $membershipIdentifier: UUID!
    $confirmed: Boolean
  ) {
    shareDeleteMembership(
      membershipIdentifier: $membershipIdentifier
      confirmed: $confirmed
    ) {
      errors {
        ...UserError
      }
      successful
      membership {
        ...ShareMembershipInfo
        share {
          identifier
          userCanUpdateSettings
        }
      }
    }
  }
  ${UserErrorFragmentDoc}
  ${ShareMembershipInfoFragmentDoc}
`;
/**
 * __useShareDeleteMembershipMutation__
 *
 * To run a mutation, you first call `useShareDeleteMembershipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareDeleteMembershipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareDeleteMembershipMutation, { data, loading, error }] = useShareDeleteMembershipMutation({
 *   variables: {
 *      membershipIdentifier: // value for 'membershipIdentifier'
 *      confirmed: // value for 'confirmed'
 *   },
 * });
 */
function useShareDeleteMembershipMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareDeleteMembershipDocument, baseOptions);
}
const ShareRequestAccessDocument = gql `
  mutation shareRequestAccess($shortId: ShortId!) {
    shareRequestAccess(shortId: $shortId) {
      errors {
        ...UserError
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useShareRequestAccessMutation__
 *
 * To run a mutation, you first call `useShareRequestAccessMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useShareRequestAccessMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [shareRequestAccessMutation, { data, loading, error }] = useShareRequestAccessMutation({
 *   variables: {
 *      shortId: // value for 'shortId'
 *   },
 * });
 */
function useShareRequestAccessMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ShareRequestAccessDocument, baseOptions);
}
const AddShareMembershipDocument = gql `
  mutation addShareMembership($input: AddShareMembershipParams!) {
    addShareMembership(input: $input) {
      shareMembership {
        ...ShareMembershipInfo
        share {
          identifier
          userCanUpdateSettings
        }
      }
    }
  }
  ${ShareMembershipInfoFragmentDoc}
`;
/**
 * __useAddShareMembershipMutation__
 *
 * To run a mutation, you first call `useAddShareMembershipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAddShareMembershipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [addShareMembershipMutation, { data, loading, error }] = useAddShareMembershipMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useAddShareMembershipMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AddShareMembershipDocument, baseOptions);
}
const UpdateShareMembershipDocument = gql `
  mutation updateShareMembership($input: UpdateShareMembershipParams!) {
    updateShareMembership(input: $input) {
      shareMembership {
        ...ShareMembershipInfo
        share {
          identifier
          userCanUpdateSettings
        }
      }
    }
  }
  ${ShareMembershipInfoFragmentDoc}
`;
/**
 * __useUpdateShareMembershipMutation__
 *
 * To run a mutation, you first call `useUpdateShareMembershipMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateShareMembershipMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateShareMembershipMutation, { data, loading, error }] = useUpdateShareMembershipMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateShareMembershipMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateShareMembershipDocument, baseOptions);
}
const GetShareMembershipsDocument = gql `
  query getShareMemberships(
    $shareIdentifier: ShortId!
    $after: String
    $limit: Int = 10
  ) {
    share(shortId: $shareIdentifier) {
      identifier
      myMembership {
        ...ShareMembershipInfo
      }
      memberships(limit: $limit, after: $after) {
        entries {
          ...ShareMembershipInfo
        }
        meta {
          after
          totalCount
        }
      }
      parentAccessLevel
      workspace {
        avatar {
          small
        }
        name
        identifier
      }
    }
  }
  ${ShareMembershipInfoFragmentDoc}
`;
/**
 * __useGetShareMembershipsQuery__
 *
 * To run a query within a React component, call `useGetShareMembershipsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareMembershipsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareMembershipsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetShareMembershipsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareMembershipsDocument, baseOptions);
}
function useGetShareMembershipsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareMembershipsDocument, baseOptions);
}
const GetShareMembershipByShareShortIdDocument = gql `
  query getShareMembershipByShareShortId($shareIdentifier: ShortId) {
    share(shortId: $shareIdentifier) {
      identifier
      name
      version(preferredState: PROCESSED) {
        identifier
        shortId
        document {
          identifier
          name
        }
      }
    }
    shareMembership(shareShortId: $shareIdentifier) {
      identifier
      inviteStatus
      inviter {
        name
        avatar {
          large
        }
      }
    }
  }
`;
/**
 * __useGetShareMembershipByShareShortIdQuery__
 *
 * To run a query within a React component, call `useGetShareMembershipByShareShortIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareMembershipByShareShortIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareMembershipByShareShortIdQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetShareMembershipByShareShortIdQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareMembershipByShareShortIdDocument, baseOptions);
}
function useGetShareMembershipByShareShortIdLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareMembershipByShareShortIdDocument, baseOptions);
}
const GetShareMembershipDocument = gql `
  query getShareMembership($token: String) {
    shareMembership(token: $token) {
      identifier
      email
      inviteStatus
      inviter {
        name
        avatar {
          large
        }
      }
    }
  }
`;
/**
 * __useGetShareMembershipQuery__
 *
 * To run a query within a React component, call `useGetShareMembershipQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareMembershipQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareMembershipQuery({
 *   variables: {
 *      token: // value for 'token'
 *   },
 * });
 */
function useGetShareMembershipQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareMembershipDocument, baseOptions);
}
function useGetShareMembershipLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareMembershipDocument, baseOptions);
}
const GetTrashDocument = gql `
  query getTrash(
    $identifier: UUID!
    $after: String
    $search: TrashSearchParams
  ) {
    workspaceTrash(identifier: $identifier, after: $after, search: $search) {
      entries {
        __typename
        ... on TrashedProject {
          ...TrashedProject
        }
        ... on TrashedShare {
          ...TrashedShare
        }
      }
      meta {
        ...ShareMeta
      }
    }
  }
  ${TrashedProjectFragmentDoc}
  ${TrashedShareFragmentDoc}
  ${ShareMetaFragmentDoc}
`;
/**
 * __useGetTrashQuery__
 *
 * To run a query within a React component, call `useGetTrashQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetTrashQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetTrashQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      after: // value for 'after'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetTrashQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetTrashDocument, baseOptions);
}
function useGetTrashLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetTrashDocument, baseOptions);
}
const GetCampaignSignUpContentDocument = gql `
  query getCampaignSignUpContent($campaign: String!) {
    campaignSignUpContent(campaign: $campaign) @client {
      active
      content {
        ...SignUpContent
      }
    }
  }
  ${SignUpContentFragmentDoc}
`;
/**
 * __useGetCampaignSignUpContentQuery__
 *
 * To run a query within a React component, call `useGetCampaignSignUpContentQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCampaignSignUpContentQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCampaignSignUpContentQuery({
 *   variables: {
 *      campaign: // value for 'campaign'
 *   },
 * });
 */
function useGetCampaignSignUpContentQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetCampaignSignUpContentDocument, baseOptions);
}
function useGetCampaignSignUpContentLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetCampaignSignUpContentDocument, baseOptions);
}
const GetRoleSignUpContentDocument = gql `
  query getRoleSignUpContent {
    roleSignUpContent @client {
      designer {
        ...SignUpContent
      }
      designSystemManager {
        ...SignUpContent
      }
      managerOrDesignTeamLead {
        ...SignUpContent
      }
      product {
        ...SignUpContent
      }
      developer {
        ...SignUpContent
      }
      marketingSpecialist {
        ...SignUpContent
      }
      writer {
        ...SignUpContent
      }
      financeOrAdmin {
        ...SignUpContent
      }
      researcher {
        ...SignUpContent
      }
      student {
        ...SignUpContent
      }
      teacher {
        ...SignUpContent
      }
      educationalInstitution {
        ...SignUpContent
      }
      other {
        ...SignUpContent
      }
    }
  }
  ${SignUpContentFragmentDoc}
`;
/**
 * __useGetRoleSignUpContentQuery__
 *
 * To run a query within a React component, call `useGetRoleSignUpContentQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetRoleSignUpContentQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetRoleSignUpContentQuery({
 *   variables: {
 *   },
 * });
 */
function useGetRoleSignUpContentQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetRoleSignUpContentDocument, baseOptions);
}
function useGetRoleSignUpContentLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetRoleSignUpContentDocument, baseOptions);
}
const UpdateWorkspaceCustomerSsoShortNameDocument = gql `
  mutation updateWorkspaceCustomerSsoShortName(
    $customerId: UUID!
    $shortName: String!
  ) {
    updateWorkspaceCustomerSsoShortName(
      customerIdentifier: $customerId
      shortName: $shortName
    ) {
      customer {
        identifier
        ssoShortName
      }
    }
  }
`;
/**
 * __useUpdateWorkspaceCustomerSsoShortNameMutation__
 *
 * To run a mutation, you first call `useUpdateWorkspaceCustomerSsoShortNameMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateWorkspaceCustomerSsoShortNameMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateWorkspaceCustomerSsoShortNameMutation, { data, loading, error }] = useUpdateWorkspaceCustomerSsoShortNameMutation({
 *   variables: {
 *      customerId: // value for 'customerId'
 *      shortName: // value for 'shortName'
 *   },
 * });
 */
function useUpdateWorkspaceCustomerSsoShortNameMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateWorkspaceCustomerSsoShortNameDocument, baseOptions);
}
const UpdateWorkspaceCustomerSsoIdentityProviderConfigDocument = gql `
  mutation updateWorkspaceCustomerSsoIdentityProviderConfig(
    $config: UpdateWorkspaceCustomerSsoIdentityProviderConfigParams
    $customerId: UUID!
    $metadata: Upload
  ) {
    updateWorkspaceCustomerSsoIdentityProviderConfig(
      config: $config
      customerIdentifier: $customerId
      metadata: $metadata
    ) {
      customer {
        identifier
        ssoEnabled
        ssoConfig {
          ...WorkspaceSsoConfig
        }
      }
    }
  }
  ${WorkspaceSsoConfigFragmentDoc}
`;
/**
 * __useUpdateWorkspaceCustomerSsoIdentityProviderConfigMutation__
 *
 * To run a mutation, you first call `useUpdateWorkspaceCustomerSsoIdentityProviderConfigMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateWorkspaceCustomerSsoIdentityProviderConfigMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateWorkspaceCustomerSsoIdentityProviderConfigMutation, { data, loading, error }] = useUpdateWorkspaceCustomerSsoIdentityProviderConfigMutation({
 *   variables: {
 *      config: // value for 'config'
 *      customerId: // value for 'customerId'
 *      metadata: // value for 'metadata'
 *   },
 * });
 */
function useUpdateWorkspaceCustomerSsoIdentityProviderConfigMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateWorkspaceCustomerSsoIdentityProviderConfigDocument, baseOptions);
}
const UpdateWorkspaceCustomerSsoAttributeMappingDocument = gql `
  mutation updateWorkspaceCustomerSsoAttributeMapping(
    $customerIdentifier: UUID!
    $mappings: UpdateWorkspaceCustomerSsoAttributeMappingParams!
  ) {
    updateWorkspaceCustomerSsoAttributeMapping(
      customerIdentifier: $customerIdentifier
      mappings: $mappings
    ) {
      customer {
        identifier
        ssoConfig {
          ...WorkspaceSsoConfig
        }
      }
    }
  }
  ${WorkspaceSsoConfigFragmentDoc}
`;
/**
 * __useUpdateWorkspaceCustomerSsoAttributeMappingMutation__
 *
 * To run a mutation, you first call `useUpdateWorkspaceCustomerSsoAttributeMappingMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateWorkspaceCustomerSsoAttributeMappingMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateWorkspaceCustomerSsoAttributeMappingMutation, { data, loading, error }] = useUpdateWorkspaceCustomerSsoAttributeMappingMutation({
 *   variables: {
 *      customerIdentifier: // value for 'customerIdentifier'
 *      mappings: // value for 'mappings'
 *   },
 * });
 */
function useUpdateWorkspaceCustomerSsoAttributeMappingMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateWorkspaceCustomerSsoAttributeMappingDocument, baseOptions);
}
const GetPublicWorkspaceForAccountLinkingDocument = gql `
  query getPublicWorkspaceForAccountLinking($workspaceId: UUID!) {
    publicWorkspace(identifier: $workspaceId) {
      ...PublicWorkspaceMinimal
    }
    me {
      identifier
      personalWorkspace {
        ...WorkspaceMinimalIdentity
      }
      workspaces {
        ...WorkspaceMinimalIdentity
      }
      ssoLinkToken(team: $workspaceId)
    }
  }
  ${PublicWorkspaceMinimalFragmentDoc}
  ${WorkspaceMinimalIdentityFragmentDoc}
`;
/**
 * __useGetPublicWorkspaceForAccountLinkingQuery__
 *
 * To run a query within a React component, call `useGetPublicWorkspaceForAccountLinkingQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPublicWorkspaceForAccountLinkingQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPublicWorkspaceForAccountLinkingQuery({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *   },
 * });
 */
function useGetPublicWorkspaceForAccountLinkingQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPublicWorkspaceForAccountLinkingDocument, baseOptions);
}
function useGetPublicWorkspaceForAccountLinkingLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPublicWorkspaceForAccountLinkingDocument, baseOptions);
}
const DisableScimDocument = gql `
  mutation disableScim($identifier: UUID!) {
    disableScim(identifier: $identifier) {
      workspace {
        identifier
        scimEnabled
      }
    }
  }
`;
/**
 * __useDisableScimMutation__
 *
 * To run a mutation, you first call `useDisableScimMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDisableScimMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [disableScimMutation, { data, loading, error }] = useDisableScimMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useDisableScimMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DisableScimDocument, baseOptions);
}
const EnableScimDocument = gql `
  mutation enableScim($identifier: UUID!) {
    enableScim(identifier: $identifier) {
      workspace {
        identifier
        scimEnabled
      }
      scimToken
    }
  }
`;
/**
 * __useEnableScimMutation__
 *
 * To run a mutation, you first call `useEnableScimMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEnableScimMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [enableScimMutation, { data, loading, error }] = useEnableScimMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useEnableScimMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EnableScimDocument, baseOptions);
}
const ResetScimTokenDocument = gql `
  mutation resetScimToken($identifier: UUID!) {
    resetScimToken(identifier: $identifier) {
      scimToken
    }
  }
`;
/**
 * __useResetScimTokenMutation__
 *
 * To run a mutation, you first call `useResetScimTokenMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useResetScimTokenMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [resetScimTokenMutation, { data, loading, error }] = useResetScimTokenMutation({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useResetScimTokenMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ResetScimTokenDocument, baseOptions);
}
const GetSsoStartUrlDocument = gql `
  query getSsoStartUrl($teamName: String!) {
    ssoStartUrl(teamName: $teamName) {
      successful
      errors {
        ...UserError
      }
      url
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useGetSsoStartUrlQuery__
 *
 * To run a query within a React component, call `useGetSsoStartUrlQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSsoStartUrlQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSsoStartUrlQuery({
 *   variables: {
 *      teamName: // value for 'teamName'
 *   },
 * });
 */
function useGetSsoStartUrlQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSsoStartUrlDocument, baseOptions);
}
function useGetSsoStartUrlLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSsoStartUrlDocument, baseOptions);
}
const NotificationMarkAllAsReadDocument = gql `
  mutation notificationMarkAllAsRead {
    notificationMarkAllAsRead {
      successful
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useNotificationMarkAllAsReadMutation__
 *
 * To run a mutation, you first call `useNotificationMarkAllAsReadMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useNotificationMarkAllAsReadMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [notificationMarkAllAsReadMutation, { data, loading, error }] = useNotificationMarkAllAsReadMutation({
 *   variables: {
 *   },
 * });
 */
function useNotificationMarkAllAsReadMutation(baseOptions) {
    return ApolloReactHooks.useMutation(NotificationMarkAllAsReadDocument, baseOptions);
}
const NotificationMarkAllAsSeenDocument = gql `
  mutation notificationMarkAllAsSeen {
    notificationMarkAllAsSeen {
      successful
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useNotificationMarkAllAsSeenMutation__
 *
 * To run a mutation, you first call `useNotificationMarkAllAsSeenMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useNotificationMarkAllAsSeenMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [notificationMarkAllAsSeenMutation, { data, loading, error }] = useNotificationMarkAllAsSeenMutation({
 *   variables: {
 *   },
 * });
 */
function useNotificationMarkAllAsSeenMutation(baseOptions) {
    return ApolloReactHooks.useMutation(NotificationMarkAllAsSeenDocument, baseOptions);
}
const NotificationMarkAsReadDocument = gql `
  mutation notificationMarkAsRead($id: ID!) {
    notificationMarkAsRead(id: $id) {
      successful
      notification {
        identifier
        isRead
      }
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useNotificationMarkAsReadMutation__
 *
 * To run a mutation, you first call `useNotificationMarkAsReadMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useNotificationMarkAsReadMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [notificationMarkAsReadMutation, { data, loading, error }] = useNotificationMarkAsReadMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
function useNotificationMarkAsReadMutation(baseOptions) {
    return ApolloReactHooks.useMutation(NotificationMarkAsReadDocument, baseOptions);
}
const GetNotificationsDocument = gql `
  query getNotifications($after: String) {
    me {
      identifier
      ...NotificationStatus
      notifications(after: $after) {
        entries {
          ...NotificationInfo
        }
        meta {
          after
        }
      }
    }
  }
  ${NotificationStatusFragmentDoc}
  ${NotificationInfoFragmentDoc}
`;
/**
 * __useGetNotificationsQuery__
 *
 * To run a query within a React component, call `useGetNotificationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetNotificationsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetNotificationsQuery({
 *   variables: {
 *      after: // value for 'after'
 *   },
 * });
 */
function useGetNotificationsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetNotificationsDocument, baseOptions);
}
function useGetNotificationsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetNotificationsDocument, baseOptions);
}
const NotificationCreatedDocument = gql `
  subscription notificationCreated {
    notificationCreated {
      ...NotificationInfo
    }
  }
  ${NotificationInfoFragmentDoc}
`;
/**
 * __useNotificationCreatedSubscription__
 *
 * To run a query within a React component, call `useNotificationCreatedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useNotificationCreatedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useNotificationCreatedSubscription({
 *   variables: {
 *   },
 * });
 */
function useNotificationCreatedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(NotificationCreatedDocument, baseOptions);
}
const UserPasswordUpdateDocument = gql `
  mutation userPasswordUpdate(
    $currentPassword: String!
    $newPassword: String!
  ) {
    userPasswordUpdate(
      input: { currentPassword: $currentPassword, password: $newPassword }
    ) {
      errors {
        ...UserError
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserPasswordUpdateMutation__
 *
 * To run a mutation, you first call `useUserPasswordUpdateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserPasswordUpdateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userPasswordUpdateMutation, { data, loading, error }] = useUserPasswordUpdateMutation({
 *   variables: {
 *      currentPassword: // value for 'currentPassword'
 *      newPassword: // value for 'newPassword'
 *   },
 * });
 */
function useUserPasswordUpdateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserPasswordUpdateDocument, baseOptions);
}
const UserDeleteDocument = gql `
  mutation userDelete($password: String!) {
    userDelete(password: $password) {
      successful
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserDeleteMutation__
 *
 * To run a mutation, you first call `useUserDeleteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserDeleteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userDeleteMutation, { data, loading, error }] = useUserDeleteMutation({
 *   variables: {
 *      password: // value for 'password'
 *   },
 * });
 */
function useUserDeleteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserDeleteDocument, baseOptions);
}
const EmailUpdateDocument = gql `
  mutation emailUpdate($email: String!, $password: String!) {
    emailUpdate(email: $email, password: $password) {
      errors {
        ...UserError
      }
      user {
        identifier
        email
        newEmail
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useEmailUpdateMutation__
 *
 * To run a mutation, you first call `useEmailUpdateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEmailUpdateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [emailUpdateMutation, { data, loading, error }] = useEmailUpdateMutation({
 *   variables: {
 *      email: // value for 'email'
 *      password: // value for 'password'
 *   },
 * });
 */
function useEmailUpdateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EmailUpdateDocument, baseOptions);
}
const EmailResendChangeConfirmationDocument = gql `
  mutation emailResendChangeConfirmation {
    emailResendChangeConfirmation {
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useEmailResendChangeConfirmationMutation__
 *
 * To run a mutation, you first call `useEmailResendChangeConfirmationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEmailResendChangeConfirmationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [emailResendChangeConfirmationMutation, { data, loading, error }] = useEmailResendChangeConfirmationMutation({
 *   variables: {
 *   },
 * });
 */
function useEmailResendChangeConfirmationMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EmailResendChangeConfirmationDocument, baseOptions);
}
const UserRequestPasswordResetDocument = gql `
  mutation userRequestPasswordReset($email: String!) {
    userRequestPasswordReset(email: $email) {
      errors {
        ...UserError
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserRequestPasswordResetMutation__
 *
 * To run a mutation, you first call `useUserRequestPasswordResetMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserRequestPasswordResetMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userRequestPasswordResetMutation, { data, loading, error }] = useUserRequestPasswordResetMutation({
 *   variables: {
 *      email: // value for 'email'
 *   },
 * });
 */
function useUserRequestPasswordResetMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserRequestPasswordResetDocument, baseOptions);
}
const EmailVerifyDocument = gql `
  mutation emailVerify($token: String!) {
    emailVerify(token: $token) {
      errors {
        ...UserError
      }
      email
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useEmailVerifyMutation__
 *
 * To run a mutation, you first call `useEmailVerifyMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEmailVerifyMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [emailVerifyMutation, { data, loading, error }] = useEmailVerifyMutation({
 *   variables: {
 *      token: // value for 'token'
 *   },
 * });
 */
function useEmailVerifyMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EmailVerifyDocument, baseOptions);
}
const ResendVerificationEmailDocument = gql `
  mutation resendVerificationEmail {
    resendVerificationEmail {
      errors {
        ...UserError
      }
      successful
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useResendVerificationEmailMutation__
 *
 * To run a mutation, you first call `useResendVerificationEmailMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useResendVerificationEmailMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [resendVerificationEmailMutation, { data, loading, error }] = useResendVerificationEmailMutation({
 *   variables: {
 *   },
 * });
 */
function useResendVerificationEmailMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ResendVerificationEmailDocument, baseOptions);
}
const UserPasswordResetDocument = gql `
  mutation userPasswordReset($token: String!, $newPassword: String!) {
    userPasswordReset(input: { token: $token, newPassword: $newPassword }) {
      successful
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserPasswordResetMutation__
 *
 * To run a mutation, you first call `useUserPasswordResetMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserPasswordResetMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userPasswordResetMutation, { data, loading, error }] = useUserPasswordResetMutation({
 *   variables: {
 *      token: // value for 'token'
 *      newPassword: // value for 'newPassword'
 *   },
 * });
 */
function useUserPasswordResetMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserPasswordResetDocument, baseOptions);
}
const UserUpdateDocument = gql `
  mutation userUpdate($input: UserParams) {
    userUpdate(input: $input) {
      errors {
        ...UserError
      }
      successful
      user {
        identifier
        name
        receiveEmailNotifications
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserUpdateMutation__
 *
 * To run a mutation, you first call `useUserUpdateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserUpdateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userUpdateMutation, { data, loading, error }] = useUserUpdateMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUserUpdateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserUpdateDocument, baseOptions);
}
const UserAvatarDeleteDocument = gql `
  mutation userAvatarDelete {
    userAvatarDelete {
      user {
        identifier
        hasAvatar
        avatar {
          large
          small
        }
      }
      successful
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserAvatarDeleteMutation__
 *
 * To run a mutation, you first call `useUserAvatarDeleteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserAvatarDeleteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userAvatarDeleteMutation, { data, loading, error }] = useUserAvatarDeleteMutation({
 *   variables: {
 *   },
 * });
 */
function useUserAvatarDeleteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserAvatarDeleteDocument, baseOptions);
}
const UserAvatarCreateDocument = gql `
  mutation userAvatarCreate($file: Upload!) {
    userAvatarCreate(file: $file) {
      user {
        identifier
        hasAvatar
        avatar {
          large
          small
        }
      }
      successful
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserAvatarCreateMutation__
 *
 * To run a mutation, you first call `useUserAvatarCreateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserAvatarCreateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userAvatarCreateMutation, { data, loading, error }] = useUserAvatarCreateMutation({
 *   variables: {
 *      file: // value for 'file'
 *   },
 * });
 */
function useUserAvatarCreateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserAvatarCreateDocument, baseOptions);
}
const SendDownloadSketchEmailDocument = gql `
  mutation sendDownloadSketchEmail {
    sendDownloadSketchEmail {
      sent
    }
  }
`;
/**
 * __useSendDownloadSketchEmailMutation__
 *
 * To run a mutation, you first call `useSendDownloadSketchEmailMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSendDownloadSketchEmailMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [sendDownloadSketchEmailMutation, { data, loading, error }] = useSendDownloadSketchEmailMutation({
 *   variables: {
 *   },
 * });
 */
function useSendDownloadSketchEmailMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SendDownloadSketchEmailDocument, baseOptions);
}
const UserAcceptTosAgreementDocument = gql `
  mutation userAcceptTosAgreement {
    userAcceptTosAgreement {
      successful
      errors {
        ...UserError
      }
    }
  }
  ${UserErrorFragmentDoc}
`;
/**
 * __useUserAcceptTosAgreementMutation__
 *
 * To run a mutation, you first call `useUserAcceptTosAgreementMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUserAcceptTosAgreementMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [userAcceptTosAgreementMutation, { data, loading, error }] = useUserAcceptTosAgreementMutation({
 *   variables: {
 *   },
 * });
 */
function useUserAcceptTosAgreementMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UserAcceptTosAgreementDocument, baseOptions);
}
const UpdateUserPreferencesDocument = gql `
  mutation updateUserPreferences($showOnboardingFirstSteps: Boolean) {
    updateUserPreferences(showOnboardingFirstSteps: $showOnboardingFirstSteps) {
      user {
        ...UserPreferences
      }
    }
  }
  ${UserPreferencesFragmentDoc}
`;
/**
 * __useUpdateUserPreferencesMutation__
 *
 * To run a mutation, you first call `useUpdateUserPreferencesMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateUserPreferencesMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateUserPreferencesMutation, { data, loading, error }] = useUpdateUserPreferencesMutation({
 *   variables: {
 *      showOnboardingFirstSteps: // value for 'showOnboardingFirstSteps'
 *   },
 * });
 */
function useUpdateUserPreferencesMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateUserPreferencesDocument, baseOptions);
}
const GetUserIdDocument = gql `
  query getUserId {
    me {
      identifier
    }
  }
`;
/**
 * __useGetUserIdQuery__
 *
 * To run a query within a React component, call `useGetUserIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetUserIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetUserIdQuery({
 *   variables: {
 *   },
 * });
 */
function useGetUserIdQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetUserIdDocument, baseOptions);
}
function useGetUserIdLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetUserIdDocument, baseOptions);
}
const GetInitialUserDocument = gql `
  query getInitialUser {
    me {
      identifier
      ...InitialUser
    }
  }
  ${InitialUserFragmentDoc}
`;
/**
 * __useGetInitialUserQuery__
 *
 * To run a query within a React component, call `useGetInitialUserQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetInitialUserQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetInitialUserQuery({
 *   variables: {
 *   },
 * });
 */
function useGetInitialUserQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetInitialUserDocument, baseOptions);
}
function useGetInitialUserLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetInitialUserDocument, baseOptions);
}
const GetNotificationStatusDocument = gql `
  query getNotificationStatus {
    me {
      ...NotificationStatus
    }
  }
  ${NotificationStatusFragmentDoc}
`;
/**
 * __useGetNotificationStatusQuery__
 *
 * To run a query within a React component, call `useGetNotificationStatusQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetNotificationStatusQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetNotificationStatusQuery({
 *   variables: {
 *   },
 * });
 */
function useGetNotificationStatusQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetNotificationStatusDocument, baseOptions);
}
function useGetNotificationStatusLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetNotificationStatusDocument, baseOptions);
}
const GetUserSettingsDocument = gql `
  query getUserSettings($after: String, $limit: Int) {
    me {
      ...User
      identifier
      mfaState
      newEmail
      avatar {
        large
      }
      workspaceMemberships(after: $after, limit: $limit) {
        entries {
          ...UserWorkspaceMembership
        }
        meta {
          after
        }
      }
      activeSessions(limit: 20) {
        entries {
          ...UserActiveSession
        }
      }
    }
  }
  ${UserFragmentDoc}
  ${UserWorkspaceMembershipFragmentDoc}
  ${UserActiveSessionFragmentDoc}
`;
/**
 * __useGetUserSettingsQuery__
 *
 * To run a query within a React component, call `useGetUserSettingsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetUserSettingsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetUserSettingsQuery({
 *   variables: {
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetUserSettingsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetUserSettingsDocument, baseOptions);
}
function useGetUserSettingsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetUserSettingsDocument, baseOptions);
}
const GetUserHubSpotIdentificationTokenDocument = gql `
  query getUserHubSpotIdentificationToken {
    me {
      identifier
      email
      hsIdentificationToken
    }
  }
`;
/**
 * __useGetUserHubSpotIdentificationTokenQuery__
 *
 * To run a query within a React component, call `useGetUserHubSpotIdentificationTokenQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetUserHubSpotIdentificationTokenQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetUserHubSpotIdentificationTokenQuery({
 *   variables: {
 *   },
 * });
 */
function useGetUserHubSpotIdentificationTokenQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetUserHubSpotIdentificationTokenDocument, baseOptions);
}
function useGetUserHubSpotIdentificationTokenLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetUserHubSpotIdentificationTokenDocument, baseOptions);
}
const GetUserNotificationsCountDocument = gql `
  query getUserNotificationsCount {
    me {
      identifier
      ...UserNotificationCount
    }
  }
  ${UserNotificationCountFragmentDoc}
`;
/**
 * __useGetUserNotificationsCountQuery__
 *
 * To run a query within a React component, call `useGetUserNotificationsCountQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetUserNotificationsCountQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetUserNotificationsCountQuery({
 *   variables: {
 *   },
 * });
 */
function useGetUserNotificationsCountQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetUserNotificationsCountDocument, baseOptions);
}
function useGetUserNotificationsCountLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetUserNotificationsCountDocument, baseOptions);
}
const GetShareForDeleteEntityFromCacheDocument = gql `
  query getShareForDeleteEntityFromCache($shortId: ShortId!) {
    share(shortId: $shortId) {
      identifier
    }
  }
`;
/**
 * __useGetShareForDeleteEntityFromCacheQuery__
 *
 * To run a query within a React component, call `useGetShareForDeleteEntityFromCacheQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetShareForDeleteEntityFromCacheQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetShareForDeleteEntityFromCacheQuery({
 *   variables: {
 *      shortId: // value for 'shortId'
 *   },
 * });
 */
function useGetShareForDeleteEntityFromCacheQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetShareForDeleteEntityFromCacheDocument, baseOptions);
}
function useGetShareForDeleteEntityFromCacheLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetShareForDeleteEntityFromCacheDocument, baseOptions);
}
const GetArtboardRevisionsDocument = gql `
  query getArtboardRevisions(
    $shareIdentifier: UUID!
    $permanentArtboardIdentifier: ID!
    $after: String
  ) {
    artboardRevisions(
      shareIdentifier: $shareIdentifier
      permanentArtboardId: $permanentArtboardIdentifier
      after: $after
    ) {
      entries {
        ... on Revision {
          order
          ...ArtboardRevision
        }
        ... on RevisionDeleted {
          order
          ...ArtboardRevisionDeleted
        }
        ... on RevisionUnavailable {
          order
          ...ArtboardRevisionUnavailable
        }
      }
      meta {
        after
        totalCount
      }
    }
  }
  ${ArtboardRevisionFragmentDoc}
  ${ArtboardRevisionDeletedFragmentDoc}
  ${ArtboardRevisionUnavailableFragmentDoc}
`;
/**
 * __useGetArtboardRevisionsQuery__
 *
 * To run a query within a React component, call `useGetArtboardRevisionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetArtboardRevisionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetArtboardRevisionsQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      permanentArtboardIdentifier: // value for 'permanentArtboardIdentifier'
 *      after: // value for 'after'
 *   },
 * });
 */
function useGetArtboardRevisionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetArtboardRevisionsDocument, baseOptions);
}
function useGetArtboardRevisionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetArtboardRevisionsDocument, baseOptions);
}
const VersionDeleteDocument = gql `
  mutation versionDelete($versionIdentifier: UUID!) {
    versionDelete(versionIdentifier: $versionIdentifier) {
      successful
      errors {
        ...UserError
      }
      versionDeleted {
        ...VersionDeleted
      }
      share {
        ...ShareInfo
      }
    }
  }
  ${UserErrorFragmentDoc}
  ${VersionDeletedFragmentDoc}
  ${ShareInfoFragmentDoc}
`;
/**
 * __useVersionDeleteMutation__
 *
 * To run a mutation, you first call `useVersionDeleteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useVersionDeleteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [versionDeleteMutation, { data, loading, error }] = useVersionDeleteMutation({
 *   variables: {
 *      versionIdentifier: // value for 'versionIdentifier'
 *   },
 * });
 */
function useVersionDeleteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(VersionDeleteDocument, baseOptions);
}
const VersionUpdateKindDocument = gql `
  mutation versionUpdateKind($versionIdentifier: UUID!, $kind: Kind!) {
    versionUpdateKind(versionIdentifier: $versionIdentifier, kind: $kind) {
      shortId
      kind
    }
  }
`;
/**
 * __useVersionUpdateKindMutation__
 *
 * To run a mutation, you first call `useVersionUpdateKindMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useVersionUpdateKindMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [versionUpdateKindMutation, { data, loading, error }] = useVersionUpdateKindMutation({
 *   variables: {
 *      versionIdentifier: // value for 'versionIdentifier'
 *      kind: // value for 'kind'
 *   },
 * });
 */
function useVersionUpdateKindMutation(baseOptions) {
    return ApolloReactHooks.useMutation(VersionUpdateKindDocument, baseOptions);
}
const UpdateVersionDescriptionDocument = gql `
  mutation updateVersionDescription(
    $versionIdentifier: UUID!
    $shareIdentifier: UUID!
    $description: String!
  ) {
    updateVersionDescription(
      versionIdentifier: $versionIdentifier
      shareIdentifier: $shareIdentifier
      description: $description
    ) {
      shortId
      description
    }
  }
`;
/**
 * __useUpdateVersionDescriptionMutation__
 *
 * To run a mutation, you first call `useUpdateVersionDescriptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateVersionDescriptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateVersionDescriptionMutation, { data, loading, error }] = useUpdateVersionDescriptionMutation({
 *   variables: {
 *      versionIdentifier: // value for 'versionIdentifier'
 *      shareIdentifier: // value for 'shareIdentifier'
 *      description: // value for 'description'
 *   },
 * });
 */
function useUpdateVersionDescriptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateVersionDescriptionDocument, baseOptions);
}
const GetVersionDocument = gql `
  query getVersion($shareIdentifier: ShortId!, $versionShortId: ShortId) {
    share(shortId: $shareIdentifier) {
      identifier
      version(shortId: $versionShortId) {
        ...Version
      }
    }
  }
  ${VersionFragmentDoc}
`;
/**
 * __useGetVersionQuery__
 *
 * To run a query within a React component, call `useGetVersionQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetVersionQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetVersionQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      versionShortId: // value for 'versionShortId'
 *   },
 * });
 */
function useGetVersionQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetVersionDocument, baseOptions);
}
function useGetVersionLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetVersionDocument, baseOptions);
}
const GetLatestStarDocument = gql `
  query getLatestStar($shareIdentifier: ShortId) {
    share(shortId: $shareIdentifier) {
      identifier
      version(preferredState: PROCESSED_AND_PUBLISHED) {
        kind
        ...Version
      }
    }
  }
  ${VersionFragmentDoc}
`;
/**
 * __useGetLatestStarQuery__
 *
 * To run a query within a React component, call `useGetLatestStarQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetLatestStarQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetLatestStarQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *   },
 * });
 */
function useGetLatestStarQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetLatestStarDocument, baseOptions);
}
function useGetLatestStarLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetLatestStarDocument, baseOptions);
}
const VersionUploadProcessedDocument = gql `
  subscription versionUploadProcessed {
    versionUploadProcessed {
      ...ShareInfo
    }
  }
  ${ShareInfoFragmentDoc}
`;
/**
 * __useVersionUploadProcessedSubscription__
 *
 * To run a query within a React component, call `useVersionUploadProcessedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useVersionUploadProcessedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useVersionUploadProcessedSubscription({
 *   variables: {
 *   },
 * });
 */
function useVersionUploadProcessedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(VersionUploadProcessedDocument, baseOptions);
}
const VersionUploadCompleteDocument = gql `
  subscription versionUploadComplete($after: String = null, $limit: Int = 20) {
    versionUploadComplete {
      ...ShareVersionHistory
    }
  }
  ${ShareVersionHistoryFragmentDoc}
`;
/**
 * __useVersionUploadCompleteSubscription__
 *
 * To run a query within a React component, call `useVersionUploadCompleteSubscription` and pass it any options that fit your needs.
 * When your component renders, `useVersionUploadCompleteSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useVersionUploadCompleteSubscription({
 *   variables: {
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useVersionUploadCompleteSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(VersionUploadCompleteDocument, baseOptions);
}
const VersionDeletedDocument = gql `
  subscription versionDeleted {
    versionDeleted {
      share {
        ...ShareInfo
        ...ShareLatestStarredVersion
      }
      versionDeleted {
        ...VersionDeleted
      }
    }
  }
  ${ShareInfoFragmentDoc}
  ${ShareLatestStarredVersionFragmentDoc}
  ${VersionDeletedFragmentDoc}
`;
/**
 * __useVersionDeletedSubscription__
 *
 * To run a query within a React component, call `useVersionDeletedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useVersionDeletedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useVersionDeletedSubscription({
 *   variables: {
 *   },
 * });
 */
function useVersionDeletedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(VersionDeletedDocument, baseOptions);
}
const VersionUpdatedDocument = gql `
  subscription versionUpdated {
    versionUpdated {
      ...VersionListItem
    }
  }
  ${VersionListItemFragmentDoc}
`;
/**
 * __useVersionUpdatedSubscription__
 *
 * To run a query within a React component, call `useVersionUpdatedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useVersionUpdatedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useVersionUpdatedSubscription({
 *   variables: {
 *   },
 * });
 */
function useVersionUpdatedSubscription(baseOptions) {
    return ApolloReactHooks.useSubscription(VersionUpdatedDocument, baseOptions);
}
const GetVersionHistoryDocument = gql `
  query getVersionHistory(
    $shareIdentifier: ShortId!
    $after: String = null
    $limit: Int = 20
  ) {
    share(shortId: $shareIdentifier) {
      ...ShareVersionHistory
    }
  }
  ${ShareVersionHistoryFragmentDoc}
`;
/**
 * __useGetVersionHistoryQuery__
 *
 * To run a query within a React component, call `useGetVersionHistoryQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetVersionHistoryQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetVersionHistoryQuery({
 *   variables: {
 *      shareIdentifier: // value for 'shareIdentifier'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetVersionHistoryQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetVersionHistoryDocument, baseOptions);
}
function useGetVersionHistoryLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetVersionHistoryDocument, baseOptions);
}
const GetWorkspaceBillingDocument = gql `
  query getWorkspaceBilling($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      customer {
        identifier
        billing {
          ...BillingMinimal
        }
      }
    }
  }
  ${BillingMinimalFragmentDoc}
`;
/**
 * __useGetWorkspaceBillingQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceBillingQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceBillingQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceBillingQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceBillingQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceBillingDocument, baseOptions);
}
function useGetWorkspaceBillingLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceBillingDocument, baseOptions);
}
const GetBillingForWorkspaceNoticeDocument = gql `
  query getBillingForWorkspaceNotice($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      customer {
        identifier
        billing {
          ...BillingWorkspaceNotice
        }
      }
    }
  }
  ${BillingWorkspaceNoticeFragmentDoc}
`;
/**
 * __useGetBillingForWorkspaceNoticeQuery__
 *
 * To run a query within a React component, call `useGetBillingForWorkspaceNoticeQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetBillingForWorkspaceNoticeQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetBillingForWorkspaceNoticeQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetBillingForWorkspaceNoticeQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetBillingForWorkspaceNoticeDocument, baseOptions);
}
function useGetBillingForWorkspaceNoticeLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetBillingForWorkspaceNoticeDocument, baseOptions);
}
const GetNextBillingCycleDocument = gql `
  query getNextBillingCycle($customerId: UUID!, $isNewPricing: Boolean = false)
  @PaymentQLApi {
    customer(id: $customerId) {
      id
      billingSummary(newPricing: $isNewPricing) {
        nextBillingCycle {
          totalAmount
        }
      }
    }
  }
`;
/**
 * __useGetNextBillingCycleQuery__
 *
 * To run a query within a React component, call `useGetNextBillingCycleQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetNextBillingCycleQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetNextBillingCycleQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *      isNewPricing: // value for 'isNewPricing'
 *   },
 * });
 */
function useGetNextBillingCycleQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetNextBillingCycleDocument, baseOptions);
}
function useGetNextBillingCycleLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetNextBillingCycleDocument, baseOptions);
}
const UpdateBillingDetailsDocument = gql `
  mutation updateBillingDetails($input: UpdateBillingDetailsInput!)
  @PaymentQLApi {
    updateBillingDetails(input: $input) {
      customer {
        id
        billingDetails {
          ...BillingDetails
        }
      }
    }
  }
  ${BillingDetailsFragmentDoc}
`;
/**
 * __useUpdateBillingDetailsMutation__
 *
 * To run a mutation, you first call `useUpdateBillingDetailsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateBillingDetailsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateBillingDetailsMutation, { data, loading, error }] = useUpdateBillingDetailsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateBillingDetailsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateBillingDetailsDocument, baseOptions);
}
const GetBillingHistoryDocument = gql `
  query getBillingHistory($customerId: UUID!, $after: String, $limit: Int! = 10)
  @PaymentQLApi {
    customer(id: $customerId) {
      id
      billingHistory(after: $after, limit: $limit) {
        entries {
          ...BillingHistoryEntry
        }
        meta {
          after
          totalCount
        }
      }
    }
  }
  ${BillingHistoryEntryFragmentDoc}
`;
/**
 * __useGetBillingHistoryQuery__
 *
 * To run a query within a React component, call `useGetBillingHistoryQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetBillingHistoryQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetBillingHistoryQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetBillingHistoryQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetBillingHistoryDocument, baseOptions);
}
function useGetBillingHistoryLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetBillingHistoryDocument, baseOptions);
}
const GetBillingHistoryCountDocument = gql `
  query getBillingHistoryCount($customerId: UUID!) @PaymentQLApi {
    customer(id: $customerId) {
      id
      billingHistory {
        meta {
          totalCount
        }
      }
    }
  }
`;
/**
 * __useGetBillingHistoryCountQuery__
 *
 * To run a query within a React component, call `useGetBillingHistoryCountQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetBillingHistoryCountQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetBillingHistoryCountQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useGetBillingHistoryCountQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetBillingHistoryCountDocument, baseOptions);
}
function useGetBillingHistoryCountLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetBillingHistoryCountDocument, baseOptions);
}
const GetAvailablePlansDocument = gql `
  query getAvailablePlans($customerId: UUID) @PaymentQLApi {
    availablePlans(customerId: $customerId) {
      ...BillingPlan
    }
  }
  ${BillingPlanFragmentDoc}
`;
/**
 * __useGetAvailablePlansQuery__
 *
 * To run a query within a React component, call `useGetAvailablePlansQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAvailablePlansQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAvailablePlansQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useGetAvailablePlansQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAvailablePlansDocument, baseOptions);
}
function useGetAvailablePlansLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAvailablePlansDocument, baseOptions);
}
const CancelScheduledBillingSeatsDocument = gql `
  mutation cancelScheduledBillingSeats($workspaceId: UUID!) {
    cancelScheduledBillingSeats(input: { workspaceIdentifier: $workspaceId }) {
      workspace {
        identifier
        customer {
          identifier
          billing {
            seats {
              ...BillingSeatsInfo
            }
          }
        }
      }
    }
  }
  ${BillingSeatsInfoFragmentDoc}
`;
/**
 * __useCancelScheduledBillingSeatsMutation__
 *
 * To run a mutation, you first call `useCancelScheduledBillingSeatsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelScheduledBillingSeatsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelScheduledBillingSeatsMutation, { data, loading, error }] = useCancelScheduledBillingSeatsMutation({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *   },
 * });
 */
function useCancelScheduledBillingSeatsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CancelScheduledBillingSeatsDocument, baseOptions);
}
const UpdateBillingSeatsDocument = gql `
  mutation updateBillingSeats($input: UpdateBillingSeatsParams!) {
    updateBillingSeats(input: $input) {
      workspace {
        identifier
        customer {
          identifier
          billing {
            seats {
              ...BillingSeatsInfo
            }
          }
        }
      }
      pendingScaToken
    }
  }
  ${BillingSeatsInfoFragmentDoc}
`;
/**
 * __useUpdateBillingSeatsMutation__
 *
 * To run a mutation, you first call `useUpdateBillingSeatsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateBillingSeatsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateBillingSeatsMutation, { data, loading, error }] = useUpdateBillingSeatsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateBillingSeatsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateBillingSeatsDocument, baseOptions);
}
const ApplyCouponDocument = gql `
  mutation applyCoupon($promotionCode: String!, $customerId: UUID!)
  @PaymentQLApi {
    applyCoupon(promotionCode: $promotionCode, customerId: $customerId) {
      customer {
        id
        subscription {
          status
        }
      }
    }
  }
`;
/**
 * __useApplyCouponMutation__
 *
 * To run a mutation, you first call `useApplyCouponMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useApplyCouponMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [applyCouponMutation, { data, loading, error }] = useApplyCouponMutation({
 *   variables: {
 *      promotionCode: // value for 'promotionCode'
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useApplyCouponMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ApplyCouponDocument, baseOptions);
}
const SubscribeCustomerToPlanDocument = gql `
  mutation subscribeCustomerToPlan($input: SubscribeCustomerToPlanInput!)
  @PaymentQLApi {
    subscribeCustomerToPlan(input: $input) {
      pendingScaToken
      customer {
        id
        paymentDetails {
          ...PaymentDetails
        }
      }
    }
  }
  ${PaymentDetailsFragmentDoc}
`;
/**
 * __useSubscribeCustomerToPlanMutation__
 *
 * To run a mutation, you first call `useSubscribeCustomerToPlanMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSubscribeCustomerToPlanMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [subscribeCustomerToPlanMutation, { data, loading, error }] = useSubscribeCustomerToPlanMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useSubscribeCustomerToPlanMutation(baseOptions) {
    return ApolloReactHooks.useMutation(SubscribeCustomerToPlanDocument, baseOptions);
}
const GetCustomerDocument = gql `
  query getCustomer($customerId: UUID!, $isNewPricing: Boolean = false)
  @PaymentQLApi {
    customer(id: $customerId) {
      id
      credits
      subscription {
        provider
        pendingScaToken
        currentPlan {
          ...BillingPlan
        }
        renewalDate
      }
      billingSummary(newPricing: $isNewPricing) {
        nextBillingCycle {
          totalAmount
        }
      }
      paymentDetails {
        ...PaymentDetails
      }
      billingDetails {
        ...BillingDetails
      }
    }
  }
  ${BillingPlanFragmentDoc}
  ${PaymentDetailsFragmentDoc}
  ${BillingDetailsFragmentDoc}
`;
/**
 * __useGetCustomerQuery__
 *
 * To run a query within a React component, call `useGetCustomerQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCustomerQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCustomerQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *      isNewPricing: // value for 'isNewPricing'
 *   },
 * });
 */
function useGetCustomerQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetCustomerDocument, baseOptions);
}
function useGetCustomerLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetCustomerDocument, baseOptions);
}
const GetCustomerPlanDocument = gql `
  query getCustomerPlan($customerId: UUID!) @PaymentQLApi {
    customer(id: $customerId) {
      id
      subscription {
        status
        scheduledSeatsTotal
        currentSeatsTotal
        trialEnd
        currentPlan {
          ...BillingPlan
        }
      }
    }
  }
  ${BillingPlanFragmentDoc}
`;
/**
 * __useGetCustomerPlanQuery__
 *
 * To run a query within a React component, call `useGetCustomerPlanQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCustomerPlanQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCustomerPlanQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useGetCustomerPlanQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetCustomerPlanDocument, baseOptions);
}
function useGetCustomerPlanLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetCustomerPlanDocument, baseOptions);
}
const GetCustomerProviderDocument = gql `
  query getCustomerProvider($customerId: UUID!) @PaymentQLApi {
    customer(id: $customerId) {
      id
      subscription {
        provider
      }
    }
  }
`;
/**
 * __useGetCustomerProviderQuery__
 *
 * To run a query within a React component, call `useGetCustomerProviderQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetCustomerProviderQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetCustomerProviderQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useGetCustomerProviderQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetCustomerProviderDocument, baseOptions);
}
function useGetCustomerProviderLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetCustomerProviderDocument, baseOptions);
}
const DraftBillingSummaryDocument = gql `
  query draftBillingSummary(
    $planId: String!
    $seats: Int!
    $promotionCode: String
  ) @PaymentQLApi {
    draftBillingSummary(
      planID: $planId
      seats: $seats
      promotionCode: $promotionCode
    ) {
      billingCycleAmount
      monthlyAmount
      memberBillingCycleAmount
      memberMonthlyAmount
      trialEnd
      couponInfo {
        ...Coupon
      }
    }
  }
  ${CouponFragmentDoc}
`;
/**
 * __useDraftBillingSummaryQuery__
 *
 * To run a query within a React component, call `useDraftBillingSummaryQuery` and pass it any options that fit your needs.
 * When your component renders, `useDraftBillingSummaryQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useDraftBillingSummaryQuery({
 *   variables: {
 *      planId: // value for 'planId'
 *      seats: // value for 'seats'
 *      promotionCode: // value for 'promotionCode'
 *   },
 * });
 */
function useDraftBillingSummaryQuery(baseOptions) {
    return ApolloReactHooks.useQuery(DraftBillingSummaryDocument, baseOptions);
}
function useDraftBillingSummaryLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(DraftBillingSummaryDocument, baseOptions);
}
const RequestLicenseRedemptionDocument = gql `
  mutation requestLicenseRedemption($input: RequestLicenseRedemptionInput!)
  @PaymentQLApi {
    requestLicenseRedemption(input: $input) {
      licenseRedemption {
        ...LicenseRedemption
      }
    }
  }
  ${LicenseRedemptionFragmentDoc}
`;
/**
 * __useRequestLicenseRedemptionMutation__
 *
 * To run a mutation, you first call `useRequestLicenseRedemptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRequestLicenseRedemptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [requestLicenseRedemptionMutation, { data, loading, error }] = useRequestLicenseRedemptionMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useRequestLicenseRedemptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RequestLicenseRedemptionDocument, baseOptions);
}
const AcceptLicenseRedemptionDocument = gql `
  mutation acceptLicenseRedemption(
    $redemptionToken: String!
    $workspaceId: UUID!
  ) @PaymentQLApi {
    acceptLicenseRedemption(token: $redemptionToken, teamId: $workspaceId) {
      licenseRedemption {
        id
        status
      }
    }
  }
`;
/**
 * __useAcceptLicenseRedemptionMutation__
 *
 * To run a mutation, you first call `useAcceptLicenseRedemptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptLicenseRedemptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptLicenseRedemptionMutation, { data, loading, error }] = useAcceptLicenseRedemptionMutation({
 *   variables: {
 *      redemptionToken: // value for 'redemptionToken'
 *      workspaceId: // value for 'workspaceId'
 *   },
 * });
 */
function useAcceptLicenseRedemptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AcceptLicenseRedemptionDocument, baseOptions);
}
const CancelLicenseRedemptionDocument = gql `
  mutation cancelLicenseRedemption($id: UUID!) @PaymentQLApi {
    cancelLicenseRedemption(id: $id) {
      licenseRedemption {
        id
        status
      }
    }
  }
`;
/**
 * __useCancelLicenseRedemptionMutation__
 *
 * To run a mutation, you first call `useCancelLicenseRedemptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelLicenseRedemptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelLicenseRedemptionMutation, { data, loading, error }] = useCancelLicenseRedemptionMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
function useCancelLicenseRedemptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CancelLicenseRedemptionDocument, baseOptions);
}
const RequestLicenseRedemptionsDocument = gql `
  mutation requestLicenseRedemptions($input: RequestLicenseRedemptionsInput!)
  @PaymentQLApi {
    requestLicenseRedemptions(input: $input) {
      licenseRedemptions {
        ... on LicenseRedemptionError {
          serial
          reason
        }
      }
    }
  }
`;
/**
 * __useRequestLicenseRedemptionsMutation__
 *
 * To run a mutation, you first call `useRequestLicenseRedemptionsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRequestLicenseRedemptionsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [requestLicenseRedemptionsMutation, { data, loading, error }] = useRequestLicenseRedemptionsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useRequestLicenseRedemptionsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RequestLicenseRedemptionsDocument, baseOptions);
}
const GetLicenseRedemptionsDocument = gql `
  query getLicenseRedemptions(
    $customerId: UUID!
    $after: String
    $limit: Int! = 10
    $status: LicenseRedemptionStatus
  ) @PaymentQLApi {
    customer(id: $customerId) {
      id
      licenseRedemptions(after: $after, limit: $limit, status: $status) {
        entries {
          ...LicenseRedemption
        }
        meta {
          after
          totalCount
          limit
        }
      }
    }
  }
  ${LicenseRedemptionFragmentDoc}
`;
/**
 * __useGetLicenseRedemptionsQuery__
 *
 * To run a query within a React component, call `useGetLicenseRedemptionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetLicenseRedemptionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetLicenseRedemptionsQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *      status: // value for 'status'
 *   },
 * });
 */
function useGetLicenseRedemptionsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetLicenseRedemptionsDocument, baseOptions);
}
function useGetLicenseRedemptionsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetLicenseRedemptionsDocument, baseOptions);
}
const GetLicenseRedemptionDocument = gql `
  query getLicenseRedemption($token: String!) @PaymentQLApi {
    licenseRedemption(token: $token) {
      id
      serial
      status
      months
      seats
      credits
      customer {
        id
      }
    }
  }
`;
/**
 * __useGetLicenseRedemptionQuery__
 *
 * To run a query within a React component, call `useGetLicenseRedemptionQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetLicenseRedemptionQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetLicenseRedemptionQuery({
 *   variables: {
 *      token: // value for 'token'
 *   },
 * });
 */
function useGetLicenseRedemptionQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetLicenseRedemptionDocument, baseOptions);
}
function useGetLicenseRedemptionLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetLicenseRedemptionDocument, baseOptions);
}
const GetLicenseRedemptionPreviewDocument = gql `
  query getLicenseRedemptionPreview($serial: String!) @PaymentQLApi {
    previewLicenseRedemption(serial: $serial) {
      ...LicenseRedemptionPreview
    }
  }
  ${LicenseRedemptionPreviewFragmentDoc}
`;
/**
 * __useGetLicenseRedemptionPreviewQuery__
 *
 * To run a query within a React component, call `useGetLicenseRedemptionPreviewQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetLicenseRedemptionPreviewQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetLicenseRedemptionPreviewQuery({
 *   variables: {
 *      serial: // value for 'serial'
 *   },
 * });
 */
function useGetLicenseRedemptionPreviewQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetLicenseRedemptionPreviewDocument, baseOptions);
}
function useGetLicenseRedemptionPreviewLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetLicenseRedemptionPreviewDocument, baseOptions);
}
const GetOrganizationsDocument = gql `
  query getOrganizations {
    me {
      identifier
      organizations {
        identifier
        shortId
      }
    }
  }
`;
/**
 * __useGetOrganizationsQuery__
 *
 * To run a query within a React component, call `useGetOrganizationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetOrganizationsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetOrganizationsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetOrganizationsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetOrganizationsDocument, baseOptions);
}
function useGetOrganizationsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetOrganizationsDocument, baseOptions);
}
const UpdatePaymentDetailsDocument = gql `
  mutation updatePaymentDetails($input: UpdatePaymentDetailsInput!)
  @PaymentQLApi {
    updatePaymentDetails(input: $input) {
      customer {
        id
        paymentDetails {
          ...PaymentDetails
        }
        subscription {
          pendingScaToken
        }
      }
    }
  }
  ${PaymentDetailsFragmentDoc}
`;
/**
 * __useUpdatePaymentDetailsMutation__
 *
 * To run a mutation, you first call `useUpdatePaymentDetailsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdatePaymentDetailsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updatePaymentDetailsMutation, { data, loading, error }] = useUpdatePaymentDetailsMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdatePaymentDetailsMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdatePaymentDetailsDocument, baseOptions);
}
const GetPaymentDetailsDocument = gql `
  query getPaymentDetails($customerId: UUID!) @PaymentQLApi {
    customer(id: $customerId) {
      id
      paymentDetails {
        ...PaymentDetails
      }
    }
  }
  ${PaymentDetailsFragmentDoc}
`;
/**
 * __useGetPaymentDetailsQuery__
 *
 * To run a query within a React component, call `useGetPaymentDetailsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPaymentDetailsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPaymentDetailsQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useGetPaymentDetailsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPaymentDetailsDocument, baseOptions);
}
function useGetPaymentDetailsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPaymentDetailsDocument, baseOptions);
}
const EnablePersonalTokenUsageDocument = gql `
  mutation enablePersonalTokenUsage(
    $workspaceIdentifier: UUID!
    $userIdentifier: UUID!
  ) {
    ssoUserEnablePersonalTokenUsageInWorkspace(
      workspaceIdentifier: $workspaceIdentifier
      userIdentifier: $userIdentifier
    ) {
      userIdentifier
      workspaceIdentifier
      canUsePersonalTokens
    }
  }
`;
/**
 * __useEnablePersonalTokenUsageMutation__
 *
 * To run a mutation, you first call `useEnablePersonalTokenUsageMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEnablePersonalTokenUsageMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [enablePersonalTokenUsageMutation, { data, loading, error }] = useEnablePersonalTokenUsageMutation({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      userIdentifier: // value for 'userIdentifier'
 *   },
 * });
 */
function useEnablePersonalTokenUsageMutation(baseOptions) {
    return ApolloReactHooks.useMutation(EnablePersonalTokenUsageDocument, baseOptions);
}
const DisablePersonalTokenUsageDocument = gql `
  mutation disablePersonalTokenUsage(
    $workspaceIdentifier: UUID!
    $userIdentifier: UUID!
  ) {
    ssoUserDisablePersonalTokenUsageInWorkspace(
      workspaceIdentifier: $workspaceIdentifier
      userIdentifier: $userIdentifier
    ) {
      userIdentifier
      workspaceIdentifier
      canUsePersonalTokens
    }
  }
`;
/**
 * __useDisablePersonalTokenUsageMutation__
 *
 * To run a mutation, you first call `useDisablePersonalTokenUsageMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDisablePersonalTokenUsageMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [disablePersonalTokenUsageMutation, { data, loading, error }] = useDisablePersonalTokenUsageMutation({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      userIdentifier: // value for 'userIdentifier'
 *   },
 * });
 */
function useDisablePersonalTokenUsageMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DisablePersonalTokenUsageDocument, baseOptions);
}
const GetSeatsUpdateBillingSimulationDocument = gql `
  query getSeatsUpdateBillingSimulation(
    $customerId: UUID!
    $totalSeats: Int!
    $nextBillingCycle: Boolean
  ) @PaymentQLApi {
    seatsUpdateBillingSimulation(
      customerId: $customerId
      seats: $totalSeats
      nextBillingCycle: $nextBillingCycle
    ) {
      totalAmount
      monthlyAmount
      extraSeatsAmount
      prorated {
        creditsAmount
        chargeAmount
        amount
        proratedRefundAmountForNewSeats
        daysElapsedInCycle
      }
      couponInfo {
        ...Coupon
      }
    }
  }
  ${CouponFragmentDoc}
`;
/**
 * __useGetSeatsUpdateBillingSimulationQuery__
 *
 * To run a query within a React component, call `useGetSeatsUpdateBillingSimulationQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetSeatsUpdateBillingSimulationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetSeatsUpdateBillingSimulationQuery({
 *   variables: {
 *      customerId: // value for 'customerId'
 *      totalSeats: // value for 'totalSeats'
 *      nextBillingCycle: // value for 'nextBillingCycle'
 *   },
 * });
 */
function useGetSeatsUpdateBillingSimulationQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetSeatsUpdateBillingSimulationDocument, baseOptions);
}
function useGetSeatsUpdateBillingSimulationLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetSeatsUpdateBillingSimulationDocument, baseOptions);
}
const CancelSubscriptionDocument = gql `
  mutation cancelSubscription($customerId: UUID!) @PaymentQLApi {
    cancelSubscription(customerId: $customerId) {
      customer {
        id
        subscription {
          subscriptionEnd
          renewalDate
        }
      }
    }
  }
`;
/**
 * __useCancelSubscriptionMutation__
 *
 * To run a mutation, you first call `useCancelSubscriptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelSubscriptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelSubscriptionMutation, { data, loading, error }] = useCancelSubscriptionMutation({
 *   variables: {
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useCancelSubscriptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CancelSubscriptionDocument, baseOptions);
}
const UndoCancelSubscriptionDocument = gql `
  mutation undoCancelSubscription($customerId: UUID!) @PaymentQLApi {
    undoCancelSubscription(customerId: $customerId) {
      customer {
        id
        subscription {
          subscriptionEnd
          renewalDate
        }
      }
    }
  }
`;
/**
 * __useUndoCancelSubscriptionMutation__
 *
 * To run a mutation, you first call `useUndoCancelSubscriptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUndoCancelSubscriptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [undoCancelSubscriptionMutation, { data, loading, error }] = useUndoCancelSubscriptionMutation({
 *   variables: {
 *      customerId: // value for 'customerId'
 *   },
 * });
 */
function useUndoCancelSubscriptionMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UndoCancelSubscriptionDocument, baseOptions);
}
const GetTaxableCountriesDocument = gql `
  query getTaxableCountries @PaymentQLApi {
    taxableCountries {
      code
    }
  }
`;
/**
 * __useGetTaxableCountriesQuery__
 *
 * To run a query within a React component, call `useGetTaxableCountriesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetTaxableCountriesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetTaxableCountriesQuery({
 *   variables: {
 *   },
 * });
 */
function useGetTaxableCountriesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetTaxableCountriesDocument, baseOptions);
}
function useGetTaxableCountriesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetTaxableCountriesDocument, baseOptions);
}
const WebhookDestinationUpdateDocument = gql `
  mutation webhookDestinationUpdate(
    $workspaceIdentifier: UUID!
    $url: String!
  ) {
    webhookDestinationUpdate(
      workspaceIdentifier: $workspaceIdentifier
      url: $url
    ) {
      ...WebhookDestination
    }
  }
  ${WebhookDestinationFragmentDoc}
`;
/**
 * __useWebhookDestinationUpdateMutation__
 *
 * To run a mutation, you first call `useWebhookDestinationUpdateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useWebhookDestinationUpdateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [webhookDestinationUpdateMutation, { data, loading, error }] = useWebhookDestinationUpdateMutation({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      url: // value for 'url'
 *   },
 * });
 */
function useWebhookDestinationUpdateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(WebhookDestinationUpdateDocument, baseOptions);
}
const WebhookDestinationDeleteDocument = gql `
  mutation webhookDestinationDelete($workspaceIdentifier: UUID!) {
    webhookDestinationDelete(workspaceIdentifier: $workspaceIdentifier) {
      workspaceIdentifier
    }
  }
`;
/**
 * __useWebhookDestinationDeleteMutation__
 *
 * To run a mutation, you first call `useWebhookDestinationDeleteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useWebhookDestinationDeleteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [webhookDestinationDeleteMutation, { data, loading, error }] = useWebhookDestinationDeleteMutation({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *   },
 * });
 */
function useWebhookDestinationDeleteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(WebhookDestinationDeleteDocument, baseOptions);
}
const GetWebhookDestinationDocument = gql `
  query getWebhookDestination($workspaceIdentifier: UUID!) {
    workspace(identifier: $workspaceIdentifier) {
      identifier
      webhookDestination {
        ...WebhookDestination
      }
    }
  }
  ${WebhookDestinationFragmentDoc}
`;
/**
 * __useGetWebhookDestinationQuery__
 *
 * To run a query within a React component, call `useGetWebhookDestinationQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWebhookDestinationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWebhookDestinationQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *   },
 * });
 */
function useGetWebhookDestinationQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWebhookDestinationDocument, baseOptions);
}
function useGetWebhookDestinationLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWebhookDestinationDocument, baseOptions);
}
const CreateWorkspaceDocument = gql `
  mutation createWorkspace($input: CreateWorkspaceParams!) {
    createWorkspace(input: $input) {
      workspace {
        ...WorkspaceMinimal
        memberships {
          list(limit: 1) {
            entries {
              identifier
              role
            }
          }
        }
      }
    }
  }
  ${WorkspaceMinimalFragmentDoc}
`;
/**
 * __useCreateWorkspaceMutation__
 *
 * To run a mutation, you first call `useCreateWorkspaceMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateWorkspaceMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createWorkspaceMutation, { data, loading, error }] = useCreateWorkspaceMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useCreateWorkspaceMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateWorkspaceDocument, baseOptions);
}
const UpgradePersonalWorkspaceDocument = gql `
  mutation upgradePersonalWorkspace($input: UpgradePersonalWorkspaceParams!) {
    upgradePersonalWorkspace(input: $input) {
      workspace {
        ...WorkspaceMinimal
        memberships {
          list(limit: 1) {
            entries {
              identifier
              role
            }
          }
        }
      }
    }
  }
  ${WorkspaceMinimalFragmentDoc}
`;
/**
 * __useUpgradePersonalWorkspaceMutation__
 *
 * To run a mutation, you first call `useUpgradePersonalWorkspaceMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpgradePersonalWorkspaceMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [upgradePersonalWorkspaceMutation, { data, loading, error }] = useUpgradePersonalWorkspaceMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpgradePersonalWorkspaceMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpgradePersonalWorkspaceDocument, baseOptions);
}
const AcceptWorkspaceInviteDocument = gql `
  mutation acceptWorkspaceInvite($membershipToken: String!) {
    acceptWorkspaceInvite(membershipToken: $membershipToken) {
      membership {
        identifier
        inviter {
          identifier
          name
        }
        workspace {
          identifier
          name
        }
      }
    }
  }
`;
/**
 * __useAcceptWorkspaceInviteMutation__
 *
 * To run a mutation, you first call `useAcceptWorkspaceInviteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptWorkspaceInviteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptWorkspaceInviteMutation, { data, loading, error }] = useAcceptWorkspaceInviteMutation({
 *   variables: {
 *      membershipToken: // value for 'membershipToken'
 *   },
 * });
 */
function useAcceptWorkspaceInviteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AcceptWorkspaceInviteDocument, baseOptions);
}
const UpdateWorkspaceDocument = gql `
  mutation updateWorkspace($input: UpdateWorkspaceParams!) {
    updateWorkspace(input: $input) {
      workspace {
        identifier
        name
        avatar {
          large
          small
        }
      }
    }
  }
`;
/**
 * __useUpdateWorkspaceMutation__
 *
 * To run a mutation, you first call `useUpdateWorkspaceMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateWorkspaceMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateWorkspaceMutation, { data, loading, error }] = useUpdateWorkspaceMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateWorkspaceMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateWorkspaceDocument, baseOptions);
}
const DeleteWorkspaceAvatarDocument = gql `
  mutation deleteWorkspaceAvatar($workspaceId: UUID!) {
    deleteWorkspaceAvatar(identifier: $workspaceId) {
      workspace {
        identifier
        avatar {
          small
          large
        }
      }
    }
  }
`;
/**
 * __useDeleteWorkspaceAvatarMutation__
 *
 * To run a mutation, you first call `useDeleteWorkspaceAvatarMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteWorkspaceAvatarMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteWorkspaceAvatarMutation, { data, loading, error }] = useDeleteWorkspaceAvatarMutation({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *   },
 * });
 */
function useDeleteWorkspaceAvatarMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteWorkspaceAvatarDocument, baseOptions);
}
const DeleteWorkspaceDocument = gql `
  mutation deleteWorkspace($workspaceId: UUID!) {
    deleteWorkspace(identifier: $workspaceId) {
      deletedWorkspace {
        identifier
      }
    }
  }
`;
/**
 * __useDeleteWorkspaceMutation__
 *
 * To run a mutation, you first call `useDeleteWorkspaceMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteWorkspaceMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteWorkspaceMutation, { data, loading, error }] = useDeleteWorkspaceMutation({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *   },
 * });
 */
function useDeleteWorkspaceMutation(baseOptions) {
    return ApolloReactHooks.useMutation(DeleteWorkspaceDocument, baseOptions);
}
const AgreeToWorkspaceCustomerTermsOfServiceDocument = gql `
  mutation agreeToWorkspaceCustomerTermsOfService(
    $customerId: UUID!
    $tosAgreed: Boolean!
  ) {
    agreeToWorkspaceCustomerTermsOfService(
      customerIdentifier: $customerId
      agreed: $tosAgreed
    ) {
      customer {
        identifier
        tosAgreed
      }
    }
  }
`;
/**
 * __useAgreeToWorkspaceCustomerTermsOfServiceMutation__
 *
 * To run a mutation, you first call `useAgreeToWorkspaceCustomerTermsOfServiceMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAgreeToWorkspaceCustomerTermsOfServiceMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [agreeToWorkspaceCustomerTermsOfServiceMutation, { data, loading, error }] = useAgreeToWorkspaceCustomerTermsOfServiceMutation({
 *   variables: {
 *      customerId: // value for 'customerId'
 *      tosAgreed: // value for 'tosAgreed'
 *   },
 * });
 */
function useAgreeToWorkspaceCustomerTermsOfServiceMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AgreeToWorkspaceCustomerTermsOfServiceDocument, baseOptions);
}
const UpdateRestrictedUploadsStateDocument = gql `
  mutation updateRestrictedUploadsState(
    $workspaceId: UUID!
    $enableRestrictedUploads: Boolean!
  ) {
    updateRestrictedUploadsState(
      input: {
        identifier: $workspaceId
        restrictedUploads: $enableRestrictedUploads
      }
    ) {
      workspace {
        identifier
        uploadsRestrictedFor
      }
    }
  }
`;
/**
 * __useUpdateRestrictedUploadsStateMutation__
 *
 * To run a mutation, you first call `useUpdateRestrictedUploadsStateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateRestrictedUploadsStateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateRestrictedUploadsStateMutation, { data, loading, error }] = useUpdateRestrictedUploadsStateMutation({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *      enableRestrictedUploads: // value for 'enableRestrictedUploads'
 *   },
 * });
 */
function useUpdateRestrictedUploadsStateMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateRestrictedUploadsStateDocument, baseOptions);
}
const UpdateRestrictedPublicLinksDocument = gql `
  mutation updateRestrictedPublicLinks(
    $workspaceId: UUID!
    $enabled: Boolean!
  ) {
    updateRestrictedPublicLinks(
      input: { workspaceIdentifier: $workspaceId, enabled: $enabled }
    ) {
      workspace {
        identifier
        features {
          restrictedPublicLinks
        }
      }
    }
  }
`;
/**
 * __useUpdateRestrictedPublicLinksMutation__
 *
 * To run a mutation, you first call `useUpdateRestrictedPublicLinksMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateRestrictedPublicLinksMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateRestrictedPublicLinksMutation, { data, loading, error }] = useUpdateRestrictedPublicLinksMutation({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *      enabled: // value for 'enabled'
 *   },
 * });
 */
function useUpdateRestrictedPublicLinksMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateRestrictedPublicLinksDocument, baseOptions);
}
const GetWorkspacesDocument = gql `
  query getWorkspaces($includeBilling: Boolean = false) {
    me {
      identifier
      personalWorkspace {
        ...WorkspaceMinimal
      }
      workspaces {
        ...WorkspaceMinimalWithBillingStatus
      }
    }
  }
  ${WorkspaceMinimalFragmentDoc}
  ${WorkspaceMinimalWithBillingStatusFragmentDoc}
`;
/**
 * __useGetWorkspacesQuery__
 *
 * To run a query within a React component, call `useGetWorkspacesQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspacesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspacesQuery({
 *   variables: {
 *      includeBilling: // value for 'includeBilling'
 *   },
 * });
 */
function useGetWorkspacesQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspacesDocument, baseOptions);
}
function useGetWorkspacesLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspacesDocument, baseOptions);
}
const GetWorkspacesForDuplicationDocument = gql `
  query getWorkspacesForDuplication {
    me {
      identifier
      workspaces {
        ...DuplicationWorkspace
      }
    }
  }
  ${DuplicationWorkspaceFragmentDoc}
`;
/**
 * __useGetWorkspacesForDuplicationQuery__
 *
 * To run a query within a React component, call `useGetWorkspacesForDuplicationQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspacesForDuplicationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspacesForDuplicationQuery({
 *   variables: {
 *   },
 * });
 */
function useGetWorkspacesForDuplicationQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspacesForDuplicationDocument, baseOptions);
}
function useGetWorkspacesForDuplicationLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspacesForDuplicationDocument, baseOptions);
}
const GetWorkspaceDocument = gql `
  query getWorkspace($identifier: UUID!) {
    workspace(identifier: $identifier) {
      ...WorkspaceMinimal
    }
  }
  ${WorkspaceMinimalFragmentDoc}
`;
/**
 * __useGetWorkspaceQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceDocument, baseOptions);
}
function useGetWorkspaceLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceDocument, baseOptions);
}
const GetPublicWorkspaceDocument = gql `
  query getPublicWorkspace($identifier: UUID!) {
    publicWorkspace(identifier: $identifier) {
      ...PublicWorkspaceMinimal
    }
  }
  ${PublicWorkspaceMinimalFragmentDoc}
`;
/**
 * __useGetPublicWorkspaceQuery__
 *
 * To run a query within a React component, call `useGetPublicWorkspaceQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPublicWorkspaceQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPublicWorkspaceQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetPublicWorkspaceQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPublicWorkspaceDocument, baseOptions);
}
function useGetPublicWorkspaceLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPublicWorkspaceDocument, baseOptions);
}
const GetWorkspaceSettingsDocument = gql `
  query getWorkspaceSettings(
    $identifier: UUID!
    $includeShares: Boolean = true
  ) {
    workspace(identifier: $identifier) {
      ...WorkspaceMinimal
      canUseRestrictedUploads
      uploadsRestrictedFor
      scimEnabled
      customer {
        identifier
        billing {
          status
          seats {
            ...BillingSeatsInfo
          }
          trialEnd
          subscriptionEnd
        }
        ssoEnabled
        ssoShortName
      }
      features {
        ssoEnabled
        userDirectoryEnabled
      }
      shares @include(if: $includeShares) {
        meta {
          totalCount
        }
      }
    }
  }
  ${WorkspaceMinimalFragmentDoc}
  ${BillingSeatsInfoFragmentDoc}
`;
/**
 * __useGetWorkspaceSettingsQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceSettingsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceSettingsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceSettingsQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *      includeShares: // value for 'includeShares'
 *   },
 * });
 */
function useGetWorkspaceSettingsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceSettingsDocument, baseOptions);
}
function useGetWorkspaceSettingsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceSettingsDocument, baseOptions);
}
const GetWorkspaceSsoSettingsDocument = gql `
  query getWorkspaceSsoSettings($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      customer {
        identifier
        ssoConfig {
          ...WorkspaceSsoConfig
        }
      }
    }
  }
  ${WorkspaceSsoConfigFragmentDoc}
`;
/**
 * __useGetWorkspaceSsoSettingsQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceSsoSettingsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceSsoSettingsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceSsoSettingsQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceSsoSettingsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceSsoSettingsDocument, baseOptions);
}
function useGetWorkspaceSsoSettingsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceSsoSettingsDocument, baseOptions);
}
const GetWorkspaceSubscribeInformationDocument = gql `
  query getWorkspaceSubscribeInformation($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      customer {
        identifier
        billing {
          trialEnd
          seats {
            ...BillingSeatsInfo
          }
        }
      }
      memberships {
        list {
          meta {
            totalCount
          }
        }
      }
    }
  }
  ${BillingSeatsInfoFragmentDoc}
`;
/**
 * __useGetWorkspaceSubscribeInformationQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceSubscribeInformationQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceSubscribeInformationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceSubscribeInformationQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceSubscribeInformationQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceSubscribeInformationDocument, baseOptions);
}
function useGetWorkspaceSubscribeInformationLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceSubscribeInformationDocument, baseOptions);
}
const GetPublicWorkspaceFromTokenDocument = gql `
  query getPublicWorkspaceFromToken($inviteToken: String!) {
    getPublicWorkspaceFromToken(membershipToken: $inviteToken) {
      identifier
      name
      avatar {
        small
      }
    }
  }
`;
/**
 * __useGetPublicWorkspaceFromTokenQuery__
 *
 * To run a query within a React component, call `useGetPublicWorkspaceFromTokenQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPublicWorkspaceFromTokenQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPublicWorkspaceFromTokenQuery({
 *   variables: {
 *      inviteToken: // value for 'inviteToken'
 *   },
 * });
 */
function useGetPublicWorkspaceFromTokenQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPublicWorkspaceFromTokenDocument, baseOptions);
}
function useGetPublicWorkspaceFromTokenLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPublicWorkspaceFromTokenDocument, baseOptions);
}
const GetWorkspaceUploadsRestrictedForDocument = gql `
  query getWorkspaceUploadsRestrictedFor($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      uploadsRestrictedFor
    }
  }
`;
/**
 * __useGetWorkspaceUploadsRestrictedForQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceUploadsRestrictedForQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceUploadsRestrictedForQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceUploadsRestrictedForQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceUploadsRestrictedForQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceUploadsRestrictedForDocument, baseOptions);
}
function useGetWorkspaceUploadsRestrictedForLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceUploadsRestrictedForDocument, baseOptions);
}
const GetWorkspaceProfileIdentifierDocument = gql `
  query getWorkspaceProfileIdentifier($identifier: UUID!) {
    workspace(identifier: $identifier) {
      identifier
      profile {
        identifier
      }
    }
  }
`;
/**
 * __useGetWorkspaceProfileIdentifierQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceProfileIdentifierQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceProfileIdentifierQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceProfileIdentifierQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceProfileIdentifierQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceProfileIdentifierDocument, baseOptions);
}
function useGetWorkspaceProfileIdentifierLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceProfileIdentifierDocument, baseOptions);
}
const GetWorkspaceCustomerDocument = gql `
  query getWorkspaceCustomer($identifier: UUID!) {
    workspaceCustomer(identifier: $identifier) {
      workspaces {
        entries {
          ...WorkspaceMinimal
        }
      }
    }
  }
  ${WorkspaceMinimalFragmentDoc}
`;
/**
 * __useGetWorkspaceCustomerQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceCustomerQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceCustomerQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceCustomerQuery({
 *   variables: {
 *      identifier: // value for 'identifier'
 *   },
 * });
 */
function useGetWorkspaceCustomerQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceCustomerDocument, baseOptions);
}
function useGetWorkspaceCustomerLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceCustomerDocument, baseOptions);
}
const InviteWorkspaceMembersDocument = gql `
  mutation inviteWorkspaceMembers($input: InviteWorkspaceMembersParams!) {
    inviteWorkspaceMembers(input: $input) {
      workspace {
        identifier
        customer {
          identifier
          billing {
            seats {
              ...BillingSeatsInfo
            }
          }
        }
      }
      pendingScaToken
    }
  }
  ${BillingSeatsInfoFragmentDoc}
`;
/**
 * __useInviteWorkspaceMembersMutation__
 *
 * To run a mutation, you first call `useInviteWorkspaceMembersMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useInviteWorkspaceMembersMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [inviteWorkspaceMembersMutation, { data, loading, error }] = useInviteWorkspaceMembersMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useInviteWorkspaceMembersMutation(baseOptions) {
    return ApolloReactHooks.useMutation(InviteWorkspaceMembersDocument, baseOptions);
}
const UpdateWorkspaceMemberDocument = gql `
  mutation updateWorkspaceMember($input: UpdateWorkspaceMemberParams!) {
    updateWorkspaceMember(input: $input) {
      membership {
        ...WorkspaceMembership
        identifier
        role
        isEditor
        workspace {
          identifier
          customer {
            identifier
            billing {
              seats {
                ...BillingSeatsInfo
              }
            }
          }
        }
      }
      pendingScaToken
    }
  }
  ${WorkspaceMembershipFragmentDoc}
  ${BillingSeatsInfoFragmentDoc}
`;
/**
 * __useUpdateWorkspaceMemberMutation__
 *
 * To run a mutation, you first call `useUpdateWorkspaceMemberMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateWorkspaceMemberMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateWorkspaceMemberMutation, { data, loading, error }] = useUpdateWorkspaceMemberMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateWorkspaceMemberMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateWorkspaceMemberDocument, baseOptions);
}
const RemoveWorkspaceMemberDocument = gql `
  mutation removeWorkspaceMember($membershipId: UUID!) {
    removeWorkspaceMember(membershipIdentifier: $membershipId) {
      workspace {
        identifier
        memberships {
          userCanAccess
          totalMemberships: list {
            meta {
              totalCount
            }
          }
        }
        customer {
          identifier
          billing {
            seats {
              ...BillingSeatsInfo
            }
          }
        }
      }
    }
  }
  ${BillingSeatsInfoFragmentDoc}
`;
/**
 * __useRemoveWorkspaceMemberMutation__
 *
 * To run a mutation, you first call `useRemoveWorkspaceMemberMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRemoveWorkspaceMemberMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [removeWorkspaceMemberMutation, { data, loading, error }] = useRemoveWorkspaceMemberMutation({
 *   variables: {
 *      membershipId: // value for 'membershipId'
 *   },
 * });
 */
function useRemoveWorkspaceMemberMutation(baseOptions) {
    return ApolloReactHooks.useMutation(RemoveWorkspaceMemberDocument, baseOptions);
}
const ResendWorkspaceInviteDocument = gql `
  mutation resendWorkspaceInvite($membershipId: UUID!) {
    resendWorkspaceInvite(identifier: $membershipId) {
      membership {
        identifier
      }
    }
  }
`;
/**
 * __useResendWorkspaceInviteMutation__
 *
 * To run a mutation, you first call `useResendWorkspaceInviteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useResendWorkspaceInviteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [resendWorkspaceInviteMutation, { data, loading, error }] = useResendWorkspaceInviteMutation({
 *   variables: {
 *      membershipId: // value for 'membershipId'
 *   },
 * });
 */
function useResendWorkspaceInviteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(ResendWorkspaceInviteDocument, baseOptions);
}
const LeaveWorkspaceDocument = gql `
  mutation leaveWorkspace($membershipId: UUID!) {
    removeWorkspaceMember(membershipIdentifier: $membershipId) {
      workspace {
        identifier
      }
    }
  }
`;
/**
 * __useLeaveWorkspaceMutation__
 *
 * To run a mutation, you first call `useLeaveWorkspaceMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useLeaveWorkspaceMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [leaveWorkspaceMutation, { data, loading, error }] = useLeaveWorkspaceMutation({
 *   variables: {
 *      membershipId: // value for 'membershipId'
 *   },
 * });
 */
function useLeaveWorkspaceMutation(baseOptions) {
    return ApolloReactHooks.useMutation(LeaveWorkspaceDocument, baseOptions);
}
const GetPendingWorkspaceMembershipDocument = gql `
  query getPendingWorkspaceMembership(
    $membershipToken: String!
    $includeCustomer: Boolean! = false
  ) {
    pendingWorkspaceMembership(membershipToken: $membershipToken) {
      identifier
      isEditor
      role
      user {
        identifier
        name
        email
        avatar {
          small
        }
      }
      workspace {
        identifier
        name
        avatar {
          small
        }
        customer @include(if: $includeCustomer) {
          identifier
          ssoStartUrl
          ssoEnabled
        }
      }
      invite {
        identifier
        email
      }
      inviter {
        identifier
        name
      }
    }
    me {
      ...UserEmail
    }
  }
  ${UserEmailFragmentDoc}
`;
/**
 * __useGetPendingWorkspaceMembershipQuery__
 *
 * To run a query within a React component, call `useGetPendingWorkspaceMembershipQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPendingWorkspaceMembershipQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPendingWorkspaceMembershipQuery({
 *   variables: {
 *      membershipToken: // value for 'membershipToken'
 *      includeCustomer: // value for 'includeCustomer'
 *   },
 * });
 */
function useGetPendingWorkspaceMembershipQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPendingWorkspaceMembershipDocument, baseOptions);
}
function useGetPendingWorkspaceMembershipLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPendingWorkspaceMembershipDocument, baseOptions);
}
const GetWorkspaceMembershipsDocument = gql `
  query getWorkspaceMemberships(
    $workspaceId: UUID!
    $after: String
    $filter: String
    $limit: Int = 20
    $role: WorkspaceMembershipRole
    $accessLevel: WorkspaceMemberAccessLevel
    $excludeRoles: [WorkspaceMembershipRole]
    $activityStatus: UserActivityStatus
    $order: WorkspaceMembershipOrderType
    $orderDirection: OrderDirection
  ) {
    workspace(identifier: $workspaceId) {
      identifier
      memberships {
        userCanAccess
        totalMemberships: list {
          meta {
            totalCount
          }
        }
        totalGuests: list(role: GUEST) {
          meta {
            totalCount
          }
        }
        totalMembers: list(excludeRoles: GUEST) {
          meta {
            totalCount
          }
        }
        list(
          after: $after
          search: $filter
          limit: $limit
          role: $role
          accessLevel: $accessLevel
          excludeRoles: $excludeRoles
          activityStatus: $activityStatus
          order: $order
          orderDirection: $orderDirection
        ) {
          entries {
            ...WorkspaceMembership
          }
          meta {
            after
            totalCount
          }
        }
      }
    }
  }
  ${WorkspaceMembershipFragmentDoc}
`;
/**
 * __useGetWorkspaceMembershipsQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceMembershipsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceMembershipsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceMembershipsQuery({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *      after: // value for 'after'
 *      filter: // value for 'filter'
 *      limit: // value for 'limit'
 *      role: // value for 'role'
 *      accessLevel: // value for 'accessLevel'
 *      excludeRoles: // value for 'excludeRoles'
 *      activityStatus: // value for 'activityStatus'
 *      order: // value for 'order'
 *      orderDirection: // value for 'orderDirection'
 *   },
 * });
 */
function useGetWorkspaceMembershipsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceMembershipsDocument, baseOptions);
}
function useGetWorkspaceMembershipsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceMembershipsDocument, baseOptions);
}
const GetWorkspaceMembershipsCountDocument = gql `
  query getWorkspaceMembershipsCount($workspaceId: UUID!) {
    workspace(identifier: $workspaceId) {
      identifier
      memberships {
        totalMemberships: list {
          meta {
            totalCount
          }
        }
        totalGuests: list(role: GUEST) {
          meta {
            totalCount
          }
        }
        totalMembers: list(excludeRoles: GUEST) {
          meta {
            totalCount
          }
        }
      }
    }
  }
`;
/**
 * __useGetWorkspaceMembershipsCountQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceMembershipsCountQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceMembershipsCountQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceMembershipsCountQuery({
 *   variables: {
 *      workspaceId: // value for 'workspaceId'
 *   },
 * });
 */
function useGetWorkspaceMembershipsCountQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceMembershipsCountDocument, baseOptions);
}
function useGetWorkspaceMembershipsCountLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceMembershipsCountDocument, baseOptions);
}
const GetInvitableUsersDocument = gql `
  query getInvitableUsers(
    $workspaceIdentifier: UUID!
    $shareIdentifier: UUID
    $projectIdentifier: UUID
    $search: String
  ) {
    invitableUsers(
      shareIdentifier: $shareIdentifier
      workspaceIdentifier: $workspaceIdentifier
      projectIdentifier: $projectIdentifier
      search: $search
      limit: 5
    ) {
      entries {
        ...InvitableUser
      }
    }
  }
  ${InvitableUserFragmentDoc}
`;
/**
 * __useGetInvitableUsersQuery__
 *
 * To run a query within a React component, call `useGetInvitableUsersQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetInvitableUsersQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetInvitableUsersQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      shareIdentifier: // value for 'shareIdentifier'
 *      projectIdentifier: // value for 'projectIdentifier'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetInvitableUsersQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetInvitableUsersDocument, baseOptions);
}
function useGetInvitableUsersLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetInvitableUsersDocument, baseOptions);
}
const GetInvitableEmailUsersDocument = gql `
  query getInvitableEmailUsers(
    $workspaceIdentifier: UUID!
    $shareIdentifier: UUID
    $projectIdentifier: UUID
    $search: String
  ) {
    invitableUsers(
      shareIdentifier: $shareIdentifier
      workspaceIdentifier: $workspaceIdentifier
      projectIdentifier: $projectIdentifier
      search: $search
      limit: 5
    ) {
      entries {
        ...InvitableUserEmail
      }
    }
  }
  ${InvitableUserEmailFragmentDoc}
`;
/**
 * __useGetInvitableEmailUsersQuery__
 *
 * To run a query within a React component, call `useGetInvitableEmailUsersQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetInvitableEmailUsersQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetInvitableEmailUsersQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      shareIdentifier: // value for 'shareIdentifier'
 *      projectIdentifier: // value for 'projectIdentifier'
 *      search: // value for 'search'
 *   },
 * });
 */
function useGetInvitableEmailUsersQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetInvitableEmailUsersDocument, baseOptions);
}
function useGetInvitableEmailUsersLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetInvitableEmailUsersDocument, baseOptions);
}
const GetAccessShareMembershipsDocument = gql `
  query getAccessShareMemberships(
    $workspaceIdentifier: UUID!
    $workspaceMembershipIdentifier: UUID!
    $shareSearch: ShareMemberAccessSearchParams
    $after: String
    $limit: Int = 10
  ) {
    workspace(identifier: $workspaceIdentifier) {
      identifier
      workspaceMembership(identifier: $workspaceMembershipIdentifier) {
        shareMemberships(search: $shareSearch, after: $after, limit: $limit) {
          entries {
            ...UserAccessShareMembership
          }
          meta {
            after
            before
            limit
            totalCount
          }
        }
      }
    }
  }
  ${UserAccessShareMembershipFragmentDoc}
`;
/**
 * __useGetAccessShareMembershipsQuery__
 *
 * To run a query within a React component, call `useGetAccessShareMembershipsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAccessShareMembershipsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAccessShareMembershipsQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      workspaceMembershipIdentifier: // value for 'workspaceMembershipIdentifier'
 *      shareSearch: // value for 'shareSearch'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetAccessShareMembershipsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAccessShareMembershipsDocument, baseOptions);
}
function useGetAccessShareMembershipsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAccessShareMembershipsDocument, baseOptions);
}
const GetAccessProjectMembershipsDocument = gql `
  query getAccessProjectMemberships(
    $workspaceIdentifier: UUID!
    $workspaceMembershipIdentifier: UUID!
    $projectSearch: ProjectMemberAccessSearchParams
    $after: String
    $limit: Int = 10
  ) {
    workspace(identifier: $workspaceIdentifier) {
      identifier
      workspaceMembership(identifier: $workspaceMembershipIdentifier) {
        projectMemberships(
          search: $projectSearch
          after: $after
          limit: $limit
        ) {
          entries {
            ...UserAccessProjectMembership
          }
          meta {
            after
            before
            limit
            totalCount
          }
        }
      }
    }
  }
  ${UserAccessProjectMembershipFragmentDoc}
`;
/**
 * __useGetAccessProjectMembershipsQuery__
 *
 * To run a query within a React component, call `useGetAccessProjectMembershipsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetAccessProjectMembershipsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetAccessProjectMembershipsQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      workspaceMembershipIdentifier: // value for 'workspaceMembershipIdentifier'
 *      projectSearch: // value for 'projectSearch'
 *      after: // value for 'after'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetAccessProjectMembershipsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetAccessProjectMembershipsDocument, baseOptions);
}
function useGetAccessProjectMembershipsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetAccessProjectMembershipsDocument, baseOptions);
}
const InviteWorkspacePartnerDocument = gql `
  mutation inviteWorkspacePartner($input: InviteWorkspacePartnerParams!) {
    inviteWorkspacePartner(input: $input) {
      identifier
      name
    }
  }
`;
/**
 * __useInviteWorkspacePartnerMutation__
 *
 * To run a mutation, you first call `useInviteWorkspacePartnerMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useInviteWorkspacePartnerMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [inviteWorkspacePartnerMutation, { data, loading, error }] = useInviteWorkspacePartnerMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useInviteWorkspacePartnerMutation(baseOptions) {
    return ApolloReactHooks.useMutation(InviteWorkspacePartnerDocument, baseOptions);
}
const AcceptPartnershipInviteDocument = gql `
  mutation acceptPartnershipInvite(
    $membershipToken: String!
    $seats: Int!
    $tosAgreed: Boolean!
  ) {
    acceptPartnershipInvite(
      membershipToken: $membershipToken
      seats: $seats
      tosAgreed: $tosAgreed
    ) {
      membership {
        identifier
      }
    }
  }
`;
/**
 * __useAcceptPartnershipInviteMutation__
 *
 * To run a mutation, you first call `useAcceptPartnershipInviteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptPartnershipInviteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptPartnershipInviteMutation, { data, loading, error }] = useAcceptPartnershipInviteMutation({
 *   variables: {
 *      membershipToken: // value for 'membershipToken'
 *      seats: // value for 'seats'
 *      tosAgreed: // value for 'tosAgreed'
 *   },
 * });
 */
function useAcceptPartnershipInviteMutation(baseOptions) {
    return ApolloReactHooks.useMutation(AcceptPartnershipInviteDocument, baseOptions);
}
const GetPartnerDocument = gql `
  query getPartner($email: String!) {
    partner(email: $email) {
      identifier
      name
      email
      avatar {
        small
      }
    }
  }
`;
/**
 * __useGetPartnerQuery__
 *
 * To run a query within a React component, call `useGetPartnerQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPartnerQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPartnerQuery({
 *   variables: {
 *      email: // value for 'email'
 *   },
 * });
 */
function useGetPartnerQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPartnerDocument, baseOptions);
}
function useGetPartnerLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPartnerDocument, baseOptions);
}
const GetPartnerBillingDetailsDocument = gql `
  query getPartnerBillingDetails @PaymentQLApi {
    partnerBillingDetails {
      name
      email
      address {
        line1
        city
        state
        country
        postalCode
      }
      taxId
    }
  }
`;
/**
 * __useGetPartnerBillingDetailsQuery__
 *
 * To run a query within a React component, call `useGetPartnerBillingDetailsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPartnerBillingDetailsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPartnerBillingDetailsQuery({
 *   variables: {
 *   },
 * });
 */
function useGetPartnerBillingDetailsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetPartnerBillingDetailsDocument, baseOptions);
}
function useGetPartnerBillingDetailsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetPartnerBillingDetailsDocument, baseOptions);
}
const CreateWorkspaceProfileDocument = gql `
  mutation createWorkspaceProfile($workspaceIdentifier: UUID!) {
    createWorkspaceProfile(workspaceIdentifier: $workspaceIdentifier) {
      workspaceProfile {
        identifier
      }
    }
  }
`;
/**
 * __useCreateWorkspaceProfileMutation__
 *
 * To run a mutation, you first call `useCreateWorkspaceProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateWorkspaceProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createWorkspaceProfileMutation, { data, loading, error }] = useCreateWorkspaceProfileMutation({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *   },
 * });
 */
function useCreateWorkspaceProfileMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CreateWorkspaceProfileDocument, baseOptions);
}
const UpdateWorkspaceProfileDocument = gql `
  mutation updateWorkspaceProfile($input: UpdateWorkspaceProfileParams!) {
    updateWorkspaceProfile(input: $input) {
      workspaceProfile {
        identifier
        ...WorkspaceProfile
      }
    }
  }
  ${WorkspaceProfileFragmentDoc}
`;
/**
 * __useUpdateWorkspaceProfileMutation__
 *
 * To run a mutation, you first call `useUpdateWorkspaceProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateWorkspaceProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateWorkspaceProfileMutation, { data, loading, error }] = useUpdateWorkspaceProfileMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateWorkspaceProfileMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateWorkspaceProfileDocument, baseOptions);
}
const CheckWorkspaceProfileShortUrlDocument = gql `
  mutation checkWorkspaceProfileShortUrl($shortUrlName: String!) {
    checkWorkspaceProfileShortUrl(shortUrlName: $shortUrlName) {
      available
    }
  }
`;
/**
 * __useCheckWorkspaceProfileShortUrlMutation__
 *
 * To run a mutation, you first call `useCheckWorkspaceProfileShortUrlMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCheckWorkspaceProfileShortUrlMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [checkWorkspaceProfileShortUrlMutation, { data, loading, error }] = useCheckWorkspaceProfileShortUrlMutation({
 *   variables: {
 *      shortUrlName: // value for 'shortUrlName'
 *   },
 * });
 */
function useCheckWorkspaceProfileShortUrlMutation(baseOptions) {
    return ApolloReactHooks.useMutation(CheckWorkspaceProfileShortUrlDocument, baseOptions);
}
const UpdateWorkspaceProfileShortUrlDocument = gql `
  mutation updateWorkspaceProfileShortUrl(
    $input: UpdateWorkspaceProfileShortUrlParams!
  ) {
    updateWorkspaceProfileShortUrl(input: $input) {
      workspaceProfile {
        ...WorkspaceProfile
      }
    }
  }
  ${WorkspaceProfileFragmentDoc}
`;
/**
 * __useUpdateWorkspaceProfileShortUrlMutation__
 *
 * To run a mutation, you first call `useUpdateWorkspaceProfileShortUrlMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateWorkspaceProfileShortUrlMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateWorkspaceProfileShortUrlMutation, { data, loading, error }] = useUpdateWorkspaceProfileShortUrlMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
function useUpdateWorkspaceProfileShortUrlMutation(baseOptions) {
    return ApolloReactHooks.useMutation(UpdateWorkspaceProfileShortUrlDocument, baseOptions);
}
const GetWorkspaceProfileDocument = gql `
  query getWorkspaceProfile($workspaceIdentifier: UUID, $shortUrlName: String) {
    workspaceProfile(
      workspaceIdentifier: $workspaceIdentifier
      shortUrlName: $shortUrlName
    ) {
      ...WorkspaceProfile
    }
  }
  ${WorkspaceProfileFragmentDoc}
`;
/**
 * __useGetWorkspaceProfileQuery__
 *
 * To run a query within a React component, call `useGetWorkspaceProfileQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetWorkspaceProfileQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetWorkspaceProfileQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      shortUrlName: // value for 'shortUrlName'
 *   },
 * });
 */
function useGetWorkspaceProfileQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetWorkspaceProfileDocument, baseOptions);
}
function useGetWorkspaceProfileLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetWorkspaceProfileDocument, baseOptions);
}
const GetProfilePublicationsDocument = gql `
  query getProfilePublications($workspaceIdentifier: UUID!, $after: String) {
    workspaceProfile(workspaceIdentifier: $workspaceIdentifier) {
      identifier
      publications(after: $after) {
        entries {
          ...PublicationItem
        }
        meta {
          after
          totalCount
        }
      }
    }
  }
  ${PublicationItemFragmentDoc}
`;
/**
 * __useGetProfilePublicationsQuery__
 *
 * To run a query within a React component, call `useGetProfilePublicationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetProfilePublicationsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetProfilePublicationsQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      after: // value for 'after'
 *   },
 * });
 */
function useGetProfilePublicationsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetProfilePublicationsDocument, baseOptions);
}
function useGetProfilePublicationsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetProfilePublicationsDocument, baseOptions);
}
const GetRandomPublicationsDocument = gql `
  query getRandomPublications(
    $workspaceIdentifier: UUID!
    $exclude: [UUID!]
    $limit: Int = 2
  ) {
    workspaceProfile(workspaceIdentifier: $workspaceIdentifier) {
      identifier
      shortUrlName
      publications(sortOrder: RANDOM, exclude: $exclude, limit: $limit) {
        entries {
          ...PublicationItem
        }
      }
    }
  }
  ${PublicationItemFragmentDoc}
`;
/**
 * __useGetRandomPublicationsQuery__
 *
 * To run a query within a React component, call `useGetRandomPublicationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetRandomPublicationsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetRandomPublicationsQuery({
 *   variables: {
 *      workspaceIdentifier: // value for 'workspaceIdentifier'
 *      exclude: // value for 'exclude'
 *      limit: // value for 'limit'
 *   },
 * });
 */
function useGetRandomPublicationsQuery(baseOptions) {
    return ApolloReactHooks.useQuery(GetRandomPublicationsDocument, baseOptions);
}
function useGetRandomPublicationsLazyQuery(baseOptions) {
    return ApolloReactHooks.useLazyQuery(GetRandomPublicationsDocument, baseOptions);
}

export { AcceptLicenseRedemptionDocument, AcceptPartnershipInviteDocument, AcceptProjectAccessRequestDocument, AcceptProjectMembershipInvitationDocument, AcceptShareMembershipInvitationDocument, AcceptWorkspaceInviteDocument, AddProjectMemberDocument, AddReactionToPublicationDocument, AddShareMemberNotificationFragmentDoc, AddShareMembershipDocument, AddSharesToCollectionDocument, AgreeToWorkspaceCustomerTermsOfServiceDocument, AnnotationArtboardInfoFragmentDoc, AnnotationCommentCountFragmentDoc, AnnotationCoordinatesFragmentDoc, AnnotationCreatedDocument, AnnotationDeletedDocument, AnnotationDotFragmentDoc, AnnotationLastCommentFragmentDoc, AnnotationMovedDocument, AnnotationPageInfoFragmentDoc, AnnotationReplyCreatedDocument, AnnotationReplyDeletedDocument, AnnotationReplyEditedDocument, AnnotationReplyReadDocument, AnnotationResolutionInfoFragmentDoc, AnnotationResolvedDocument, AnnotationSubjectCountersFragmentDoc, AnnotationSubjectFragmentDoc, AnnotationSubjectSubscriptionStatusFragmentDoc, AnnotationUnresolvedDocument, ApplyCouponDocument, ArtboardDetailInfoCwvFragmentDoc, ArtboardDetailInfoFragmentDoc, ArtboardRevisionDeletedFragmentDoc, ArtboardRevisionFragmentDoc, ArtboardRevisionUnavailableFragmentDoc, ArtboardThumbnailsFragmentDoc, AttributeMappingEntryFragmentDoc, BaseAnnotationFragmentDoc, BaseCommentFragmentDoc, BillingDetailsFragmentDoc, BillingHistoryEntryFragmentDoc, BillingMinimalFragmentDoc, BillingPlanFragmentDoc, BillingSeatsInfoFragmentDoc, BillingWorkspaceNoticeFragmentDoc, CanUseDesignSystemManagerDocument, CancelLicenseRedemptionDocument, CancelScheduledBillingSeatsDocument, CancelSubscriptionDocument, CheckWorkspaceProfileShortUrlDocument, CollectionChangedDocument, CollectionCreatedDocument, CollectionDeletedDocument, CollectionForSelectFragmentDoc, CollectionFragmentDoc, CollectionPreviewsFragmentDoc, CollectionSharePreviewFragmentDoc, CollectionSharesFragmentDoc, CommentFragmentDoc, CommentNotificationFragmentDoc, CommentUserFragmentDoc, CommunityHeroFragmentDoc, CommunityItemFragmentDoc, CommunityPublicationQueryFragmentDoc, CommunitySectionFragmentDoc, ComponentInfoFragmentDoc, ConfirmMfaSetupDocument, CouponFragmentDoc, CouponMinimalFragmentDoc, CreateAnnotationCommentDocument, CreateAnnotationDocument, CreateCollectionDocument, CreateDesignSystemDocsDocument, CreateDesignSystemDocument, CreateProjectDocument, CreateWorkspaceDocument, CreateWorkspaceProfileDocument, CredentialsFragmentDoc, DeleteAnnotationDocument, DeleteAnnotationReplyDocument, DeleteCollectionDocument, DeleteDesignSystemDocument, DeleteProjectDocument, DeleteProjectPermanentlyDocument, DeleteSharePermanentlyDocument, DeleteWorkspaceAvatarDocument, DeleteWorkspaceDocument, DeletedAnnotationSubjectCountersFragmentDoc, DisableMfaDocument, DisablePersonalTokenUsageDocument, DisableScimDocument, DiscoverItemFragmentDoc, DiscoverSectionFragmentDoc, DocumentDownloadFragmentDoc, DocumentDownloadableAssetsFragmentDoc, DownloadableAssetFragmentDoc, DownloadableAssetRenderedNotificationFragmentDoc, DraftBillingSummaryDocument, DuplicateShareToDraftsDocument, DuplicationWorkspaceFragmentDoc, EditCollectionDescriptionDocument, EditCollectionDocument, EditCommentDocument, EditDescriptionsDocument, EmailResendChangeConfirmationDocument, EmailUpdateDocument, EmailVerifyDocument, EmptiedWorkspaceTrashDocument, EmptyWorkspaceTrashDocument, EnablePersonalTokenUsageDocument, EnableScimDocument, EventDateFragmentDoc, GenerateMfaRecoveryCodesDocument, GetAccessProjectMembershipsDocument, GetAccessShareMembershipsDocument, GetAnnotationCommentsDocument, GetAnnotationDotDocument, GetAnnotationDotsDocument, GetAnnotationLatestVisibleVersionDocument, GetAnnotationSettingsDocument, GetAnnotationSummaryDocument, GetAnnotationsDocument, GetArtboardByPermanentArtboardShortIdDocument, GetArtboardBySlugDocument, GetArtboardInDocumentWithUuidDocument, GetArtboardPrototypeInfoDocument, GetArtboardRevisionsDocument, GetArtboardsForDocumentDocument, GetArtboardsForPageDocument, GetAvailablePlansDocument, GetBaseCommentDocument, GetBillingForWorkspaceNoticeDocument, GetBillingHistoryCountDocument, GetBillingHistoryDocument, GetBrowserSettingsDocument, GetCampaignSignUpContentDocument, GetCollectionSharesDocument, GetCollectionSharesUnpaginatedDocument, GetCommunityPageItemsDocument, GetComponentGroupsDocument, GetComponentWithUuidDocument, GetComponentsCountDocument, GetComponentsDocument, GetCustomerDocument, GetCustomerPlanDocument, GetCustomerProviderDocument, GetDesignSystemDocument, GetDesignSystemSectionsDocument, GetDetailViewArtboardDocument, GetDiscoverDocument, GetDiscoverHashDocument, GetDiscoverResourcesDocument, GetDocumentListSettingsDocument, GetDownloadableAssetsDocument, GetEligibleSharesDocument, GetInitialUserDocument, GetInspectorComponentDataDocument, GetInspectorDataDocument, GetInvitableEmailUsersDocument, GetInvitableUsersDocument, GetLatestStarDocument, GetLibrariesDocument, GetLicenseRedemptionDocument, GetLicenseRedemptionPreviewDocument, GetLicenseRedemptionsDocument, GetMentionableUsersDocument, GetNextBillingCycleDocument, GetNotificationStatusDocument, GetNotificationsDocument, GetOrganizationsDocument, GetPageDocument, GetPagePresentationFileDocument, GetPagesDocument, GetPartnerBillingDetailsDocument, GetPartnerDocument, GetPaymentDetailsDocument, GetPendingDescriptionDocument, GetPendingWorkspaceMembershipDocument, GetPermanentArtboardShortIdDocument, GetPersonalTokensDocument, GetPersonalWorkspaceSharesCountDocument, GetPresentationAssetsByArtboardDocument, GetProfilePublicationsDocument, GetProjectCollectionsDocument, GetProjectCollectionsForSelectDocument, GetProjectMembershipByTokenDocument, GetProjectMembershipsCountDocument, GetProjectMembershipsDocument, GetProjectSharesDocument, GetProjectSharesDropdownOptionsDocument, GetProjectsDocument, GetPrototypeFileForDocumentDocument, GetPublicWorkspaceDocument, GetPublicWorkspaceForAccountLinkingDocument, GetPublicWorkspaceFromTokenDocument, GetPublicationDocument, GetPublicationListingDocument, GetPublicationsDocument, GetRandomPublicationsDocument, GetRoleSignUpContentDocument, GetSeatsUpdateBillingSimulationDocument, GetShareCollectionDocument, GetShareColorTokenExportDocument, GetShareDocument, GetShareDropdownOptionsDocument, GetShareForDeleteEntityFromCacheDocument, GetShareListItemDocument, GetShareMembershipByShareShortIdDocument, GetShareMembershipDocument, GetShareMembershipsDocument, GetSharePagesDocument, GetSharePermissionsDocument, GetShareSidebarDocument, GetShareUserAccessLevelDocument, GetShareWorkspaceDocument, GetSharesDocument, GetSharesDropdownOptionsDocument, GetSharesLibrariesDocument, GetShowDocumentsInProjectsDocument, GetSsoStartUrlDocument, GetSurroundingArtboardsDocument, GetTaxableCountriesDocument, GetTrashDocument, GetUserCredentialsDocument, GetUserHubSpotIdentificationTokenDocument, GetUserIdDocument, GetUserNotificationsCountDocument, GetUserSettingsDocument, GetVersionDocument, GetVersionHistoryDocument, GetWebhookDestinationDocument, GetWorkspaceBillingDocument, GetWorkspaceCustomerDocument, GetWorkspaceDesignSystemsDocument, GetWorkspaceDocument, GetWorkspaceMembershipsCountDocument, GetWorkspaceMembershipsDocument, GetWorkspaceProfileDocument, GetWorkspaceProfileIdentifierDocument, GetWorkspaceSettingsDocument, GetWorkspaceSharesCountDocument, GetWorkspaceSsoSettingsDocument, GetWorkspaceSubscribeInformationDocument, GetWorkspaceUploadsRestrictedForDocument, GetWorkspacesDocument, GetWorkspacesForDuplicationDocument, IdentityProviderConfigFragmentDoc, InitialUserFragmentDoc, InvitableUserEmailFragmentDoc, InvitableUserFragmentDoc, InviteWorkspaceMembersDocument, InviteWorkspacePartnerDocument, LeaveProjectDocument, LeaveWorkspaceDocument, LicenseRedemptionFragmentDoc, LicenseRedemptionPreviewFragmentDoc, LinkFragmentDoc, MarkCommentsAsReadDocument, MentionNotificationFragmentDoc, MoveAllCollectionSharesDocument, MoveAnnotationDocument, MoveShareToProjectDocument, NotificationCommentFragmentDoc, NotificationCreatedDocument, NotificationInfoFragmentDoc, NotificationMarkAllAsReadDocument, NotificationMarkAllAsSeenDocument, NotificationMarkAsReadDocument, NotificationStatusFragmentDoc, PageDetailsFragmentDoc, PaymentDetailsFragmentDoc, PermissionsFragmentDoc, PersonalTokenCreateDocument, PersonalTokenDeleteDocument, PersonalTokenFragmentDoc, PresentationFileDocument, PresentationFileFragmentDoc, ProjectAccessChangedDocument, ProjectChangedDocument, ProjectFragmentDoc, ProjectInSidebarAndHeaderFragmentDoc, ProjectMembershipChangedDocument, ProjectMembershipFragmentDoc, ProjectWithPrivacyIconFragmentDoc, PrototypeFileDocument, PrototypeFileFragmentDoc, PublicWorkspaceFragmentDoc, PublicWorkspaceMinimalFragmentDoc, PublicWorkspaceProfileFragmentDoc, PublicWorkspaceSsoIdentityFragmentDoc, PublicationFragmentDoc, PublicationItemFragmentDoc, PublicationListingFragmentDoc, PublicationPageFragmentDoc, PublicationQueryPreferencesFragmentDoc, PublicationReactionsFragmentDoc, PublicationReactionsUpdatedDocument, PublicationThumbnailFragmentDoc, PublishShareDocument, ReadShareFragmentDoc, RemoveProjectMemberDocument, RemoveShareFromProjectDocument, RemoveSharesFromCollectionDocument, RemoveWorkspaceMemberDocument, RenameCollectionDocument, RenameProjectDocument, RenderDownloadableAssetsDocument, ReportShareDocument, RequestAccessToProjectDocument, RequestLicenseRedemptionDocument, RequestLicenseRedemptionsDocument, ResendVerificationEmailDocument, ResendWorkspaceInviteDocument, ResetScimTokenDocument, ResolveAnnotationDocument, ResponsiveImageFragmentDoc, RestoreProjectDocument, RestoreShareDocument, RevisionCreatedDocument, RevisionDeletedDocument, RevisionDeletedFragmentDoc, RevisionInfoFragmentDoc, RevisionMovedDocument, RevisionUnavailableFragmentDoc, RevokeActiveSessionsDocument, RevokeOAuthDocument, RevokeSessionDocument, SendDownloadSketchEmailDocument, SetNotificationSubscriptionDocument, SetupMfaDocument, ShareAboutFragmentDoc, ShareAcceptRequestDocument, ShareBaseFragmentDoc, ShareChangedDocument, ShareCollectionChangedDocument, ShareDeleteDocument, ShareDeleteMembershipDocument, ShareDescriptionChangeDocument, ShareDropdownOptionsBaseFragmentDoc, ShareDropdownOptionsFragmentDoc, ShareDropdownOptionsPreloadFragmentDoc, ShareForDocumentWithUuidDocument, ShareInfoFragmentDoc, ShareInfoWithCollectionFragmentDoc, ShareLatestStarredVersionFragmentDoc, ShareLeaveDocument, ShareLibraryListItemFragmentDoc, ShareListItemBaseFragmentDoc, ShareListItemFragmentDoc, ShareMemberInfoFragmentDoc, ShareMembershipInfoFragmentDoc, ShareMetaFragmentDoc, SharePermissionsFragmentDoc, SharePublicPermissionsFragmentDoc, SharePublicationFragmentDoc, ShareRenameDocument, ShareRequestAccessDocument, ShareSettingsFragmentDoc, ShareSubscriptionStatusFragmentDoc, ShareThumbnailsFragmentDoc, ShareTypeChangeDocument, ShareUpdateDocument, ShareUserAccessLevelFragmentDoc, ShareVersionHistoryFragmentDoc, ShareViewerHasUnacceptedMembershipInviteFragmentDoc, ShareWorkspaceFragmentDoc, SharesListVersionItemFragmentDoc, SignInAndDisableMfaDocument, SignInDocument, SignInWithSsoDocument, SignUpContentFragmentDoc, SignUpDocument, SignUpImageFragmentDoc, SingleImageFragmentDoc, SizeFragmentDoc, SpeakerFragmentDoc, SubscribeCustomerToPlanDocument, SubscriptionShareFragmentDoc, TransferShareDocument, TrashedProjectFragmentDoc, TrashedShareFragmentDoc, UnResolveAnnotationDocument, UndoCancelSubscriptionDocument, UnpaginatedCollectionShareFragmentDoc, UnpublishShareDocument, UpdateBillingDetailsDocument, UpdateBillingSeatsDocument, UpdateDesignSystemContentDocument, UpdateDesignSystemDocument, UpdatePaymentDetailsDocument, UpdateProjectMembershipDocument, UpdateProjectPermissionDocument, UpdateRestrictedPublicLinksDocument, UpdateRestrictedUploadsStateDocument, UpdateShareMembershipDocument, UpdateShareParentPermissionsDocument, UpdateSharePublicPermissionsDocument, UpdateSharePublicTokenExportDocument, UpdateUserPreferencesDocument, UpdateVersionDescriptionDocument, UpdateWorkspaceCustomerSsoAttributeMappingDocument, UpdateWorkspaceCustomerSsoIdentityProviderConfigDocument, UpdateWorkspaceCustomerSsoShortNameDocument, UpdateWorkspaceDocument, UpdateWorkspaceMemberDocument, UpdateWorkspaceProfileDocument, UpdateWorkspaceProfileShortUrlDocument, UpgradePersonalWorkspaceDocument, UserAcceptTosAgreementDocument, UserAccessProjectMembershipFragmentDoc, UserAccessShareMembershipFragmentDoc, UserActiveSessionFragmentDoc, UserAvatarCreateDocument, UserAvatarDeleteDocument, UserDeleteDocument, UserEmailFragmentDoc, UserErrorFragmentDoc, UserFragmentDoc, UserNotificationCountFragmentDoc, UserNotificationFragmentDoc, UserPasswordResetDocument, UserPasswordUpdateDocument, UserPreferencesFragmentDoc, UserRequestPasswordResetDocument, UserUpdateDocument, UserWorkspaceMembershipFragmentDoc, ValidateMfaRecoveryCodeDocument, ValidateMfaTotpDocument, VersionDeleteDocument, VersionDeletedDocument, VersionDeletedFragmentDoc, VersionDocumentFragmentDoc, VersionFragmentDoc, VersionListItemFragmentDoc, VersionListItemWithOrderFragmentDoc, VersionUnavailableFragmentDoc, VersionUnavailableWithOrderFragmentDoc, VersionUpdateKindDocument, VersionUpdatedDocument, VersionUploadCompleteDocument, VersionUploadProcessedDocument, VideoFragmentDoc, VideoSourceFragmentDoc, VisibilityFragmentDoc, WebhookDestinationDeleteDocument, WebhookDestinationFragmentDoc, WebhookDestinationUpdateDocument, WorkspaceMembershipFragmentDoc, WorkspaceMinimalFragmentDoc, WorkspaceMinimalIdentityFragmentDoc, WorkspaceMinimalWithBillingStatusFragmentDoc, WorkspaceProfileFragmentDoc, WorkspaceSsoConfigFragmentDoc, WorkspaceSsoIdentityFragmentDoc, useAcceptLicenseRedemptionMutation, useAcceptPartnershipInviteMutation, useAcceptProjectAccessRequestMutation, useAcceptProjectMembershipInvitationMutation, useAcceptShareMembershipInvitationMutation, useAcceptWorkspaceInviteMutation, useAddProjectMemberMutation, useAddReactionToPublicationMutation, useAddShareMembershipMutation, useAddSharesToCollectionMutation, useAgreeToWorkspaceCustomerTermsOfServiceMutation, useAnnotationCreatedSubscription, useAnnotationDeletedSubscription, useAnnotationMovedSubscription, useAnnotationReplyCreatedSubscription, useAnnotationReplyDeletedSubscription, useAnnotationReplyEditedSubscription, useAnnotationReplyReadSubscription, useAnnotationResolvedSubscription, useAnnotationUnresolvedSubscription, useApplyCouponMutation, useCanUseDesignSystemManagerLazyQuery, useCanUseDesignSystemManagerQuery, useCancelLicenseRedemptionMutation, useCancelScheduledBillingSeatsMutation, useCancelSubscriptionMutation, useCheckWorkspaceProfileShortUrlMutation, useCollectionChangedSubscription, useCollectionCreatedSubscription, useCollectionDeletedSubscription, useConfirmMfaSetupMutation, useCreateAnnotationCommentMutation, useCreateAnnotationMutation, useCreateCollectionMutation, useCreateDesignSystemDocsMutation, useCreateDesignSystemMutation, useCreateProjectMutation, useCreateWorkspaceMutation, useCreateWorkspaceProfileMutation, useDeleteAnnotationMutation, useDeleteAnnotationReplyMutation, useDeleteCollectionMutation, useDeleteDesignSystemMutation, useDeleteProjectMutation, useDeleteProjectPermanentlyMutation, useDeleteSharePermanentlyMutation, useDeleteWorkspaceAvatarMutation, useDeleteWorkspaceMutation, useDisableMfaMutation, useDisablePersonalTokenUsageMutation, useDisableScimMutation, useDraftBillingSummaryLazyQuery, useDraftBillingSummaryQuery, useDuplicateShareToDraftsMutation, useEditCollectionDescriptionMutation, useEditCollectionMutation, useEditCommentMutation, useEditDescriptionsMutation, useEmailResendChangeConfirmationMutation, useEmailUpdateMutation, useEmailVerifyMutation, useEmptiedWorkspaceTrashSubscription, useEmptyWorkspaceTrashMutation, useEnablePersonalTokenUsageMutation, useEnableScimMutation, useGenerateMfaRecoveryCodesMutation, useGetAccessProjectMembershipsLazyQuery, useGetAccessProjectMembershipsQuery, useGetAccessShareMembershipsLazyQuery, useGetAccessShareMembershipsQuery, useGetAnnotationCommentsLazyQuery, useGetAnnotationCommentsQuery, useGetAnnotationDotLazyQuery, useGetAnnotationDotQuery, useGetAnnotationDotsLazyQuery, useGetAnnotationDotsQuery, useGetAnnotationLatestVisibleVersionLazyQuery, useGetAnnotationLatestVisibleVersionQuery, useGetAnnotationSettingsLazyQuery, useGetAnnotationSettingsQuery, useGetAnnotationSummaryLazyQuery, useGetAnnotationSummaryQuery, useGetAnnotationsLazyQuery, useGetAnnotationsQuery, useGetArtboardByPermanentArtboardShortIdLazyQuery, useGetArtboardByPermanentArtboardShortIdQuery, useGetArtboardBySlugLazyQuery, useGetArtboardBySlugQuery, useGetArtboardInDocumentWithUuidLazyQuery, useGetArtboardInDocumentWithUuidQuery, useGetArtboardPrototypeInfoLazyQuery, useGetArtboardPrototypeInfoQuery, useGetArtboardRevisionsLazyQuery, useGetArtboardRevisionsQuery, useGetArtboardsForDocumentLazyQuery, useGetArtboardsForDocumentQuery, useGetArtboardsForPageLazyQuery, useGetArtboardsForPageQuery, useGetAvailablePlansLazyQuery, useGetAvailablePlansQuery, useGetBaseCommentLazyQuery, useGetBaseCommentQuery, useGetBillingForWorkspaceNoticeLazyQuery, useGetBillingForWorkspaceNoticeQuery, useGetBillingHistoryCountLazyQuery, useGetBillingHistoryCountQuery, useGetBillingHistoryLazyQuery, useGetBillingHistoryQuery, useGetBrowserSettingsLazyQuery, useGetBrowserSettingsQuery, useGetCampaignSignUpContentLazyQuery, useGetCampaignSignUpContentQuery, useGetCollectionSharesLazyQuery, useGetCollectionSharesQuery, useGetCollectionSharesUnpaginatedLazyQuery, useGetCollectionSharesUnpaginatedQuery, useGetCommunityPageItemsLazyQuery, useGetCommunityPageItemsQuery, useGetComponentGroupsLazyQuery, useGetComponentGroupsQuery, useGetComponentWithUuidLazyQuery, useGetComponentWithUuidQuery, useGetComponentsCountLazyQuery, useGetComponentsCountQuery, useGetComponentsLazyQuery, useGetComponentsQuery, useGetCustomerLazyQuery, useGetCustomerPlanLazyQuery, useGetCustomerPlanQuery, useGetCustomerProviderLazyQuery, useGetCustomerProviderQuery, useGetCustomerQuery, useGetDesignSystemLazyQuery, useGetDesignSystemQuery, useGetDesignSystemSectionsLazyQuery, useGetDesignSystemSectionsQuery, useGetDetailViewArtboardLazyQuery, useGetDetailViewArtboardQuery, useGetDiscoverHashLazyQuery, useGetDiscoverHashQuery, useGetDiscoverLazyQuery, useGetDiscoverQuery, useGetDiscoverResourcesLazyQuery, useGetDiscoverResourcesQuery, useGetDocumentListSettingsLazyQuery, useGetDocumentListSettingsQuery, useGetDownloadableAssetsLazyQuery, useGetDownloadableAssetsQuery, useGetEligibleSharesLazyQuery, useGetEligibleSharesQuery, useGetInitialUserLazyQuery, useGetInitialUserQuery, useGetInspectorComponentDataLazyQuery, useGetInspectorComponentDataQuery, useGetInspectorDataLazyQuery, useGetInspectorDataQuery, useGetInvitableEmailUsersLazyQuery, useGetInvitableEmailUsersQuery, useGetInvitableUsersLazyQuery, useGetInvitableUsersQuery, useGetLatestStarLazyQuery, useGetLatestStarQuery, useGetLibrariesLazyQuery, useGetLibrariesQuery, useGetLicenseRedemptionLazyQuery, useGetLicenseRedemptionPreviewLazyQuery, useGetLicenseRedemptionPreviewQuery, useGetLicenseRedemptionQuery, useGetLicenseRedemptionsLazyQuery, useGetLicenseRedemptionsQuery, useGetMentionableUsersLazyQuery, useGetMentionableUsersQuery, useGetNextBillingCycleLazyQuery, useGetNextBillingCycleQuery, useGetNotificationStatusLazyQuery, useGetNotificationStatusQuery, useGetNotificationsLazyQuery, useGetNotificationsQuery, useGetOrganizationsLazyQuery, useGetOrganizationsQuery, useGetPageLazyQuery, useGetPagePresentationFileLazyQuery, useGetPagePresentationFileQuery, useGetPageQuery, useGetPagesLazyQuery, useGetPagesQuery, useGetPartnerBillingDetailsLazyQuery, useGetPartnerBillingDetailsQuery, useGetPartnerLazyQuery, useGetPartnerQuery, useGetPaymentDetailsLazyQuery, useGetPaymentDetailsQuery, useGetPendingDescriptionLazyQuery, useGetPendingDescriptionQuery, useGetPendingWorkspaceMembershipLazyQuery, useGetPendingWorkspaceMembershipQuery, useGetPermanentArtboardShortIdLazyQuery, useGetPermanentArtboardShortIdQuery, useGetPersonalTokensLazyQuery, useGetPersonalTokensQuery, useGetPersonalWorkspaceSharesCountLazyQuery, useGetPersonalWorkspaceSharesCountQuery, useGetPresentationAssetsByArtboardLazyQuery, useGetPresentationAssetsByArtboardQuery, useGetProfilePublicationsLazyQuery, useGetProfilePublicationsQuery, useGetProjectCollectionsForSelectLazyQuery, useGetProjectCollectionsForSelectQuery, useGetProjectCollectionsLazyQuery, useGetProjectCollectionsQuery, useGetProjectMembershipByTokenLazyQuery, useGetProjectMembershipByTokenQuery, useGetProjectMembershipsCountLazyQuery, useGetProjectMembershipsCountQuery, useGetProjectMembershipsLazyQuery, useGetProjectMembershipsQuery, useGetProjectSharesDropdownOptionsLazyQuery, useGetProjectSharesDropdownOptionsQuery, useGetProjectSharesLazyQuery, useGetProjectSharesQuery, useGetProjectsLazyQuery, useGetProjectsQuery, useGetPrototypeFileForDocumentLazyQuery, useGetPrototypeFileForDocumentQuery, useGetPublicWorkspaceForAccountLinkingLazyQuery, useGetPublicWorkspaceForAccountLinkingQuery, useGetPublicWorkspaceFromTokenLazyQuery, useGetPublicWorkspaceFromTokenQuery, useGetPublicWorkspaceLazyQuery, useGetPublicWorkspaceQuery, useGetPublicationLazyQuery, useGetPublicationListingLazyQuery, useGetPublicationListingQuery, useGetPublicationQuery, useGetPublicationsLazyQuery, useGetPublicationsQuery, useGetRandomPublicationsLazyQuery, useGetRandomPublicationsQuery, useGetRoleSignUpContentLazyQuery, useGetRoleSignUpContentQuery, useGetSeatsUpdateBillingSimulationLazyQuery, useGetSeatsUpdateBillingSimulationQuery, useGetShareCollectionLazyQuery, useGetShareCollectionQuery, useGetShareColorTokenExportLazyQuery, useGetShareColorTokenExportQuery, useGetShareDropdownOptionsLazyQuery, useGetShareDropdownOptionsQuery, useGetShareForDeleteEntityFromCacheLazyQuery, useGetShareForDeleteEntityFromCacheQuery, useGetShareLazyQuery, useGetShareListItemLazyQuery, useGetShareListItemQuery, useGetShareMembershipByShareShortIdLazyQuery, useGetShareMembershipByShareShortIdQuery, useGetShareMembershipLazyQuery, useGetShareMembershipQuery, useGetShareMembershipsLazyQuery, useGetShareMembershipsQuery, useGetSharePagesLazyQuery, useGetSharePagesQuery, useGetSharePermissionsLazyQuery, useGetSharePermissionsQuery, useGetShareQuery, useGetShareSidebarLazyQuery, useGetShareSidebarQuery, useGetShareUserAccessLevelLazyQuery, useGetShareUserAccessLevelQuery, useGetShareWorkspaceLazyQuery, useGetShareWorkspaceQuery, useGetSharesDropdownOptionsLazyQuery, useGetSharesDropdownOptionsQuery, useGetSharesLazyQuery, useGetSharesLibrariesLazyQuery, useGetSharesLibrariesQuery, useGetSharesQuery, useGetShowDocumentsInProjectsLazyQuery, useGetShowDocumentsInProjectsQuery, useGetSsoStartUrlLazyQuery, useGetSsoStartUrlQuery, useGetSurroundingArtboardsLazyQuery, useGetSurroundingArtboardsQuery, useGetTaxableCountriesLazyQuery, useGetTaxableCountriesQuery, useGetTrashLazyQuery, useGetTrashQuery, useGetUserCredentialsLazyQuery, useGetUserCredentialsQuery, useGetUserHubSpotIdentificationTokenLazyQuery, useGetUserHubSpotIdentificationTokenQuery, useGetUserIdLazyQuery, useGetUserIdQuery, useGetUserNotificationsCountLazyQuery, useGetUserNotificationsCountQuery, useGetUserSettingsLazyQuery, useGetUserSettingsQuery, useGetVersionHistoryLazyQuery, useGetVersionHistoryQuery, useGetVersionLazyQuery, useGetVersionQuery, useGetWebhookDestinationLazyQuery, useGetWebhookDestinationQuery, useGetWorkspaceBillingLazyQuery, useGetWorkspaceBillingQuery, useGetWorkspaceCustomerLazyQuery, useGetWorkspaceCustomerQuery, useGetWorkspaceDesignSystemsLazyQuery, useGetWorkspaceDesignSystemsQuery, useGetWorkspaceLazyQuery, useGetWorkspaceMembershipsCountLazyQuery, useGetWorkspaceMembershipsCountQuery, useGetWorkspaceMembershipsLazyQuery, useGetWorkspaceMembershipsQuery, useGetWorkspaceProfileIdentifierLazyQuery, useGetWorkspaceProfileIdentifierQuery, useGetWorkspaceProfileLazyQuery, useGetWorkspaceProfileQuery, useGetWorkspaceQuery, useGetWorkspaceSettingsLazyQuery, useGetWorkspaceSettingsQuery, useGetWorkspaceSharesCountLazyQuery, useGetWorkspaceSharesCountQuery, useGetWorkspaceSsoSettingsLazyQuery, useGetWorkspaceSsoSettingsQuery, useGetWorkspaceSubscribeInformationLazyQuery, useGetWorkspaceSubscribeInformationQuery, useGetWorkspaceUploadsRestrictedForLazyQuery, useGetWorkspaceUploadsRestrictedForQuery, useGetWorkspacesForDuplicationLazyQuery, useGetWorkspacesForDuplicationQuery, useGetWorkspacesLazyQuery, useGetWorkspacesQuery, useInviteWorkspaceMembersMutation, useInviteWorkspacePartnerMutation, useLeaveProjectMutation, useLeaveWorkspaceMutation, useMarkCommentsAsReadMutation, useMoveAllCollectionSharesMutation, useMoveAnnotationMutation, useMoveShareToProjectMutation, useNotificationCreatedSubscription, useNotificationMarkAllAsReadMutation, useNotificationMarkAllAsSeenMutation, useNotificationMarkAsReadMutation, usePersonalTokenCreateMutation, usePersonalTokenDeleteMutation, usePresentationFileSubscription, useProjectAccessChangedSubscription, useProjectChangedSubscription, useProjectMembershipChangedSubscription, usePrototypeFileSubscription, usePublicationReactionsUpdatedSubscription, usePublishShareMutation, useRemoveProjectMemberMutation, useRemoveShareFromProjectMutation, useRemoveSharesFromCollectionMutation, useRemoveWorkspaceMemberMutation, useRenameCollectionMutation, useRenameProjectMutation, useRenderDownloadableAssetsMutation, useReportShareMutation, useRequestAccessToProjectMutation, useRequestLicenseRedemptionMutation, useRequestLicenseRedemptionsMutation, useResendVerificationEmailMutation, useResendWorkspaceInviteMutation, useResetScimTokenMutation, useResolveAnnotationMutation, useRestoreProjectMutation, useRestoreShareMutation, useRevisionCreatedSubscription, useRevisionDeletedSubscription, useRevisionMovedSubscription, useRevokeActiveSessionsMutation, useRevokeOAuthMutation, useRevokeSessionMutation, useSendDownloadSketchEmailMutation, useSetNotificationSubscriptionMutation, useSetupMfaMutation, useShareAcceptRequestMutation, useShareChangedSubscription, useShareCollectionChangedSubscription, useShareDeleteMembershipMutation, useShareDeleteMutation, useShareDescriptionChangeMutation, useShareForDocumentWithUuidLazyQuery, useShareForDocumentWithUuidQuery, useShareLeaveMutation, useShareRenameMutation, useShareRequestAccessMutation, useShareTypeChangeMutation, useShareUpdateMutation, useSignInAndDisableMfaMutation, useSignInMutation, useSignInWithSsoMutation, useSignUpMutation, useSubscribeCustomerToPlanMutation, useTransferShareMutation, useUnResolveAnnotationMutation, useUndoCancelSubscriptionMutation, useUnpublishShareMutation, useUpdateBillingDetailsMutation, useUpdateBillingSeatsMutation, useUpdateDesignSystemContentMutation, useUpdateDesignSystemMutation, useUpdatePaymentDetailsMutation, useUpdateProjectMembershipMutation, useUpdateProjectPermissionMutation, useUpdateRestrictedPublicLinksMutation, useUpdateRestrictedUploadsStateMutation, useUpdateShareMembershipMutation, useUpdateShareParentPermissionsMutation, useUpdateSharePublicPermissionsMutation, useUpdateSharePublicTokenExportMutation, useUpdateUserPreferencesMutation, useUpdateVersionDescriptionMutation, useUpdateWorkspaceCustomerSsoAttributeMappingMutation, useUpdateWorkspaceCustomerSsoIdentityProviderConfigMutation, useUpdateWorkspaceCustomerSsoShortNameMutation, useUpdateWorkspaceMemberMutation, useUpdateWorkspaceMutation, useUpdateWorkspaceProfileMutation, useUpdateWorkspaceProfileShortUrlMutation, useUpgradePersonalWorkspaceMutation, useUserAcceptTosAgreementMutation, useUserAvatarCreateMutation, useUserAvatarDeleteMutation, useUserDeleteMutation, useUserPasswordResetMutation, useUserPasswordUpdateMutation, useUserRequestPasswordResetMutation, useUserUpdateMutation, useValidateMfaRecoveryCodeMutation, useValidateMfaTotpMutation, useVersionDeleteMutation, useVersionDeletedSubscription, useVersionUpdateKindMutation, useVersionUpdatedSubscription, useVersionUploadCompleteSubscription, useVersionUploadProcessedSubscription, useWebhookDestinationDeleteMutation, useWebhookDestinationUpdateMutation };
